import {
  Bus, CalendarDays, FileText, Hotel, IndianRupee, Mail,
  Palmtree, Plane, Train, Users, BarChart2, BookOpen,
  Activity, MapPin, Tag, TrendingUp, X, Edit, Eye, Ban,
  CheckCircle, AlertTriangle, RefreshCw, Download, Plus,
  ChevronRight, Star, Clock, Filter, Menu, LogOut,
  Moon, Sun, PanelLeftClose, PanelLeftOpen, Bell, Bot
} from "lucide-react";
import { useState, useEffect, useCallback, useMemo, useContext, createContext, createElement, isValidElement, useRef } from "react";
import { useAdminAuth } from "./AdminAuthContext";
import Logo from "../../assets/logo.png";

const LIGHT_T = {
  bg: "#f5faf7",
  card: "#ffffff",
  sidebar: "#ffffff",
  sidebarDk: "#eafaf1",
  accent: "#16a34a",
  accentSoft: "#e9f9ef",
  green: "#16a34a",
  greenSoft: "#e9f9ef",
  teal: "#0d9488",
  tealSoft: "#e6f7f5",
  border: "#e2ece6",
  muted: "#64748b",
  faint: "#94a3b8",
  danger: "#dc3545",
  dangerSoft: "#fff1f1",
  warn: "#f59e0b",
  warnSoft: "#fffbeb",
  ink: "#0f172a",
  text: "#1e293b",
};

const DARK_T = {
  bg: "#0b1512",
  card: "#101b17",
  sidebar: "#0e1a16",
  sidebarDk: "#0c2a22",
  accent: "#34d399",
  accentSoft: "#0f2a20",
  green: "#34d399",
  greenSoft: "#0e2a22",
  teal: "#2dd4c8",
  tealSoft: "#0d2b2a",
  border: "#1e2d27",
  muted: "#94a3b8",
  faint: "#64748b",
  danger: "#f87171",
  dangerSoft: "#2a1414",
  warn: "#fbbf24",
  warnSoft: "#2a2210",
  ink: "#f1f5f9",
  text: "#e2e8f0",
};

const renderIconNode = (icon, props = {}) => {
  if (!icon) return null;
  if (isValidElement(icon)) return icon;
  if (typeof icon === "function" || typeof icon === "object") return createElement(icon, props);
  return icon;
};

/* ─── THEME CONTEXT ─────────────────────────────────────────────────── */
const ThemeContext = createContext({ theme: "light", T: LIGHT_T, toggleTheme: () => { } });

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      if (typeof window !== "undefined") {
        return localStorage.getItem("mbt_admin_theme") || "light";
      }
    } catch (e) { }
    return "light";
  });
  useEffect(() => {
    try {
      if (typeof window !== "undefined") localStorage.setItem("mbt_admin_theme", theme);
    } catch (e) { }
  }, [theme]);
  const toggleTheme = () => setTheme(t => (t === "light" ? "dark" : "light"));
  const T = theme === "dark" ? DARK_T : LIGHT_T;
  return <ThemeContext.Provider value={{ theme, T, toggleTheme }}>{children}</ThemeContext.Provider>;
}
function useThemeColors() { return useContext(ThemeContext).T; }
function useTheme() { return useContext(ThemeContext); }

const fmt = n =>
  `₹${Number(n || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmtDT = d => d ? new Date(d).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

const API_BASE = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "") + "/api";

const authH = () => {
  try {
    const admin = typeof window !== "undefined" ? JSON.parse(localStorage.getItem("mbt_admin_auth") || "null") : null;
    const token = admin?.token || (typeof window !== "undefined" ? localStorage.getItem("mbt_token") : null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch { return {}; }
};
async function apiFetch(path) {
  try { const r = await fetch(`${API_BASE}${path}`, { headers: authH() }); if (!r.ok) throw new Error(r.status); const d = await r.json(); return d.data || d || []; } catch (e) { console.error(e); return null; }
}
async function apiDownload(path, filename) {
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: authH() });
    if (!r.ok) {
      let msg = `Request failed (${r.status})`;
      try { msg = (await r.json()).error || msg; } catch { }
      throw new Error(msg);
    }
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
    return { ok: true };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
/* Fetch a binary file with auth and return the blob (used to preview PDFs). */
async function apiDownloadBlob(path) {
  try {
    const r = await fetch(`${API_BASE}${path}`, { headers: authH() });
    if (!r.ok) {
      let msg = `Request failed (${r.status})`;
      try { msg = (await r.json()).error || msg; } catch { }
      throw new Error(msg);
    }
    return { ok: true, blob: await r.blob() };
  } catch (e) {
    return { ok: false, error: e.message };
  }
}
async function apiMutate(method, path, body) {
  try { const r = await fetch(`${API_BASE}${path}`, { method, headers: { "Content-Type": "application/json", ...authH() }, body: JSON.stringify(body) }); if (!r.ok) throw new Error(r.status); const d = await r.json(); return { ok: true, data: d.data || d }; } catch (e) { return { ok: false, error: e.message }; }
}
const apiPatch = (p, b) => apiMutate("PATCH", p, b);
const apiPost = (p, b) => apiMutate("POST", p, b);
const apiPut = (p, b) => apiMutate("PUT", p, b);
const apiDelete = p => apiMutate("DELETE", p, {});

const SCFG = {
  upcoming: { bg: "#dbeafe", c: "#1d4ed8" },
  completed: { bg: "#dcfce7", c: "#166534" },
  cancelled: { bg: "#fee2e2", c: "#991b1b" },
  new: { bg: "#dbeafe", c: "#1d4ed8" },
  read: { bg: "#fef9c3", c: "#713f12" },
  replied: { bg: "#dcfce7", c: "#166534" },
  closed: { bg: "#f1f5f9", c: "#475569" },
  draft: { bg: "#f1f5f9", c: "#475569" },
  in_progress: { bg: "#dbeafe", c: "#1d4ed8" },
  submitted: { bg: "#fef9c3", c: "#713f12" },
  approved: { bg: "#dcfce7", c: "#166534" },
  rejected: { bg: "#fee2e2", c: "#991b1b" },
  active: { bg: "#dcfce7", c: "#166534" },
  inactive: { bg: "#fef9c3", c: "#713f12" },
  banned: { bg: "#fee2e2", c: "#991b1b" },
};

const MOCK_BUSES = [
  { id: 1, booking_reference: "BUS-X1234", passenger_name: "Kavya Reddy", passenger_email: "kavya@example.com", passenger_phone: "9876567890", operator: "RedBus Express", from_city: "Hyderabad", to_city: "Bengaluru", travel_date: "2026-06-05", departure_time: "21:00", arrival_time: "06:00", seat_numbers: "A1, A2", bus_type: "AC Sleeper", num_passengers: 2, total_price: 1800, status: "upcoming", created_at: "2026-05-08T12:00:00" },
  { id: 2, booking_reference: "BUS-Y5678", passenger_name: "Mohan Das", passenger_email: "mohan@example.com", passenger_phone: "9845123456", operator: "VRL Travels", from_city: "Mumbai", to_city: "Pune", travel_date: "2026-05-22", departure_time: "08:00", arrival_time: "11:30", seat_numbers: "B5", bus_type: "AC Seater", num_passengers: 1, total_price: 450, status: "upcoming", created_at: "2026-05-07T15:30:00" },
  { id: 3, booking_reference: "BUS-Z9012", passenger_name: "Sunita Joshi", passenger_email: "sunita@example.com", passenger_phone: "9712034567", operator: "KSRTC", from_city: "Bengaluru", to_city: "Mangalore", travel_date: "2026-04-15", departure_time: "23:00", arrival_time: "07:00", seat_numbers: "C3, C4", bus_type: "Volvo AC", num_passengers: 3, total_price: 2700, status: "completed", created_at: "2026-04-10T10:00:00" },
];
const MOCK_TRAINS = [
  { id: 1, booking_reference: "TRN-12345", passenger_name: "Deepak Chauhan", passenger_email: "deepak@example.com", passenger_phone: "9878901234", train_name: "Rajdhani Express", train_number: "12951", from_station: "NDLS", to_station: "MMCT", travel_date: "2026-06-20", departure_time: "16:35", arrival_time: "08:15+1", class: "3A", num_passengers: 2, total_price: 3200, pnr: "4521876543", status: "upcoming", created_at: "2026-05-09T10:30:00" },
  { id: 2, booking_reference: "TRN-67890", passenger_name: "Geeta Iyer", passenger_email: "geeta@example.com", passenger_phone: "9823078901", train_name: "Shatabdi Express", train_number: "12007", from_station: "MAS", to_station: "BZA", travel_date: "2026-05-30", departure_time: "06:00", arrival_time: "11:45", class: "CC", num_passengers: 1, total_price: 1150, pnr: "6789012345", status: "upcoming", created_at: "2026-05-11T13:00:00" },
  { id: 3, booking_reference: "TRN-24680", passenger_name: "Suresh Babu", passenger_email: "suresh@example.com", passenger_phone: "9701234567", train_name: "Duronto Express", train_number: "12213", from_station: "HWH", to_station: "PUNE", travel_date: "2026-04-05", departure_time: "20:10", arrival_time: "22:50+1", class: "2A", num_passengers: 4, total_price: 9600, pnr: "1234567890", status: "completed", created_at: "2026-04-01T09:00:00" },
];
const MOCK_ACTIVITIES = [
  { id: "act-1", title: "Jai Hind Sound & Light Show at Red Fort", city: "New Delhi", location: "New Delhi", duration: "1 hour", category: "Culture & Heritage", price: 500, oldPrice: 900, rating: 4.8, reviews: 155, description: "The Red Fort sound and light show brings Delhi's history alive.", imageUrl: "" },
  { id: "act-2", title: "The Great Indian Food Tour", city: "New Delhi", location: "Old Delhi", duration: "3 hours", category: "Food & Experiences", price: 3585, oldPrice: 4200, rating: 4.9, reviews: 82, description: "Taste iconic street food with a guided walk through Old Delhi.", imageUrl: "" },
];
const MOCK_ACTIVITY_BOOKINGS = [
  { id: 1, booking_reference: "ACT-1001", passenger_name: "Rahul Mehta", passenger_email: "rahul@example.com", passenger_phone: "9876543210", activity_title: "Jai Hind Sound & Light Show at Red Fort", city: "New Delhi", booking_date: "2026-06-10", num_participants: 2, total_price: 1000, status: "upcoming", created_at: "2026-05-20T10:00:00" },
  { id: 2, booking_reference: "ACT-1002", passenger_name: "Priya Nair", passenger_email: "priya@example.com", passenger_phone: "9845098450", activity_title: "The Great Indian Food Tour", city: "New Delhi", booking_date: "2026-05-15", num_participants: 3, total_price: 10755, status: "completed", created_at: "2026-05-01T09:30:00" },
  { id: 3, booking_reference: "ACT-1003", passenger_name: "Arjun Kapoor", passenger_email: "arjun@example.com", passenger_phone: "9900112233", activity_title: "Jai Hind Sound & Light Show at Red Fort", city: "New Delhi", booking_date: "2026-04-01", num_participants: 1, total_price: 500, status: "cancelled", created_at: "2026-03-20T14:00:00" },
];
const CITY_OPTIONS = [
  { label: "New Delhi", value: "New Delhi", code: "DEL" }, { label: "Jaipur", value: "Jaipur", code: "JAI" },
  { label: "Goa", value: "Goa", code: "GOA" }, { label: "Mumbai", value: "Mumbai", code: "BOM" },
  { label: "Paris", value: "Paris", code: "PAR" }, { label: "London", value: "London", code: "LON" },
  { label: "Phuket", value: "Phuket", code: "HKT" }, { label: "Dubai", value: "Dubai", code: "DXB" },
];
const CAT_OPTIONS = ["Sightseeing", "Walking Tour", "Adventure", "Food & Experiences", "Culture & Heritage", "Transfers"];
const PKG_CATS = [{ value: "beach", label: "Beach" }, { value: "adventure", label: "Adventure" }, { value: "cultural", label: "Cultural" }, { value: "honeymoon", label: "Honeymoon" }, { value: "family", label: "Family" }, { value: "wildlife", label: "Wildlife" }, { value: "pilgrimage", label: "Pilgrimage" }];

/* ─── SHARED UI ─────────────────────────────────────────────────────── */
function Badge({ status }) {
  const S = SCFG[status] || { bg: "#f1f5f9", c: "#475569" };
  return <span style={{ display: "inline-block", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 500, textTransform: "capitalize", background: S.bg, color: S.c, whiteSpace: "nowrap" }}>{status?.replace(/_/g, " ")}</span>;
}
function Avatar({ name, size = 32, bg = "#dbeafe", tc = "#1d4ed8" }) {
  return <div style={{ width: size, height: size, borderRadius: "50%", background: bg, color: tc, fontSize: size * 0.4, fontWeight: 500, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{(name?.[0] || "?").toUpperCase()}</div>;
}
function StatCard({ icon, label, value, sub, color = "#0d6efd" }) {
  const T = useThemeColors();
  return (
    <div style={{ background: T.card, borderRadius: 12, padding: "16px 18px", border: `1px solid ${T.border}`, display: "flex", alignItems: "center", gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", background: color + "18", color, flexShrink: 0 }}>{renderIconNode(icon, { size: 20, strokeWidth: 2 })}</div>
      <div>
        <div style={{ fontSize: 20, fontWeight: 600, color: T.ink, lineHeight: 1.1 }}>{value}</div>
        <div style={{ fontSize: 12, color: T.muted, fontWeight: 400, marginTop: 2 }}>{label}</div>
        {sub && <div style={{ fontSize: 11, color: T.faint, marginTop: 1 }}>{sub}</div>}
      </div>
    </div>
  );
}
function SearchBar({ value, onChange, placeholder }) {
  const T = useThemeColors();
  return (
    <div style={{ flex: 1, minWidth: 180, display: "flex", alignItems: "center", background: T.card, border: `1px solid ${T.border}`, borderRadius: 8, padding: "0 10px", gap: 6 }}>
      <Filter size={14} color={T.faint} />
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ flex: 1, border: "none", outline: "none", padding: "9px 0", fontSize: 13, background: "transparent", color: T.ink }} />
      {value && <button onClick={() => onChange("")} style={{ background: "none", border: "none", color: T.faint, cursor: "pointer", fontSize: 14 }}>×</button>}
    </div>
  );
}
function Sel({ value, onChange, options }) {
  const T = useThemeColors();
  return <select value={value} onChange={e => onChange(e.target.value)} style={{ padding: "9px 12px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, background: T.card, color: T.ink, cursor: "pointer", outline: "none" }}>{options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>;
}
function Btn({
  onClick,
  children,
  variant = "ghost",
  size = "md",
  disabled = false,
}) {
  const T = useThemeColors();
  const base = { border: "none", borderRadius: 8, cursor: "pointer", fontWeight: 500, display: "flex", alignItems: "center", gap: 6, fontSize: 13 };
  const pads = { sm: "5px 10px", md: "9px 16px", lg: "11px 22px" };
  const vars = {
    primary: { background: T.accent, color: "#fff" },
    green: { background: T.green, color: "#fff" },
    ghost: { background: T.card, color: T.muted, border: `1px solid ${T.border}` },
    danger: { background: T.dangerSoft, color: T.danger, border: `1px solid ${T.danger}55` },
  };
  return (
  <button
    onClick={onClick}
    disabled={disabled}
    style={{
      ...base,
      ...vars[variant],
      padding: pads[size],
      opacity: disabled ? 0.6 : 1,
      cursor: disabled ? "not-allowed" : "pointer",
    }}
  >
    {children}
  </button>
);
}
function TableWrap({ children }) {
  const T = useThemeColors();
  return <div style={{ background: T.card, borderRadius: 12, border: `1px solid ${T.border}`, overflow: "hidden", overflowX: "auto" }}><table
  style={{
    width: "100%",
    minWidth: 1180,
    borderCollapse: "collapse",
    fontSize: 13
  }}
>{children}</table></div>;
}
function Th({ children }) {
  const T = useThemeColors();
  return <th style={{ padding: "11px 13px", textAlign: "left", fontSize: 11, fontWeight: 600, color: T.muted, textTransform: "uppercase", letterSpacing: "0.5px", background: T.accentSoft, borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{children}</th>;
}
function Td({ children, style }) {
  const T = useThemeColors();

  return (
    <td
      style={{
        padding: "11px 13px",
        color: T.text,
        verticalAlign: "middle",
        borderBottom: `1px solid ${T.border}`,
        ...style,
      }}
    >
      {children}
    </td>
  );
}
function TRow({ children, onClick }) {
  const T = useThemeColors();
  const [h, sH] = useState(false);
  return <tr onClick={onClick} onMouseEnter={() => sH(true)} onMouseLeave={() => sH(false)} style={{ cursor: onClick ? "pointer" : "default", background: h ? T.accentSoft : "transparent", transition: "background .1s" }}>{children}</tr>;
}
function Modal({ onClose, title, subtitle, children, maxWidth = 640 }) {
  const T = useThemeColors();
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: T.card, borderRadius: 16, width: "100%", maxWidth, maxHeight: "90vh", display: "flex", flexDirection: "column", overflow: "hidden", boxShadow: "0 20px 60px rgba(0,0,0,0.15)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "18px 22px 14px", borderBottom: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 600, color: T.ink }}>{title}</div>
            {subtitle && <div style={{ fontSize: 12, color: T.faint, fontFamily: "monospace", marginTop: 2 }}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{ background: T.bg, border: "none", width: 30, height: 30, borderRadius: "50%", fontSize: 14, cursor: "pointer", color: T.muted, display: "flex", alignItems: "center", justifyContent: "center" }}><X size={14} /></button>
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "16px 22px 22px" }}>{children}</div>
      </div>
    </div>
  );
}
function SecTitle({ children }) {
  const T = useThemeColors();
  return <div style={{ fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.8px", color: T.faint, margin: "16px 0 8px" }}>{children}</div>;
}

/* ─── REFERENCE-FEATURE HELPERS ───────────────────────────────────── */

const SUPPORTED_CURRENCIES = [
  "INR", "USD", "EUR", "GBP", "AED", "SAR", "THB", "SGD", "MYR",
  "LKR", "NPR", "BDT", "AUD", "CAD", "CHF", "NZD", "JPY", "IDR",
  "VND", "PHP", "CNY", "ZAR",
];

/** Plain-text preview of rich HTML (library cards, customer summaries). */
const stripTags = (html) =>
  String(html || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

/** Upload one package image via the backend's existing upload conventions. */
async function uploadPackageImageFile(file) {
  const fd = new FormData();
  fd.append("image", file);
  const res = await fetch(`${API_BASE}/uploads/package-image`, {
    method: "POST",
    headers: authH(),
    body: fd,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Upload failed (HTTP ${res.status})`);
  return data.url;
}

/** List previously uploaded package images (reference "Image Library"). */
async function fetchPackageImageLibrary() {
  const r = await fetch(`${API_BASE}/uploads/package-images`, { headers: authH() });
  const d = await r.json().catch(() => []);
  if (!r.ok) throw new Error(d.error || `Failed to load images (HTTP ${r.status})`);
  return Array.isArray(d) ? d : Array.isArray(d.data) ? d.data : [];
}

/**
 * Route-map city field with suggestions from /api/cities/search
 * (reference Overview "Select Cities" flow, adapted as inline autocomplete
 * so the state/country columns stay editable).
 */
function CitySuggestInput({ value, onChange, style, placeholder }) {
  const T = useThemeColors();
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleInput = (text) => {
    onChange(text);
    clearTimeout(timer.current);
    const q = String(text || "").trim();
    if (q.length < 2) { setSuggestions([]); setOpen(false); return; }
    timer.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API_BASE}/cities/search?q=${encodeURIComponent(q)}`);
        const list = await r.json();
        if (Array.isArray(list)) { setSuggestions(list); setOpen(true); }
      } catch { setSuggestions([]); }
    }, 220);
  };

  return (
    <div style={{ position: "relative", minWidth: 0 }}>
      <input
        value={value || ""}
        onChange={e => handleInput(e.target.value)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        style={style}
        placeholder={placeholder}
        autoComplete="off"
      />
      {open && suggestions.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30,
            background: T.card, border: `1px solid ${T.border}`, borderRadius: 8,
            boxShadow: "0 10px 30px rgba(0,0,0,0.12)", maxHeight: 190,
            overflowY: "auto", padding: 4,
          }}
        >
          {suggestions.map(s => (
            <button
              key={s.id || s.name}
              type="button"
              // onMouseDown fires before the input's blur, so the pick wins.
              onMouseDown={e => { e.preventDefault(); onChange(s.name); setOpen(false); setSuggestions([]); }}
              style={{ display: "block", width: "100%", textAlign: "left", border: "none", background: "none", padding: "7px 9px", borderRadius: 6, fontSize: 12, color: T.ink, cursor: "pointer" }}
              onMouseEnter={e => { e.currentTarget.style.background = T.bg; }}
              onMouseLeave={e => { e.currentTarget.style.background = "none"; }}
            >
              {s.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Image Library picker (reference UI button on thumb / day / gallery
 * images): grid of previously uploaded package images; picking one calls
 * onSelect(url).
 */
function ImageLibraryModal({ open, onClose, onSelect }) {
  const T = useThemeColors();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    setLoading(true);
    setErr("");
    fetchPackageImageLibrary()
      .then(list => { if (mounted) setImages(list); })
      .catch(e => { if (mounted) setErr(e.message || "Could not load images"); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [open]);

  if (!open) return null;

  return (
    <Modal onClose={onClose} title="Image Library" subtitle="Previously uploaded package images" maxWidth={720}>
      {loading && <div style={{ color: T.faint, fontSize: 13, padding: 12 }}>Loading images…</div>}
      {err && <div style={{ color: T.danger, fontSize: 12, padding: "6px 0" }}>⚠ {err}</div>}
      {!loading && !err && !images.length && (
        <div style={{ color: T.faint, fontSize: 13, padding: "6px 0" }}>
          No images uploaded yet — use ⬆ Upload first, then it will appear here.
        </div>
      )}
      {!!images.length && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(140px,1fr))", gap: 10 }}>
          {images.map(img => (
            <button
              key={img.url || img.filename}
              type="button"
              onClick={() => { onSelect?.(img.url); onClose(); }}
              title={img.filename || img.url}
              style={{ border: `1px solid ${T.border}`, background: T.bg, borderRadius: 8, padding: 6, cursor: "pointer", textAlign: "left" }}
            >
              <img
                src={resolveAvatarUrl(img.url) || img.url}
                alt={img.filename || "Uploaded image"}
                style={{ width: "100%", height: 92, objectFit: "cover", borderRadius: 5, display: "block", background: T.card }}
                onError={e => { e.currentTarget.style.opacity = "0.15"; }}
              />
              <div style={{ fontSize: 10, color: T.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginTop: 4 }}>
                {img.filename || img.url}
              </div>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
}

/**
 * Rich-text field (reference description / display-text editors).
 * A contentEditable surface with a small formatting toolbar; the HTML is
 * sanitized again on the server before it is stored.
 */
function RichTextEditor({ value, onChange, minHeight = 130 }) {
  const T = useThemeColors();
  const ref = useRef(null);
  const lastHtml = useRef(value || "");

  // Sync external value changes (tab switches, package load) without
  // resetting the caret while the user is typing.
  useEffect(() => {
    if (ref.current && (value || "") !== lastHtml.current) {
      ref.current.innerHTML = value || "";
      lastHtml.current = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const commit = (html) => {
    lastHtml.current = html;
    onChange(html);
  };

  const exec = (cmd, arg) => {
    if (ref.current) ref.current.focus();
    try { document.execCommand(cmd, false, arg); } catch { /* ignore */ }
    commit(ref.current ? ref.current.innerHTML : "");
  };

  const tb = (title, cmd, arg, content) => (
    <button
      type="button"
      title={title}
      onMouseDown={e => e.preventDefault()}
      onClick={() => exec(cmd, arg)}
      style={{
        minWidth: 26, height: 24, padding: "0 6px", border: `1px solid ${T.border}`,
        borderRadius: 5, background: T.card, color: T.ink, cursor: "pointer",
        fontSize: 12, fontWeight: 600, display: "inline-flex", alignItems: "center",
        justifyContent: "center",
      }}
    >{content}</button>
  );

  return (
    <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, background: T.bg, overflow: "hidden" }}>
      <div style={{ display: "flex", gap: 4, padding: 6, borderBottom: `1px solid ${T.border}`, flexWrap: "wrap", alignItems: "center" }}>
        {tb("Bold", "bold", null, <b>B</b>)}
        {tb("Italic", "italic", null, <i>I</i>)}
        {tb("Underline", "underline", null, <u>U</u>)}
        {tb("Heading", "formatBlock", "h3", <span style={{ fontSize: 11 }}>H</span>)}
        {tb("Bulleted list", "insertUnorderedList", null, "• —")}
        {tb("Numbered list", "insertOrderedList", null, "1. —")}
        {tb("Quote", "formatBlock", "blockquote", "” ”")}
        {tb("Remove formatting", "removeFormat", null, "Tx")}
        <button
          type="button"
          title="Insert link"
          onMouseDown={e => e.preventDefault()}
          onClick={() => {
            const url = window.prompt("Link URL (https://…)");
            if (url) exec("createLink", url);
          }}
          style={{ height: 24, padding: "0 6px", border: `1px solid ${T.border}`, borderRadius: 5, background: T.card, color: T.ink, cursor: "pointer", fontSize: 12, fontWeight: 600 }}
        >🔗</button>
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={e => commit(e.currentTarget.innerHTML)}
        onBlur={e => commit(e.currentTarget.innerHTML)}
        style={{ minHeight, padding: 10, fontSize: 13, lineHeight: 1.6, color: T.ink, outline: "none", overflowY: "auto", wordBreak: "break-word" }}
      />
    </div>
  );
}

/** Small inline "type a name + Add" control used by chip editors. */
function ChipAdder({ onAdd, placeholder }) {
  const T = useThemeColors();
  const inputS = useInputStyle();
  const [value, setValue] = useState("");

  const submit = () => {
    const name = value.trim();
    if (!name) return;
    onAdd(name);
    setValue("");
  };

  return (
    <div style={{ display: "flex", gap: 6, minWidth: 0, flex: 1 }}>
      <input
        value={value}
        onChange={e => setValue(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
        placeholder={placeholder}
        style={{ ...inputS, padding: "6px 9px", fontSize: 12, flex: 1, minWidth: 0 }}
      />
      <button
        type="button"
        onClick={submit}
        title="Add"
        style={{ padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, color: T.accent, fontSize: 11, fontWeight: 700, cursor: "pointer" }}
      >+ Add</button>
    </div>
  );
}

/**
 * Image input with the project's real upload endpoint: URL field + file
 * upload (POST /api/uploads/package-image) + preview + remove.
 */
function ImageField({ value, onChange, label, hint, height = 76 }) {
  const T = useThemeColors();
  const inputS = useInputStyle();
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [libOpen, setLibOpen] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const url = await uploadPackageImageFile(file);
      onChange(url);
    } catch (error) {
      setErr(error.message || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {label && (
        <label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>{label}</label>
      )}
      <div className="pkg-image-field" style={{ display: "grid", gridTemplateColumns: "96px 1fr auto", gap: 8, alignItems: "center" }}>
        <div style={{ width: 96, height, borderRadius: 6, overflow: "hidden", background: T.card, border: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {value ? (
            <img
              src={resolveAvatarUrl(value) || value}
              alt="Preview"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
              onError={e => { e.target.style.opacity = "0.15"; }}
            />
          ) : (
            <span style={{ fontSize: 10, color: T.faint }}>No image</span>
          )}
        </div>
        <input value={value || ""} onChange={e => onChange(e.target.value)} style={inputS} placeholder="https://… or upload a file" />
        <div style={{ display: "flex", gap: 6 }}>
          <button
            type="button"
            onClick={() => fileRef.current && fileRef.current.click()}
            disabled={busy}
            title="Upload image from this computer"
            style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, color: busy ? T.faint : T.accent, fontSize: 11, fontWeight: 600, cursor: busy ? "wait" : "pointer", whiteSpace: "nowrap" }}
          >
            {busy ? "Uploading…" : "⬆ Upload"}
          </button>
          <button
            type="button"
            onClick={() => setLibOpen(true)}
            title="Pick a previously uploaded image"
            style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, color: T.accent, fontSize: 11, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}
          >
            🖼 Library
          </button>
          {value ? (
            <button
              type="button"
              aria-label="Remove image"
              title="Remove image"
              onClick={() => onChange("")}
              style={{ padding: "7px 9px", border: `1px solid ${T.danger}55`, borderRadius: 7, background: T.dangerSoft, color: T.danger, cursor: "pointer", fontSize: 11, fontWeight: 700 }}
            >✕</button>
          ) : null}
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" style={{ display: "none" }} onChange={handleFile} />
      {err && <div style={{ color: T.danger, fontSize: 11, marginTop: 4 }}>⚠ {err}</div>}
      {hint && !err && <div style={{ color: T.faint, fontSize: 11, marginTop: 4 }}>{hint}</div>}
      <ImageLibraryModal open={libOpen} onClose={() => setLibOpen(false)} onSelect={onChange} />
    </div>
  );
}

function DGrid({ children }) { return <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "2px 14px" }}>{children}</div>; }
function DRow({ label, value, full }) {
  const T = useThemeColors();
  return (
    <div style={{ display: "flex", flexDirection: "column", padding: "5px 0", borderBottom: `1px solid ${T.bg}`, gridColumn: full ? "1 / -1" : undefined }}>
      <span style={{ fontSize: 11, color: T.faint, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.3px" }}>{label}</span>
      <span style={{ fontSize: 13, color: T.ink, fontWeight: 400, marginTop: 2 }}>{value || "—"}</span>
    </div>
  );
}
function StatusActions({ statuses, current, onSelect, saving }) {
  const T = useThemeColors();
  const colors = { upcoming: T.accent, completed: T.green, cancelled: T.danger, approved: T.green, rejected: T.danger, in_progress: T.accent, submitted: T.warn, new: T.accent, read: T.warn, replied: T.green, closed: T.muted, active: T.green, inactive: T.warn, banned: T.danger, draft: T.muted };
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
      {statuses.map(s => {
        const c = colors[s] || T.muted;
        const isA = current === s;
        return <button key={s} onClick={() => onSelect(s)} disabled={saving || isA} style={{ padding: "7px 14px", borderRadius: 8, fontSize: 12, fontWeight: 500, border: `1.5px solid ${c}`, cursor: isA ? "default" : "pointer", background: isA ? c : T.card, color: isA ? "#fff" : c, opacity: saving ? .5 : 1, textTransform: "capitalize" }}>{s.replace(/_/g, " ")}</button>;
      })}
    </div>
  );
}
function useInputStyle() {
  const T = useThemeColors();
  return { width: "100%", padding: "9px 11px", border: `1px solid ${T.border}`, borderRadius: 8, fontSize: 13, outline: "none", color: T.ink, background: T.bg, boxSizing: "border-box" };
}
const API_ORIGIN = (process.env.REACT_APP_API_URL || "").replace(/\/api$/, "");

function resolveAvatarUrl(avatar) {
  if (!avatar) return null;
  if (/^https?:\/\//i.test(avatar)) return avatar;
  return `${API_ORIGIN}${avatar.startsWith("/") ? "" : "/"}${avatar}`;
}
function ViewBtn({ onClick }) {
  const T = useThemeColors();
  return <button onClick={e => { e.stopPropagation(); onClick(); }} style={{ padding: "5px 11px", background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, fontWeight: 500, color: T.accent, cursor: "pointer", whiteSpace: "nowrap" }}>View →</button>;
}

/* ══════════════════════════════════════════════════════════════════════
   ACTIVITIES SECTION
══════════════════════════════════════════════════════════════════════ */
function ActivitiesSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();
  const [activities, setActivities] = useState(MOCK_ACTIVITIES);
  const [search, setSearch] = useState("");
  const [cityFilter, setCityFilter] = useState("all");
  const [sort, setSort] = useState("default");
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ title: "", city: "", location: "", duration: "", category: "", price: "1299", oldPrice: "1800", rating: "4.8", reviews: "45", description: "", imageUrl: "" });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = activities.filter(a => {
      const mc = cityFilter === "all" || a.city === cityFilter;
      const mq = !q || `${a.title} ${a.city} ${a.location} ${a.category}`.toLowerCase().includes(q);
      return mc && mq;
    });
    if (sort === "price_low") rows = [...rows].sort((a, b) => a.price - b.price);
    if (sort === "price_high") rows = [...rows].sort((a, b) => b.price - a.price);
    if (sort === "rating") rows = [...rows].sort((a, b) => b.rating - a.rating);
    return rows;
  }, [activities, cityFilter, search, sort]);

  const stats = useMemo(() => ({
    total: activities.length,
    avgRating: activities.length ? (activities.reduce((s, a) => s + Number(a.rating || 0), 0) / activities.length).toFixed(1) : "0.0",
    avgPrice: activities.length ? Math.round(activities.reduce((s, a) => s + Number(a.price || 0), 0) / activities.length) : 0,
    cities: new Set(activities.map(a => a.city).filter(Boolean)).size
  }), [activities]);

  const openEdit = a => { setEditId(a.id); setForm({ title: a.title || "", city: a.city || "", location: a.location || "", duration: a.duration || "", category: a.category || "", price: String(a.price || ""), oldPrice: String(a.oldPrice || ""), rating: String(a.rating || ""), reviews: String(a.reviews || ""), description: a.description || "", imageUrl: a.imageUrl || "" }); setFormOpen(true); };
  const openNew = () => { setEditId(null); setForm({ title: "", city: "", location: "", duration: "", category: "", price: "1299", oldPrice: "1800", rating: "4.8", reviews: "45", description: "", imageUrl: "" }); setFormOpen(true); };
  const closeForm = () => { setFormOpen(false); setEditId(null); };
  const handleSave = e => {
    e.preventDefault();
    if (!form.title.trim() || !form.city) return;
    const city = CITY_OPTIONS.find(c => c.value === form.city);
    const payload = { ...form, id: editId || `act-${Date.now()}`, cityCode: city?.code || "", price: Number(form.price || 0), oldPrice: Number(form.oldPrice || 0), rating: Number(form.rating || 0), reviews: Number(form.reviews || 0) };
    setActivities(prev => editId ? prev.map(a => a.id === editId ? payload : a) : [payload, ...prev]);
    closeForm();
  };
  const del = id => setActivities(prev => prev.filter(a => a.id !== id));
  const disc = a => { const o = Number(a.oldPrice), p = Number(a.price); return (!o || o <= p) ? 0 : Math.round(((o - p) / o) * 100); };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard icon={<Activity size={18} />} label="Total Activities" value={stats.total} color={T.accent} />
        <StatCard icon={<Star size={18} />} label="Avg Rating" value={`${stats.avgRating}★`} color={T.green} />
        <StatCard icon={<IndianRupee size={18} />} label="Avg Price" value={fmt(stats.avgPrice)} color={T.teal} />
        <StatCard icon={<MapPin size={18} />} label="Cities Active" value={stats.cities} color="#7c3aed" />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search title, location…" />
        <Sel value={cityFilter} onChange={setCityFilter} options={[{ value: "all", label: "All Cities" }, ...CITY_OPTIONS.map(c => ({ value: c.value, label: c.label }))]} />
        <Sel value={sort} onChange={setSort} options={[{ value: "default", label: "Default" }, { value: "rating", label: "Top Rated" }, { value: "price_low", label: "Price ↑" }, { value: "price_high", label: "Price ↓" }]} />
        <span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} results</span>
        <Btn onClick={openNew} variant="primary"><Plus size={14} />Add Activity</Btn>
      </div>
      <TableWrap>
        <thead><tr><Th>Activity</Th><Th>City</Th><Th>Duration</Th><Th>Rating</Th><Th>Price</Th><Th>Discount</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: T.faint }}>No activities found</td></tr>
            : filtered.map(a => (
              <TRow key={a.id}>
                <Td><div style={{ fontWeight: 500, color: T.ink }}>{a.title}</div><div style={{ fontSize: 11, color: T.faint }}>{a.location || a.category || "Activity"}</div></Td>
                <Td><span style={{ background: T.accentSoft, color: T.accent, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 20 }}>{a.city}</span></Td>
                <Td style={{ fontSize: 12, color: T.muted }}>{a.duration || "—"}</Td>
                <Td><span style={{ color: T.green, fontWeight: 600 }}>{a.rating}★</span><span style={{ fontSize: 11, color: T.faint, marginLeft: 4 }}>({a.reviews})</span></Td>
                <Td><strong style={{ fontWeight: 600 }}>{fmt(a.price)}</strong>{a.oldPrice ? <del style={{ color: T.faint, marginLeft: 6, fontSize: 11 }}>{fmt(a.oldPrice)}</del> : null}</Td>
                <Td>{disc(a) ? <span style={{ background: T.greenSoft, color: T.green, fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 6 }}>{disc(a)}% off</span> : "—"}</Td>
                <Td>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn onClick={() => openEdit(a)} variant="ghost" size="sm"><Edit size={12} />Edit</Btn>
                    <Btn onClick={() => del(a.id)} variant="danger" size="sm"><X size={12} /></Btn>
                  </div>
                </Td>
              </TRow>
            ))}
        </tbody>
      </TableWrap>

      {formOpen && (
        <Modal onClose={closeForm} title={editId ? "Edit Activity" : "Add New Activity"} subtitle={editId ? "Update activity details" : "Create a new city activity"} maxWidth={700}>
          <form onSubmit={handleSave}>
            <SecTitle>Basic Info</SecTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <div style={{ gridColumn: "1/-1" }}><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Activity Title *</label><input name="title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} style={inputS} placeholder="e.g. Taj Mahal Sunrise Tour" required /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>City *</label><select name="city" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} style={inputS} required><option value="">Select city…</option>{CITY_OPTIONS.map(c => <option key={c.code} value={c.value}>{c.label}</option>)}</select></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Location/Area</label><input value={form.location} onChange={e => setForm(f => ({ ...f, location: e.target.value }))} style={inputS} placeholder="e.g. Old Delhi" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Duration</label><input value={form.duration} onChange={e => setForm(f => ({ ...f, duration: e.target.value }))} style={inputS} placeholder="3 hours" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Category</label><select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} style={inputS}><option value="">Select…</option>{CAT_OPTIONS.map(c => <option key={c} value={c}>{c}</option>)}</select></div>
            </div>
            <SecTitle>Pricing & Rating</SecTitle>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12 }}>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Price (₹) *</label><input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))} style={inputS} min="0" required /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Old Price (₹)</label><input type="number" value={form.oldPrice} onChange={e => setForm(f => ({ ...f, oldPrice: e.target.value }))} style={inputS} min="0" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Rating</label><input type="number" value={form.rating} onChange={e => setForm(f => ({ ...f, rating: e.target.value }))} style={inputS} min="0" max="5" step="0.1" /></div>
              <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Reviews</label><input type="number" value={form.reviews} onChange={e => setForm(f => ({ ...f, reviews: e.target.value }))} style={inputS} min="0" /></div>
            </div>
            <SecTitle>Details</SecTitle>
            <div style={{ marginBottom: 10 }}><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Description</label><textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} rows={3} style={{ ...inputS, resize: "vertical" }} placeholder="Short description…" /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Image URL</label><input value={form.imageUrl} onChange={e => setForm(f => ({ ...f, imageUrl: e.target.value }))} style={inputS} placeholder="https://…" /></div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
              <Btn onClick={closeForm} variant="ghost">Cancel</Btn>
              <button type="submit" style={{ padding: "9px 24px", border: "none", borderRadius: 8, background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>{editId ? "Save Changes" : "Add Activity"}</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   ACTIVITY BOOKINGS SECTION
══════════════════════════════════════════════════════════════════════ */
function ActivityBookingModal({ booking, onClose, onStatusChange }) {
  const [status, setStatus] = useState(booking.status);
  const [saving, setSaving] = useState(false);
  const handle = s => { setSaving(true); setStatus(s); onStatusChange(booking.id, s); setSaving(false); };
  return (
    <Modal onClose={onClose} title="Activity Booking" subtitle={booking.booking_reference}>
      <DGrid>
        <DRow label="Participant" value={booking.passenger_name} />
        <DRow label="Email" value={booking.passenger_email} />
        <DRow label="Phone" value={booking.passenger_phone} />
        <DRow label="City" value={booking.city} />
        <DRow label="Activity" value={booking.activity_title} full />
        <DRow label="Booking Date" value={fmtDate(booking.booking_date)} />
        <DRow label="Participants" value={booking.num_participants} />
        <DRow label="Total" value={fmt(booking.total_price)} />
        <DRow label="Booked On" value={fmtDT(booking.created_at)} />
      </DGrid>

<SecTitle>Update Status</SecTitle>

<StatusActions
  statuses={["upcoming", "completed", "cancelled"]}
  current={status}
  onSelect={handle}
  saving={saving}
/>
    </Modal>
  );
}

function ActivityBookingEditModal({ booking, onClose, onSave }) {
  const T = useThemeColors();
  const inputS = useInputStyle();
  const [form, setForm] = useState({
    passenger_name: booking.passenger_name || "",
    passenger_email: booking.passenger_email || "",
    passenger_phone: booking.passenger_phone || "",
    activity_title: booking.activity_title || "",
    city: booking.city || "",
    booking_date: booking.booking_date || "",
    num_participants: String(booking.num_participants || 1),
    total_price: String(booking.total_price || 0),
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  
  const handleSave = () => {
    onSave({ ...booking, ...form, num_participants: Number(form.num_participants) || 1, total_price: Number(form.total_price) || 0 });
    onClose();
  };
  return (
    <Modal onClose={onClose} title="Modify Activity Booking" subtitle={booking.booking_reference} maxWidth={620}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1/-1" }}><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Activity</label><input value={form.activity_title} onChange={e => set("activity_title", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Participant Name</label><input value={form.passenger_name} onChange={e => set("passenger_name", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Email</label><input value={form.passenger_email} onChange={e => set("passenger_email", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Phone</label><input value={form.passenger_phone} onChange={e => set("passenger_phone", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>City</label><input value={form.city} onChange={e => set("city", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Booking Date</label><input type="date" value={form.booking_date} onChange={e => set("booking_date", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Participants</label><input type="number" min="1" value={form.num_participants} onChange={e => set("num_participants", e.target.value)} style={inputS} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Total Price (₹)</label><input type="number" min="0" value={form.total_price} onChange={e => set("total_price", e.target.value)} style={inputS} /></div>
      </div>
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <Btn onClick={onClose} variant="ghost">Cancel</Btn>
        <button onClick={handleSave} style={{ padding: "9px 24px", border: "none", borderRadius: 8, background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Save Changes</button>
      </div>
    </Modal>
  );
}

function ActivityBookingsSection() {
  const T = useThemeColors();
  const [bookings, setBookings] = useState(MOCK_ACTIVITY_BOOKINGS);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return bookings.filter(b => {
      const mq = !q || `${b.passenger_name} ${b.booking_reference} ${b.activity_title} ${b.city}`.toLowerCase().includes(q);
      const ms = statusFilter === "all" || b.status === statusFilter;
      return mq && ms;
    });
  }, [bookings, search, statusFilter]);

  const stats = useMemo(() => ({
    total: bookings.length,
    upcoming: bookings.filter(b => b.status === "upcoming").length,
    completed: bookings.filter(b => b.status === "completed").length,
    cancelled: bookings.filter(b => b.status === "cancelled").length,
  }), [bookings]);

  const handleStatusChange = (id, s) => setBookings(prev => prev.map(b => b.id === id ? { ...b, status: s } : b));
  const handleSaveEdit = updated => setBookings(prev => prev.map(b => b.id === updated.id ? updated : b));
  const handleDelete = id => { setBookings(prev => prev.filter(b => b.id !== id)); setCancelConfirm(null); setSelected(null); };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard icon={<CalendarDays size={18} />} label="Total Bookings" value={stats.total} color={T.accent} />
        <StatCard icon={<Clock size={18} />} label="Upcoming" value={stats.upcoming} color={T.green} />
        <StatCard icon={<CheckCircle size={18} />} label="Completed" value={stats.completed} color={T.teal} />
        <StatCard icon={<Ban size={18} />} label="Cancelled" value={stats.cancelled} color={T.danger} />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search participant, ref, activity…" />
        <Sel value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All Status" }, { value: "upcoming", label: "Upcoming" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} />
        <span style={{ fontSize: 13, color: T.faint }}>{filtered.length} bookings</span>
      </div>
      <TableWrap>
        <thead><tr><Th>Ref</Th><Th>Participant</Th><Th>Activity</Th><Th>City</Th><Th>Date</Th><Th>Pax</Th><Th>Amount</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: T.faint }}>No activity bookings found</td></tr>
            : filtered.map(b => (
              <TRow key={b.id} onClick={() => setSelected(b)}>
                <Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b.booking_reference}</Td>
                <Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={b.passenger_name} bg="#ede9fe" tc="#6d28d9" /><div><div style={{ fontWeight: 500, color: T.ink }}>{b.passenger_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.passenger_email}</div></div></div></Td>
                <Td style={{ fontSize: 12, color: T.muted, maxWidth: 200 }}>{b.activity_title}</Td>
                <Td><span style={{ background: T.accentSoft, color: T.accent, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 20 }}>{b.city}</span></Td>
                <Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b.booking_date)}</Td>
                <Td>{b.num_participants}</Td>
                <Td style={{ fontWeight: 600, color: T.ink }}>{fmt(b.total_price)}</Td>
                <Td><Badge status={b.status} /></Td>
                <Td>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", minWidth: 230 }}>
                    <Btn onClick={e => { e.stopPropagation(); setSelected(b); }} variant="ghost" size="sm"><Eye size={12} />View</Btn>
                    <Btn onClick={e => { e.stopPropagation(); setEditing(b); }} variant="ghost" size="sm"><Edit size={12} />Edit</Btn>
                    <Btn onClick={e => { e.stopPropagation(); setCancelConfirm(b); }} variant="danger" size="sm"><X size={12} />Delete</Btn>
                  </div>
                </Td>
              </TRow>
            ))}
        </tbody>
      </TableWrap>

      {selected && <ActivityBookingModal booking={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { handleStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}
      {editing && <ActivityBookingEditModal booking={editing} onClose={() => setEditing(null)} onSave={handleSaveEdit} />}

      {cancelConfirm && (
        <Modal onClose={() => setCancelConfirm(null)} title="Delete Booking?" subtitle={cancelConfirm.booking_reference} maxWidth={480}>
          <div style={{ background: T.dangerSoft, borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: `1px solid ${T.danger}55` }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <AlertTriangle size={20} color={T.danger} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, color: T.danger, marginBottom: 4 }}>This action cannot be undone</div>
                <div style={{ fontSize: 13, color: T.text }}>Booking <strong style={{ fontWeight: 600 }}>{cancelConfirm.booking_reference}</strong> for <strong style={{ fontWeight: 600 }}>{cancelConfirm.passenger_name}</strong> will be permanently removed.</div>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
            <Btn onClick={() => setCancelConfirm(null)} variant="ghost">Go Back</Btn>
            <button onClick={() => handleDelete(cancelConfirm.id)} style={{ padding: "9px 24px", border: "none", borderRadius: 8, background: T.danger, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Confirm Delete</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   BOOKING MANAGEMENT SECTION
══════════════════════════════════════════════════════════════════════ */
function ActivityAdminPanel({ view }) {
  const T = useThemeColors();
  const cityStats = CITY_OPTIONS.map(city => ({ ...city, count: MOCK_ACTIVITIES.filter(a => a.city === city.value).length }));
  const categoryStats = CAT_OPTIONS.map(category => ({ category, count: MOCK_ACTIVITIES.filter(a => a.category === category).length }));
  const topActivities = [...MOCK_ACTIVITIES].sort((a, b) => Number(b.reviews || 0) - Number(a.reviews || 0)).slice(0, 6);

  if (view === "activities") return <ActivitiesSection />;
  if (view === "bookings") return <ActivityBookingsSection />;
  if (view === "cities") return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14 }}>{cityStats.map(city => <div key={city.code} style={{ background: T.card, border: `1px solid ${T.border}`, borderTop: `3px solid ${T.accent}`, borderRadius: 12, padding: 18 }}><div style={{ fontSize: 11, fontWeight: 600, color: T.accent, letterSpacing: 1, textTransform: "uppercase" }}>{city.code}</div><div style={{ fontSize: 17, fontWeight: 600, color: T.ink, marginTop: 8 }}>{city.label}</div><div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>{city.count} activities</div></div>)}</div>;
  if (view === "categories") return <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(210px,1fr))", gap: 14 }}>{categoryStats.map(item => <div key={item.category} style={{ background: T.card, border: `1px solid ${T.border}`, borderTop: `3px solid ${T.green}`, borderRadius: 12, padding: 18 }}><div style={{ fontSize: 11, fontWeight: 600, color: T.green, letterSpacing: 1, textTransform: "uppercase" }}>Category</div><div style={{ fontSize: 16, fontWeight: 600, color: T.ink, marginTop: 8 }}>{item.category}</div><div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>{item.count} activities</div></div>)}</div>;
  if (view === "analytics") return <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, overflow: "hidden" }}><div style={{ padding: "16px 18px", borderBottom: `1px solid ${T.border}`, fontSize: 15, fontWeight: 600, color: T.ink }}>Top Activity Performance</div>{topActivities.map(a => <div key={a.id} style={{ display: "flex", justifyContent: "space-between", gap: 14, padding: "14px 18px", borderBottom: `1px solid ${T.border}` }}><div><div style={{ fontSize: 14, fontWeight: 500, color: T.ink }}>{a.title}</div><div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>{a.city} - {a.reviews} reviews</div></div><div style={{ fontSize: 14, fontWeight: 600, color: T.warn, whiteSpace: "nowrap" }}>{a.rating} star</div></div>)}</div>;
  return <div style={{ background: T.card, border: `1px solid ${T.border}`, borderRadius: 12, padding: "46px 20px", textAlign: "center" }}><div style={{ fontSize: 20, fontWeight: 600, color: T.ink }}>No activity bookings yet</div><div style={{ fontSize: 13, color: T.muted, marginTop: 8 }}>Booking data will appear here after activity booking integration is connected.</div></div>;
}

function BookingManagementSection({ flights, hotels, buses, trains, holidayBookings, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [cancelConfirm, setCancelConfirm] = useState(null);

  const allBookings = useMemo(() => [
  ...flights.map(b => ({
    ...b,
    _type: "Flight",
    _name: b.passenger_name,
    _email: b.passenger_email,
    _ref: b.booking_reference,
    _detail: `${b.depart_code}→${b.arrive_code}`,
    _amount: b.total_price,
    _date: b.travel_date
  })),
  ...hotels.map(b => ({
    ...b,
    _type: "Hotel",
    _name: `${b.guest_first_name} ${b.guest_last_name}`,
    _email: b.guest_email,
    _ref: b.booking_reference,
    _detail: b.hotel_name,
    _amount: b.grand_total,
    _date: b.check_in
  })),
  ...buses.map(b => ({
    ...b,
    _type: "Bus",
    _name: b.passenger_name,
    _email: b.passenger_email,
    _ref: b.booking_reference,
    _detail: `${b.from_city}→${b.to_city}`,
    _amount: b.total_price,
    _date: b.travel_date
  })),
], [flights, hotels, buses]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return allBookings.filter(b => {
      const mq = !q || b._name?.toLowerCase().includes(q) || b._ref?.toLowerCase().includes(q) || b._detail?.toLowerCase().includes(q);
      const mt = typeFilter === "all" || b._type === typeFilter;
      const ms = statusFilter === "all" || b.status === statusFilter;
      return mq && mt && ms;
    });
  }, [allBookings, search, typeFilter, statusFilter]);

  const stats = useMemo(() => ({
    total: allBookings.length,
    upcoming: allBookings.filter(b => b.status === "upcoming").length,
    completed: allBookings.filter(b => b.status === "completed").length,
    cancelled: allBookings.filter(b => b.status === "cancelled").length,
  }), [allBookings]);

  const typeColors = {
  "Flight": T.accent,
  "Hotel": "#be185d",
  "Bus": T.green
};

  const doCancel = async b => {
  if (!b?.id || !b?._type) return;

  try {
    // Flight and Hotel use the Phase 5 cancellation
    // init -> confirm flow.
    if (b._type === "Flight" || b._type === "Hotel") {
      const base =
        b._type === "Flight"
          ? `/flight-bookings/${b.id}`
          : `/hotel-bookings/${b.id}`;

      // Step 1: initialize cancellation
      const initRes = await apiPost(
        `${base}/cancellation-init?admin=1`,
        {}
      );

      if (!initRes.ok) {
        alert(`Cancellation failed: ${initRes.error}`);
        return;
      }

      const requestId =
        initRes.data?.requestId ||
        `${b._type.toLowerCase()}_cancel_${Date.now()}`;

      // Step 2: confirm cancellation
      const confirmRes = await apiPost(
        `${base}/cancellation-confirm?admin=1`,
        { requestId }
      );

      if (!confirmRes.ok) {
        alert(`Cancellation confirmation failed: ${confirmRes.error}`);
        return;
      }

      const nextStatus =
  confirmRes.data?.bookingStatus || "cancelled";

onStatusChange &&
  onStatusChange(b._type, b.id, nextStatus);

      setCancelConfirm(null);

      alert(
        confirmRes.data?.message ||
        "Booking cancellation request submitted successfully."
      );

      return;
    }
// Bus cancellation already has its own supplier cancellation flow.
// Do not replace it with the generic status PATCH here.
if (b._type === "Bus") {
  const res = await apiPost(
    `/admin/bus/${b.id}/cancel`,
    {}
  );

  if (!res.ok) {
    alert(`Cancellation failed: ${res.error}`);
    return;
  }

  onStatusChange &&
    onStatusChange(
      "Bus",
      b.id,
      res.data?.bookingStatus || "CANCEL_INITIATED"
    );

  setCancelConfirm(null);

  alert(
    res.data?.message ||
    "Bus cancellation request submitted successfully."
  );

  return;
}
  } catch (err) {
    console.error("Booking cancellation failed:", err);
    alert("Cancellation failed. Please try again.");
  }
};

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard icon={<BookOpen size={18} />} label="Total Bookings" value={stats.total} color={T.accent} />
        <StatCard icon={<Clock size={18} />} label="Upcoming" value={stats.upcoming} color={T.green} />
        <StatCard icon={<CheckCircle size={18} />} label="Completed" value={stats.completed} color={T.teal} />
        <StatCard icon={<Ban size={18} />} label="Cancelled" value={stats.cancelled} color={T.danger} />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search name, ref, detail…" />
        <Sel
  value={typeFilter}
  onChange={setTypeFilter}
  options={[
    { value: "all", label: "All Types" },
    { value: "Flight", label: "Flights" },
    { value: "Hotel", label: "Hotels" },
    { value: "Bus", label: "Buses" }
  ]}
/>
        <Sel value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All Status" }, { value: "upcoming", label: "Upcoming" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} />
        <span style={{ fontSize: 13, color: T.faint }}>{filtered.length} records</span>
      </div>
      <TableWrap>
        <thead><tr><Th>Ref</Th><Th>Type</Th><Th>Passenger</Th><Th>Detail</Th><Th>Date</Th><Th>Amount</Th><Th>Status</Th><Th>Actions</Th></tr></thead>
        <tbody>
          {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>No bookings found</td></tr>
            : filtered.map((b, i) => (
              <TRow key={b._ref + i} onClick={() => setSelected(b)}>
                <Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b._ref}</Td>
                <Td><span style={{ background: (typeColors[b._type] || T.accent) + "18", color: typeColors[b._type] || T.accent, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 20 }}>{b._type}</span></Td>
                <Td><div style={{ fontWeight: 500, color: T.ink }}>{b._name}</div><div style={{ fontSize: 11, color: T.faint }}>{b._email}</div></Td>
                <Td style={{ fontSize: 12, color: T.muted }}>{b._detail}</Td>
                <Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b._date)}</Td>
                <Td style={{ fontWeight: 600, color: T.ink }}>{fmt(b._amount)}</Td>
                <Td><Badge status={b.status} /></Td>
                <Td>
                  <div style={{ display: "flex", gap: 7, flexWrap: "wrap", minWidth: 310 }}>
                    <Btn onClick={e => { e.stopPropagation(); setSelected(b); }} variant="ghost" size="sm"><Eye size={12} />View Booking</Btn>
                    {b.status !== "cancelled" && (
  <Btn
    onClick={e => {
      e.stopPropagation();
      setSelected(b);
    }}
    variant="ghost"
    size="sm"
  >
    <Edit size={12} />
    Modify Booking
  </Btn>
)}
                    {b.status === "upcoming" && <Btn onClick={e => { e.stopPropagation(); setCancelConfirm(b); }} variant="danger" size="sm"><Ban size={12} />Cancel Booking</Btn>}
                  </div>
                </Td>
              </TRow>
            ))}
        </tbody>
      </TableWrap>

      {selected && (
  selected._type === "Flight"
    ? <FlightModal
        booking={selected}
        onClose={() => setSelected(null)}
        onStatusChange={(id, s) => {
          onStatusChange("Flight", id, s);
          setSelected(v => v?.id === id ? { ...v, status: s } : v);
        }}
      />
    : selected._type === "Hotel"
      ? <HotelModal
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(id, s) => {
            onStatusChange("Hotel", id, s);
            setSelected(v => v?.id === id ? { ...v, status: s } : v);
          }}
        />
      : <BusModal
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(id, s) => {
            onStatusChange("Bus", id, s);
            setSelected(v => v?.id === id ? { ...v, status: s } : v);
          }}
        />
)}

      {cancelConfirm && (
        <Modal onClose={() => setCancelConfirm(null)} title="Cancel Booking?" subtitle={cancelConfirm._ref} maxWidth={480}>
          <div style={{ background: T.dangerSoft, borderRadius: 10, padding: "14px 16px", marginBottom: 16, border: `1px solid ${T.danger}55` }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
              <AlertTriangle size={20} color={T.danger} style={{ flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 600, color: T.danger, marginBottom: 4 }}>
  Confirm cancellation
</div>

<div style={{ fontSize: 13, color: T.text }}>
  Booking <strong style={{ fontWeight: 600 }}>
    {cancelConfirm._ref}
  </strong>{" "}
  for <strong style={{ fontWeight: 600 }}>
    {cancelConfirm._name}
  </strong>{" "}
  will be submitted for cancellation.
</div>
              </div>
            </div>
          </div>
          <DGrid>
            <DRow label="Type" value={cancelConfirm._type} />
            <DRow label="Amount" value={fmt(cancelConfirm._amount)} />
            <DRow label="Travel Date" value={fmtDate(cancelConfirm._date)} />
            <DRow label="Status" value={<Badge status={cancelConfirm.status} />} />
          </DGrid>
          <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
            <Btn onClick={() => setCancelConfirm(null)} variant="ghost">Go Back</Btn>
            <button onClick={() => doCancel(cancelConfirm)} style={{ padding: "9px 24px", border: "none", borderRadius: 8, background: T.danger, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer" }}>Confirm Cancellation</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function RefundReviewRow({ refund, onProcessed }) {
  const T = useThemeColors();

  const supplierAmount = Number(
    refund.supplier_refund_amount || 0
  );

  const totalPaid = Number(
    refund.total_amount_paid || 0
  );

  const supplierCancellationCharges = Number(
  refund.supplier_cancellation_charges || 0
);

  const [finalAmount, setFinalAmount] = useState(
    supplierAmount.toFixed(2)
  );

  const [reason, setReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const handleProcess = async () => {
    const amount = Number(finalAmount);

    if (!Number.isFinite(amount) || amount <= 0) {
      alert("Final refund amount must be greater than zero.");
      return;
    }

    if (amount > supplierAmount) {
      alert(
        `Final refund cannot exceed supplier refund of ${fmt(
          supplierAmount
        )}.`
      );
      return;
    }

    if (totalPaid > 0 && amount > totalPaid) {
  alert(
    `Final refund cannot exceed total paid of ${fmt(
      totalPaid
    )}.`
  );
  return;
}

    setProcessing(true);

    try {
      /*
 * Admin approval calls the backend refund endpoint.
 * The backend validates the refund and initiates Easebuzz.
 */
      const res = await apiPost(
        `/admin/reports/refunds/${refund.id}/process`,
        {
          final_refund_amount: amount,
          reason: reason.trim() || null,
        }
      );

      if (!res.ok) {
        alert(
          `Refund processing failed: ${res.error}`
        );
        return;
      }

      alert(
        res.data?.message ||
        "Refund submitted successfully."
      );

      onProcessed?.();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <tr>
      <Td
        style={{
          fontFamily: "monospace",
          fontSize: 12,
          fontWeight: 600,
        }}
      >
        {refund.booking_reference || "—"}
      </Td>

      <Td>
        <span
          style={{
            background: T.accentSoft,
            color: T.accent,
            padding: "3px 9px",
            borderRadius: 20,
            fontSize: 11,
            textTransform: "capitalize",
          }}
        >
          {refund.service}
        </span>
      </Td>

      <Td>
        <div
          style={{
            fontWeight: 500,
            color: T.ink,
          }}
        >
          {refund.customer_name || "—"}
        </div>

        <div
          style={{
            fontSize: 11,
            color: T.faint,
          }}
        >
          {refund.customer_email || ""}
        </div>
      </Td>

      <Td>
        {fmt(totalPaid)}
      </Td>

      <Td>
  <div
    style={{
      fontSize: 12,
      lineHeight: 1.6,
      color: T.text,
    }}
  >
    <div>
      <span style={{ color: T.faint }}>
        Cancellation Charge:
      </span>{" "}
      <strong>
        {refund.supplier_cancellation_charges !== null &&
        refund.supplier_cancellation_charges !== undefined
          ? fmt(Number(refund.supplier_cancellation_charges))
          : "—"}
      </strong>
    </div>

    <div>
      <span style={{ color: T.faint }}>
        Service Fee:
      </span>{" "}
      <strong>
        {refund.supplier_cancellation_service_fee !== null &&
        refund.supplier_cancellation_service_fee !== undefined
          ? fmt(Number(refund.supplier_cancellation_service_fee))
          : "—"}
      </strong>
    </div>
  </div>
</Td>

      <Td
        style={{
          fontWeight: 600,
          color: T.green,
        }}
      >
        {fmt(supplierAmount)}
      </Td>

      <Td>
        <div
          style={{
            fontSize: 12,
            color: T.text,
          }}
        >
          {refund.payment_gateway || "—"}
        </div>
      </Td>

      <Td>
        <span
          style={{
            fontFamily: "monospace",
            fontSize: 11,
            color: T.muted,
          }}
        >
          {refund.original_payment_transaction || "—"}
        </span>
      </Td>

      <Td>
        <input
          type="number"
          min="0.01"
          max={supplierAmount}
          step="0.01"
          value={finalAmount}
          onChange={e =>
            setFinalAmount(e.target.value)
          }
          style={{
            width: 120,
            padding: "7px 9px",
            border: `1px solid ${T.border}`,
            borderRadius: 7,
            background: T.bg,
            color: T.ink,
            fontSize: 12,
            boxSizing: "border-box",
          }}
        />

        <div
          style={{
            fontSize: 10,
            color: T.faint,
            marginTop: 3,
          }}
        >
          Max {fmt(supplierAmount)}
        </div>

        <input
          type="text"
          placeholder="Reason (optional)"
          value={reason}
          onChange={e =>
            setReason(e.target.value)
          }
          style={{
            width: 160,
            marginTop: 6,
            padding: "7px 9px",
            border: `1px solid ${T.border}`,
            borderRadius: 7,
            background: T.bg,
            color: T.ink,
            fontSize: 11,
            boxSizing: "border-box",
          }}
        />
      </Td>

      <Td>
        <Btn
          onClick={handleProcess}
          variant="primary"
          size="sm"
          disabled={processing}
        >
          <CheckCircle size={12} />
          {processing
            ? "Processing..."
            : "Process Refund"}
        </Btn>
      </Td>
    </tr>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   REPORTS SECTION
══════════════════════════════════════════════════════════════════════ */
function ReportsSection() {
  const T = useThemeColors();

  const [report, setReport] = useState("bookings");
  const [service, setService] = useState("all");
  const [status, setStatus] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const [bookings, setBookings] = useState([]);
const [sales, setSales] = useState(null);
const [refunds, setRefunds] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const buildQuery = (includeStatus = false) => {
    const params = new URLSearchParams();

    if (service !== "all") {
      params.set("service", service);
    }

    if (includeStatus && status !== "all") {
      params.set("status", status);
    }

    if (dateFrom) {
      params.set("date_from", dateFrom);
    }

    if (dateTo) {
      params.set("date_to", dateTo);
    }

    const query = params.toString();
    return query ? `?${query}` : "";
  };

  const loadBookings = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const res = await apiFetch(
        `/admin/reports/bookings${buildQuery(true)}`
      );

      if (Array.isArray(res)) {
        setBookings(res);
      } else {
        setBookings([]);
      }
    } catch (err) {
      console.error("Reports booking load failed:", err);
      setBookings([]);
      setError("Failed to load booking report.");
    } finally {
      setLoading(false);
    }
  }, [service, status, dateFrom, dateTo]);

  const loadSales = useCallback(async () => {
  setLoading(true);
  setError("");

  try {
    const res = await apiFetch(
      `/admin/reports/sales${buildQuery(false)}`
    );

    if (res && !Array.isArray(res)) {
      setSales(res);
    } else {
      setSales(null);
    }
  } catch (err) {
    console.error("Reports sales load failed:", err);
    setSales(null);
    setError("Failed to load sales report.");
  } finally {
    setLoading(false);
  }
}, [service, dateFrom, dateTo]);

const loadRefunds = useCallback(async () => {
  setLoading(true);
  setError("");

  try {
    const res = await apiFetch(
  `/admin/reports/refunds${buildQuery(false)}`
);



const refundData = Array.isArray(res)
  ? res
  : Array.isArray(res?.data)
    ? res.data
    : [];



setRefunds(refundData);
  } catch (err) {
    console.error("Refund review load failed:", err);
    setRefunds([]);
    setError("Failed to load refund review.");
  } finally {
    setLoading(false);
  }
}, [service, dateFrom, dateTo]);

  useEffect(() => {
  if (report === "bookings") {
    loadBookings();
  } else if (report === "sales") {
    loadSales();
  } else if (report === "refund") {
    loadRefunds();
  }
}, [
  report,
  loadBookings,
  loadSales,
  loadRefunds,
]);

  const clearFilters = () => {
    setService("all");
    setStatus("all");
    setDateFrom("");
    setDateTo("");
  };

  const exportReport = () => {
    let data = [];
    let headers = [];

    if (report === "bookings") {
      data = bookings;
      headers = [
        "booking_reference",
        "service",
        "customer_name",
        "customer_email",
        "customer_phone",
        "provider",
        "route",
        "travel_date",
        "booking_date",
        "status",
        "total_amount",
        "payment_status",
        "payment_method",
        "payment_id",
      ];
    } else if (report === "sales") {
      data = sales?.data || [];
      headers = [
        "service",
        "booking_count",
        "total_sales",
      ];
    }

    if (!data.length) {
      alert("No report data available to export.");
      return;
    }

    const rows = data.map(row =>
      headers
        .map(header =>
          `"${String(row?.[header] ?? "").replace(/"/g, '""')}"`
        )
        .join(",")
    );

    const csv = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `${report}_report.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    URL.revokeObjectURL(url);
  };

  const totalBookings = bookings.length;

  const completedBookings = bookings.filter(
    b => String(b.status || "").toLowerCase() === "completed"
  ).length;

  const cancelledBookings = bookings.filter(
    b => String(b.status || "").toLowerCase() === "cancelled"
  ).length;

  const totalBookingAmount = bookings.reduce(
    (sum, b) => sum + Number(b.total_amount || 0),
    0
  );

  const salesSummary = sales?.summary || {
    booking_count: 0,
    total_sales: 0,
  };

  const salesRows = sales?.data || [];

  const refundCount = refunds.length;

const pendingRefundAmount = refunds.reduce(
  (sum, r) =>
    sum + Number(r.supplier_refund_amount || 0),
  0
);

  const serviceLabel = service === "all"
    ? "All Services"
    : service.charAt(0).toUpperCase() + service.slice(1);

  return (
    <div>
      {/* Report Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        {[
          {
            id: "bookings",
            label: "Booking Report",
            icon: <BookOpen size={16} />,
            color: T.accent,
          },
          {
            id: "sales",
            label: "Sales Report",
            icon: <TrendingUp size={16} />,
            color: T.green,
          },
          {
            id: "cancellation",
            label: "Cancellation Report",
            icon: <Ban size={16} />,
            color: T.danger,
          },
          {
            id: "refund",
            label: "Refund Report",
            icon: <RefreshCw size={16} />,
            color: T.warn,
          },
          {
            id: "gst",
            label: "GST Report",
            icon: <FileText size={16} />,
            color: T.teal,
          },
        ].map(r => (
          <button
            key={r.id}
            onClick={() => setReport(r.id)}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "10px 16px",
              borderRadius: 10,
              border: `1.5px solid ${
                report === r.id ? r.color : T.border
              }`,
              background:
                report === r.id ? r.color + "14" : T.card,
              color:
                report === r.id ? r.color : T.muted,
              fontWeight: 500,
              fontSize: 13,
              cursor: "pointer",
              transition: "all .15s",
            }}
          >
            {r.icon}
            {r.label}
          </button>
        ))}
      </div>

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 20,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <Sel
          value={service}
          onChange={setService}
          options={[
            { value: "all", label: "All Services" },
            { value: "flight", label: "Flight" },
            { value: "hotel", label: "Hotel" },
            { value: "bus", label: "Bus" },
          ]}
        />

        <Sel
          value={status}
          onChange={setStatus}
          options={[
            { value: "all", label: "All Status" },
            { value: "upcoming", label: "Upcoming" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
          ]}
        />

        <input
          type="date"
          value={dateFrom}
          onChange={e => setDateFrom(e.target.value)}
          style={{
            padding: "9px 11px",
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            background: T.card,
            color: T.ink,
            fontSize: 12,
          }}
        />

        <input
          type="date"
          value={dateTo}
          onChange={e => setDateTo(e.target.value)}
          style={{
            padding: "9px 11px",
            border: `1px solid ${T.border}`,
            borderRadius: 8,
            background: T.card,
            color: T.ink,
            fontSize: 12,
          }}
        />

        <Btn onClick={clearFilters} variant="ghost">
          <X size={14} />
          Clear
        </Btn>

        <Btn
  onClick={
    report === "sales"
      ? loadSales
      : report === "refund"
        ? loadRefunds
        : loadBookings
  }
  variant="ghost"
>
          <RefreshCw size={14} />
          Refresh
        </Btn>

        <Btn onClick={exportReport} variant="ghost">
          <Download size={14} />
          Export CSV
        </Btn>
      </div>

      {/* Active filter summary */}
      <div
        style={{
          fontSize: 12,
          color: T.faint,
          marginBottom: 16,
        }}
      >
        {serviceLabel}
        {dateFrom || dateTo
          ? ` · ${dateFrom || "Start"} → ${dateTo || "Today"}`
          : " · All Dates"}
      </div>

      {error && (
        <div
          style={{
            background: T.dangerSoft,
            border: `1px solid ${T.danger}55`,
            color: T.danger,
            padding: "10px 14px",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
          }}
        >
          ⚠ {error}
        </div>
      )}

      {loading ? (
        <div
          style={{
            textAlign: "center",
            padding: "60px 0",
            color: T.faint,
          }}
        >
          Loading report…
        </div>
      ) : (
        <>
          {/* Booking report stats */}
          {report === "bookings" && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(150px,1fr))",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <StatCard
                  icon={<BookOpen size={18} />}
                  label="Total Bookings"
                  value={totalBookings}
                  color={T.accent}
                />

                <StatCard
                  icon={<CheckCircle size={18} />}
                  label="Completed"
                  value={completedBookings}
                  color={T.green}
                />

                <StatCard
                  icon={<Ban size={18} />}
                  label="Cancelled"
                  value={cancelledBookings}
                  color={T.danger}
                />

                <StatCard
                  icon={<IndianRupee size={18} />}
                  label="Booking Value"
                  value={fmt(totalBookingAmount)}
                  color={T.teal}
                />
              </div>

              <TableWrap>
                <thead>
                  <tr>
                    <Th>Ref</Th>
                    <Th>Service</Th>
                    <Th>Customer</Th>
                    <Th>Route / Provider</Th>
                    <Th>Travel Date</Th>
                    <Th>Amount</Th>
                    <Th>Status</Th>
                    <Th>Payment</Th>
                    <Th>Booked</Th>
                  </tr>
                </thead>

                <tbody>
                  {bookings.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          padding: 40,
                          color: T.faint,
                        }}
                      >
                        No bookings found.
                      </td>
                    </tr>
                  ) : (
                    bookings.map((b, i) => (
                      <TRow key={`${b.service}-${b.id}-${i}`}>
                        <Td
                          style={{
                            fontFamily: "monospace",
                            fontSize: 12,
                            color: T.muted,
                          }}
                        >
                          {b.booking_reference || "—"}
                        </Td>

                        <Td>
                          <span
                            style={{
                              background: T.accentSoft,
                              color: T.accent,
                              fontSize: 11,
                              fontWeight: 500,
                              padding: "3px 9px",
                              borderRadius: 20,
                              textTransform: "capitalize",
                            }}
                          >
                            {b.service}
                          </span>
                        </Td>

                        <Td>
                          <div
                            style={{
                              fontWeight: 500,
                              color: T.ink,
                            }}
                          >
                            {b.customer_name || "—"}
                          </div>

                          <div
                            style={{
                              fontSize: 11,
                              color: T.faint,
                            }}
                          >
                            {b.customer_email || ""}
                          </div>
                        </Td>

                        <Td>
                          <div
                            style={{
                              fontSize: 12,
                              color: T.ink,
                            }}
                          >
                            {b.route || "—"}
                          </div>

                          <div
                            style={{
                              fontSize: 11,
                              color: T.faint,
                            }}
                          >
                            {b.provider || ""}
                          </div>
                        </Td>

                        <Td
                          style={{
                            fontSize: 12,
                            color: T.faint,
                          }}
                        >
                          {fmtDate(b.travel_date)}
                        </Td>

                        <Td
                          style={{
                            fontWeight: 600,
                          }}
                        >
                          {fmt(b.total_amount)}
                        </Td>

                        <Td>
                          <Badge status={b.status} />
                        </Td>

                        <Td>
                          <div
                            style={{
                              fontSize: 11,
                              color: T.muted,
                            }}
                          >
                            {b.payment_status || "—"}
                          </div>

                          <div
                            style={{
                              fontSize: 10,
                              color: T.faint,
                            }}
                          >
                            {b.payment_method || ""}
                          </div>
                        </Td>

                        <Td
                          style={{
                            fontSize: 12,
                            color: T.faint,
                          }}
                        >
                          {fmtDate(b.booking_date)}
                        </Td>
                      </TRow>
                    ))
                  )}
                </tbody>
              </TableWrap>
            </>
          )}

          {/* Sales report */}
          {report === "sales" && (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit,minmax(180px,1fr))",
                  gap: 12,
                  marginBottom: 20,
                }}
              >
                <StatCard
                  icon={<BookOpen size={18} />}
                  label="Bookings"
                  value={Number(
                    salesSummary.booking_count || 0
                  )}
                  color={T.accent}
                />

                <StatCard
                  icon={<IndianRupee size={18} />}
                  label="Total Sales"
                  value={fmt(
                    salesSummary.total_sales || 0
                  )}
                  color={T.green}
                />
              </div>

              <TableWrap>
                <thead>
                  <tr>
                    <Th>Service</Th>
                    <Th>Bookings</Th>
                    <Th>Total Sales</Th>
                    <Th>Average Sale</Th>
                  </tr>
                </thead>

                <tbody>
                  {salesRows.length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        style={{
                          textAlign: "center",
                          padding: 40,
                          color: T.faint,
                        }}
                      >
                        No sales data found.
                      </td>
                    </tr>
                  ) : (
                    salesRows.map((row, i) => {
                      const count = Number(
                        row.booking_count || 0
                      );

                      const total = Number(
                        row.total_sales || 0
                      );

                      return (
                        <TRow key={`${row.service}-${i}`}>
                          <Td
                            style={{
                              fontWeight: 500,
                              color: T.ink,
                              textTransform: "capitalize",
                            }}
                          >
                            {row.service}
                          </Td>

                          <Td>{count}</Td>

                          <Td
                            style={{
                              fontWeight: 600,
                              color: T.green,
                            }}
                          >
                            {fmt(total)}
                          </Td>

                          <Td
                            style={{
                              color: T.muted,
                            }}
                          >
                            {count
                              ? fmt(Math.round(total / count))
                              : "—"}
                          </Td>
                        </TRow>
                      );
                    })
                  )}

                  {salesRows.length > 0 && (
                    <tr
                      style={{
                        background: T.accentSoft,
                      }}
                    >
                      <Td
                        style={{
                          fontWeight: 600,
                          color: T.accent,
                        }}
                      >
                        TOTAL
                      </Td>

                      <Td
                        style={{
                          fontWeight: 600,
                        }}
                      >
                        {Number(
                          salesSummary.booking_count || 0
                        )}
                      </Td>

                      <Td
                        style={{
                          fontWeight: 600,
                          color: T.green,
                        }}
                      >
                        {fmt(
                          salesSummary.total_sales || 0
                        )}
                      </Td>

                      <Td>—</Td>
                    </tr>
                  )}
                </tbody>
              </TableWrap>
            </>
          )}

{report === "refund" && (
  <>
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          "repeat(auto-fit,minmax(180px,1fr))",
        gap: 12,
        marginBottom: 20,
      }}
    >
      <StatCard
        icon={<RefreshCw size={18} />}
        label="Pending Refund Reviews"
        value={refundCount}
        color={T.warn}
      />

      <StatCard
        icon={<IndianRupee size={18} />}
        label="Supplier Refund Amount"
        value={fmt(pendingRefundAmount)}
        color={T.green}
      />
    </div>

    <TableWrap>
      <thead>
        <tr>
          <Th>Booking Reference</Th>
          <Th>Service</Th>
          <Th>Customer</Th>
          <Th>Total Paid</Th>
          <Th>Cancellation Breakdown</Th>
          <Th>Supplier Refund</Th>
          <Th>Payment Gateway</Th>
          <Th>Transaction</Th>
          <Th>Final Refund</Th>
          <Th>Action</Th>
        </tr>
      </thead>

      <tbody>
        {refunds.length === 0 ? (
          <tr>
            <td
              colSpan={10}
              style={{
                textAlign: "center",
                padding: 40,
                color: T.faint,
              }}
            >
              No refunds are pending admin review.
            </td>
          </tr>
        ) : (
          refunds.map(refund => (
            <RefundReviewRow
              key={refund.id}
              refund={refund}
              onProcessed={loadRefunds}
            />
          ))
        )}
      </tbody>
    </TableWrap>
  </>
)}

{report === "cancellation" && (
  <div
    style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: 40,
      textAlign: "center",
    }}
  >
    <FileText
      size={30}
      style={{
        color: T.faint,
        marginBottom: 10,
      }}
    />

    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: T.ink,
        marginBottom: 6,
      }}
    >
      Cancellation Report
    </div>

    <div
      style={{
        fontSize: 12,
        color: T.faint,
      }}
    >
      This report will use the cancellation API once connected.
    </div>
  </div>
)}

{report === "gst" && (
  <div
    style={{
      background: T.card,
      border: `1px solid ${T.border}`,
      borderRadius: 12,
      padding: 40,
      textAlign: "center",
    }}
  >
    <FileText
      size={30}
      style={{
        color: T.faint,
        marginBottom: 10,
      }}
    />

    <div
      style={{
        fontSize: 14,
        fontWeight: 600,
        color: T.ink,
        marginBottom: 6,
      }}
    >
      GST Report
    </div>

    <div
      style={{
        fontSize: 12,
        color: T.faint,
      }}
    >
      This report will use the GST API once connected.
    </div>
  </div>
)}
        </>
      )}
    </div>
  );
}
/* ══════════════════════════════════════════════════════════════════════
   USERS SECTION
══════════════════════════════════════════════════════════════════════ */
function UserDetailModal({ user, onClose, onStatusChange }) {
  const T = useThemeColors();
  const [saving, setSaving] = useState(false);
  const handleStatus = async s => { setSaving(true); const res = await apiPatch(`/users/${user.id}/status`, { status: s }); if (res.ok) onStatusChange(user.id, s); setSaving(false); onClose(); };
  const tc = { Gold: "#f59e0b", Silver: "#94a3b8", Member: T.accent };
  const c = tc[user.tier] || T.accent;
  return (
    <Modal onClose={onClose} title={user.name || "User"} subtitle={`ID: ${user.id} · ${user.email}`} maxWidth={620}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 18, padding: "12px 14px", background: T.accentSoft, borderRadius: 10 }}>
        {user.avatar ? <img src={resolveAvatarUrl(user.avatar)} alt={user.name} style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} /> : <Avatar name={user.name} size={52} />}
        <div>
          <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{user.name || "—"}</div>
          <div style={{ fontSize: 13, color: T.muted }}>{user.email}</div>
          <div style={{ display: "flex", gap: 8, marginTop: 5 }}>
            <span style={{ background: c + "18", color: c, fontSize: 11, fontWeight: 500, padding: "2px 9px", borderRadius: 20 }}>★ {user.tier || "Member"}</span>
            <Badge status={user.status || "active"} />
          </div>
        </div>
      </div>
      <DGrid>
        <DRow label="Phone" value={user.phone} /><DRow label="Member Since" value={fmtDate(user.created_at)} />
        <DRow label="Nationality" value={user.nationality} /><DRow label="DOB" value={fmtDate(user.dob)} />
        <DRow label="Passport No." value={user.passport_number} /><DRow label="Passport Exp" value={fmtDate(user.passport_expiry)} />
        <DRow label="Last Login" value={fmtDT(user.last_login)} /><DRow label="Last Active" value={fmtDT(user.last_active)} />
        <DRow label="Address" value={user.address} full />
      </DGrid>
      <SecTitle>Update Status</SecTitle>
      <StatusActions statuses={["active", "inactive", "banned"]} current={user.status || "active"} onSelect={handleStatus} saving={saving} />
    </Modal>
  );
}
function UsersSection({ data, loading, onRefresh, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [tierF, setTierF] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(u => { const q = search.toLowerCase(); return (!q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q) || String(u.id).includes(q)) && (filter === "all" || (u.status || "active") === filter) && (tierF === "all" || (u.tier || "Member") === tierF); });
  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard icon={<Users size={18} />} label="Total Users" value={data.length} color={T.accent} />
        <StatCard icon={<CheckCircle size={18} />} label="Active" value={data.filter(u => (u.status || "active") === "active").length} color={T.green} />
        <StatCard icon={<Star size={18} />} label="Gold Members" value={data.filter(u => u.tier === "Gold").length} color="#f59e0b" />
        <StatCard icon={<Star size={18} />} label="Silver Members" value={data.filter(u => u.tier === "Silver").length} color={T.muted} />
      </div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search name, email, ID…" />
        <Sel value={tierF} onChange={setTierF} options={[{ value: "all", label: "All Tiers" }, { value: "Gold", label: "Gold" }, { value: "Silver", label: "Silver" }, { value: "Member", label: "Member" }]} />
        <Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All Status" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }, { value: "banned", label: "Banned" }]} />
        <span style={{ fontSize: 13, color: T.faint }}>{filtered.length} users</span>
        <Btn onClick={onRefresh} variant="ghost"><RefreshCw size={14} />Refresh</Btn>
      </div>
      {loading ? <div style={{ textAlign: "center", padding: "60px 0", color: T.faint }}>Loading users…</div> : (
        <TableWrap>
          <thead><tr><Th>#</Th><Th>User</Th><Th>Phone</Th><Th>Tier</Th><Th>Status</Th><Th>Joined</Th><Th>Last Login</Th><Th>Nationality</Th><Th>Action</Th></tr></thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: T.faint }}>No users found</td></tr>
              : filtered.map(u => {
                const tc = { Gold: "#f59e0b", Silver: "#94a3b8", Member: T.accent }; const c = tc[u.tier || "Member"] || T.accent; return (
                  <TRow key={u.id} onClick={() => setSelected(u)}>
                    <Td style={{ fontFamily: "monospace", fontSize: 12, color: T.faint }}>#{u.id}</Td>
                    <Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}>{u.avatar ? <img src={u.avatar} alt={u.name} style={{ width: 30, height: 30, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} /> : <Avatar name={u.name} size={30} />}<div><div style={{ fontWeight: 500, color: T.ink }}>{u.name || "—"}</div><div style={{ fontSize: 11, color: T.faint }}>{u.email}</div></div></div></Td>
                    <Td style={{ fontSize: 12, color: T.muted }}>{u.phone || "—"}</Td>
                    <Td><span style={{ background: c + "18", color: c, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 20 }}>{u.tier || "Member"}</span></Td>
                    <Td><Badge status={u.status || "active"} /></Td>
                    <Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(u.created_at)}</Td>
                    <Td style={{ fontSize: 12, color: T.faint }}>{fmtDT(u.last_login)}</Td>
                    <Td style={{ fontSize: 12, color: T.muted }}>{u.nationality || "—"}</Td>
                    <Td><ViewBtn onClick={() => setSelected(u)} /></Td>
                  </TRow>);
              })}
          </tbody>
        </TableWrap>
      )}
      {selected && <UserDetailModal user={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   PACKAGES SECTION
══════════════════════════════════════════════════════════════════════ */
const EMPTY_PKG = {
  title: "",
  location: "",
  category_id: "beach",
  badge: "",
badge_type: "",
  image_url: "",
  duration: "",
  duration_days: "",
  group_size: "2–10",

  // Pricing basis
  pricing_basis: "per_person",

  // Authoritative pricing
base_price: "",
discount_percent: "",
discount_amount: "",
selling_price: "",
gst: "",
other_tax: "",
other_taxes: [],
tcs: "",
service_fee: "",
other_charges: "",
final_price: "",

  // Legacy fields kept only for backward compatibility
  original_price: "",
  discounted_price: "",
  discount: "",

  rating: "4.5",
  reviews: 0,
  description: "",
  is_active: true,

  // Reference Overview fields
  package_code: "",
  currency: "INR",
  route_map_url: "",
  inclusion_display_text: "",
  exclusion_display_text: "",

  booking_start_date: "",
booking_end_date: "",

    // Booking window
  booking_min_advance_days: 0,
booking_max_advance_years: 2,
departure_type: "daily",
  departure_interval_days: "",
  departure_start_date: "",
  departure_dates: [],
};

function PackageFormModal({ pkg, categories, onClose, onSaved }) {
  const T = useThemeColors();
  const inputS = useInputStyle();
  const isEdit = !!pkg;
  const toTL = items => items.map(i => typeof i === "string" ? i : i?.name || "").filter(Boolean);
  const catOpts = (categories?.length ? categories : PKG_CATS).filter(c => (c.value || c.id) !== "all").map(c => ({ value: c.value || c.id, label: c.label }));
  const toDateInputValue = (value) => {
  if (!value) return "";

  const text = String(value);

  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) {
    return text;
  }

  const date = new Date(text);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return date.toISOString().slice(0, 10);
};
  const [form, setForm] = useState(() => {
  if (!isEdit) {
    return { ...EMPTY_PKG };
  }

  return {
  ...EMPTY_PKG,
  ...pkg,

  booking_start_date: toDateInputValue(pkg.booking_start_date),
  booking_end_date: toDateInputValue(pkg.booking_end_date),
  departure_start_date: toDateInputValue(pkg.departure_start_date),

  is_active: pkg.is_active ?? true,

  other_taxes:
    Array.isArray(pkg.other_taxes)
      ? pkg.other_taxes
      : Number(pkg.other_tax) > 0
        ? [{ name: "", amount: Number(pkg.other_tax) }]
        : []
};
});
  const [itinerary, setItin] = useState(() => {
    if (!isEdit) return [];

    // Per-day extras come back as flat { day, name } arrays — merge them
    // back into their day rows so edit mode restores everything.
    const byDay = (rows, day, key) =>
      (rows || []).filter(r => Number(r.day) === day).map(r => r.name);

    return (pkg.itinerary || []).map((item, index) => {
      const day = Number(item.day) || index + 1;
      return {
        day,
        title: item.title || "",
        description: item.description ?? item.desc ?? "",
        city: item.city || "",
        image_url: item.image_url || "",
        activities: byDay(pkg.day_activities, day, "activity"),
        hotels: byDay(pkg.day_hotels, day, "hotel"),
        transfers: byDay(pkg.day_transfers, day, "transfer"),
      };
    });
  });
  const [inclusions, setInc] = useState(isEdit ? toTL(pkg.inclusions || []) : []);
  const [exclusions, setExc] = useState(isEdit ? toTL(pkg.exclusions || []) : []);
  const [highlights, setHigh] = useState(isEdit ? toTL(pkg.highlights || []) : []);

  // Facilities are stored as facility IDs in package_facilities.
  const [facilityCatalog, setFacilityCatalog] = useState([]);
  const [facilityIds, setFacilityIds] = useState(() => {
    if (!isEdit) return [];
    // IDs come straight from the API; name-based fallback happens in the
    // effect below once the facility catalog has loaded.
    return Array.isArray(pkg.facility_ids)
      ? pkg.facility_ids.map(Number).filter(Boolean)
      : [];
  });
  const [catalogError, setCatalogError] = useState("");

  // Tour gallery images (image_url + alt_text).
  const [gallery, setGallery] = useState(() =>
    isEdit && Array.isArray(pkg.gallery)
      ? pkg.gallery.map(img => ({
          title: img?.title || "",
          image_url: img?.image_url || "",
          alt_text: img?.alt_text || "",
        }))
      : []
  );

  // ── Reference-feature state ─────────────────────────────
  const [routeMap, setRouteMap] = useState(
    isEdit && Array.isArray(pkg.route_map) ? pkg.route_map : []
  );
  const [faqs, setFaqs] = useState(
    isEdit && Array.isArray(pkg.faqs) ? pkg.faqs : []
  );
  const [policyLib, setPolicyLib] = useState([]);
  const [incLib, setIncLib] = useState([]);
  const [excLib, setExcLib] = useState([]);
  const [taxLib, setTaxLib] = useState([]);
  const [policyIds, setPolicyIds] = useState(() =>
    isEdit && Array.isArray(pkg.policies) ? pkg.policies.map(p => Number(p.id)) : []
  );
  const [incIds, setIncIds] = useState(() =>
    isEdit && Array.isArray(pkg.inclusion_options) ? pkg.inclusion_options.map(o => Number(o.id)) : []
  );
  const [excIds, setExcIds] = useState(() =>
    isEdit && Array.isArray(pkg.exclusion_options) ? pkg.exclusion_options.map(o => Number(o.id)) : []
  );
  const [taxIds, setTaxIds] = useState(() =>
    isEdit && Array.isArray(pkg.tax_options) ? pkg.tax_options.map(t => Number(t.id)) : []
  );
  const [pricePeriods, setPricePeriods] = useState(() =>
    isEdit && Array.isArray(pkg.price_periods) ? pkg.price_periods : []
  );
  const [schemaPending, setSchemaPending] = useState([]);
  const [newPolicy, setNewPolicy] = useState({ name: "", content: "" });
  // Reference eye (👁) buttons: full-content viewer for library rows.
  const [viewItem, setViewItem] = useState(null);
  const [libBusy, setLibBusy] = useState(false);
  const toggleId = (setter) => (id) =>
    setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  // Create a reusable policy so the reference's "+ policy" flow works
  // end-to-end (POST /api/package-libraries/policies).
  const addPolicy = async () => {
    if (libBusy) return;
    const name = String(newPolicy.name || "").trim();
    if (!name) { setError("Policy name is required."); return; }
    setLibBusy(true);
    const res = await apiPost("/package-libraries/policies", { name, content: newPolicy.content || "" });
    setLibBusy(false);
    if (res.ok) {
      setPolicyLib(Array.isArray(res.data) ? res.data : []);
      setNewPolicy({ name: "", content: "" });
      setError(null);
    } else {
      setError(res.error || "Could not save the policy.");
    }
  };

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setTab] = useState("basic");
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    let mounted = true;

    (async () => {
      const catalog = await apiFetch("/facilities");
      if (!mounted) return;

      if (Array.isArray(catalog) && catalog.length) {
        setFacilityCatalog(catalog);

        // Resolve name-based selections once the catalog is available.
        setFacilityIds(prev => {
          if (prev.length || !isEdit) return prev;
          const names = toTL(pkg.facilities || []);
          if (!names.length) return prev;
          const resolved = names
            .map(name => catalog.find(f => f.name === name)?.id)
            .filter(Boolean);
          return resolved.length ? resolved : prev;
        });
      } else {
        setCatalogError("Facilities list could not be loaded. Save the package, then reload to assign facilities.");
      }

      // Reference tabs: reusable policy / inclusion / exclusion / tax
      // libraries plus the migration status banner. All are public reads.
      const [status, policies, incOptions, excOptions, taxOptions] = await Promise.all([
        apiFetch("/packages/schema-status"),
        apiFetch("/package-libraries/policies"),
        apiFetch("/package-libraries/inclusion-options"),
        apiFetch("/package-libraries/exclusion-options"),
        apiFetch("/package-libraries/tax-options"),
      ]);

      if (!mounted) return;

      if (status && Array.isArray(status.tables)) {
        setSchemaPending([]);
      } else if (status && status.tables) {
        setSchemaPending(
          Object.entries(status.tables)
            .filter(([, present]) => !present)
            .map(([name]) => name)
        );
      }

      if (Array.isArray(policies)) setPolicyLib(policies);
      if (Array.isArray(incOptions)) setIncLib(incOptions);
      if (Array.isArray(excOptions)) setExcLib(excOptions);
      if (Array.isArray(taxOptions)) setTaxLib(taxOptions);
    })();

    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

const basePrice = Number(form.base_price) || 0;

const discountPercent = Math.min(
  Math.max(Number(form.discount_percent) || 0, 0),
  100
);

const calculatedDiscountAmount = Number(
  ((basePrice * discountPercent) / 100).toFixed(2)
);

const calculatedSellingPrice = Number(
  Math.max(basePrice - calculatedDiscountAmount, 0).toFixed(2)
);

const gst = Number(form.gst) || 0;

const otherTax = (form.other_taxes || []).reduce(
  (sum, tax) => sum + (Number(tax.amount) || 0),
  0
);


const serviceFee = Number(form.service_fee) || 0;

const calculatedFinalPrice = Number(
  (
    calculatedSellingPrice +
    gst +
    otherTax +
    serviceFee
  ).toFixed(2)
);

const handleSave = async () => {
  if (saving) return; // ignore double-clicks / repeated submits

  if (!form.title.trim()) {
    setError("Package title is required.");
    return;
  }

  if (form.title.trim().length > 120) {
    setError("Package title must be 120 characters or fewer.");
    return;
  }

  if (form.location && form.location.trim().length > 120) {
    setError("Location must be 120 characters or fewer.");
    return;
  }

  if (form.badge && form.badge.trim().length > 40) {
    setError("Badge must be 40 characters or fewer.");
    return;
  }

  if (!form.location?.trim()) {
    setError("Location is required.");
    return;
  }

  if (!form.duration?.trim()) {
    setError("Duration is required.");
    return;
  }

  if (!form.image_url?.trim()) {
    setError("Package image is required.");
    return;
  }

  if (basePrice <= 0) {
    setError("Base price must be greater than ₹0.");
    return;
  }

  if (discountPercent < 0 || discountPercent > 100) {
    setError("Discount must be between 0% and 100%.");
    return;
  }

if (form.departure_type === "recurring") {
  if (!Number(form.departure_interval_days) || Number(form.departure_interval_days) < 1) {
    setError("Recurring departure interval must be at least 1 day.");
    return;
  }

  if (!form.departure_start_date) {
    setError("First departure date is required for recurring departures.");
    return;
  }
}

if (form.departure_type === "fixed") {
  const fixedDates = (form.departure_dates || []).filter(Boolean);

  if (!fixedDates.length) {
    setError("Add at least one departure date.");
    return;
  }

  if (new Set(fixedDates).size !== fixedDates.length) {
    setError("Duplicate departure dates are not allowed.");
    return;
  }
}

  // ── Reference-tab validation ────────────────────────────────
  if ((Number(form.duration_days) || 0) > 99) {
    setError("Duration (days) must be 99 or fewer (reference limit: Max 99 Days).");
    return;
  }

  const cleanedRouteMap = routeMap
    .map(row => ({
      city_name: String(row.city_name || "").trim(),
      country_name: String(row.country_name || "").trim(),
      state_name: String(row.state_name || "").trim(),
      no_of_days: Number(row.no_of_days) || 0,
    }))
    .filter(row => row.city_name);

  if (routeMap.some(row => !String(row.city_name || "").trim())) {
    setError("Every route-map row needs a city name — remove empty rows.");
    return;
  }

  if (routeMap.some(row => Number(row.no_of_days) <= 0)) {
    setError("Route-map rows need more than 0 days.");
    return;
  }

  const cleanedFaqs = faqs
    .map(f => ({ question: String(f.question || "").trim(), answer: f.answer || "" }))
    .filter(f => f.question || f.answer);

  if (cleanedFaqs.some(f => !f.question)) {
    setError("Every FAQ needs a question — remove incomplete rows.");
    return;
  }

  const cleanedPeriods = pricePeriods.filter(p => p && (p.from_date || p.to_date));

  for (const [index, period] of cleanedPeriods.entries()) {
    if (!period.from_date || !period.to_date) {
      setError(`Price period ${index + 1}: both start and end dates are required.`);
      return;
    }
    if (period.from_date > period.to_date) {
      setError(`Price period ${index + 1}: start date must be on or before the end date.`);
      return;
    }
    const rows = (period.rows || []).filter(r => String(r.hotel_type || "").trim());
    if (!rows.length) {
      setError(`Price period ${index + 1}: add at least one hotel-type row.`);
      return;
    }
    if (rows.some(r => ["pax_1","pax_2","pax_4","pax_6","pax_8","extra_adult","extra_child","extra_infant"].some(k => Number(r[k]) < 0))) {
      setError(`Price period ${index + 1}: prices cannot be negative.`);
      return;
    }
  }

  const sortedPeriods = [...cleanedPeriods].sort((a, b) => (a.from_date < b.from_date ? -1 : 1));
  for (let i = 1; i < sortedPeriods.length; i += 1) {
    if (sortedPeriods[i].from_date <= sortedPeriods[i - 1].to_date) {
      setError(`Price periods overlap: ${sortedPeriods[i - 1].from_date}–${sortedPeriods[i - 1].to_date} and ${sortedPeriods[i].from_date}–${sortedPeriods[i].to_date}.`);
      return;
    }
  }

  setSaving(true);
  setError(null);

  const payload = {
    ...form,

    // Reference Overview fields
    package_code: form.package_code ? String(form.package_code).trim() : null,
    currency: form.currency || "INR",
    route_map_url: String(form.route_map_url || "").trim() || null,
    inclusion_display_text: form.inclusion_display_text || null,
    exclusion_display_text: form.exclusion_display_text || null,

    // Reference-feature relation payloads
    route_map: cleanedRouteMap,
    faqs: cleanedFaqs,
    policy_ids: policyIds,
    inclusion_option_ids: incIds,
    exclusion_option_ids: excIds,
    tax_option_ids: taxIds,
    price_periods: cleanedPeriods.map(p => ({
      from_date: p.from_date,
      to_date: p.to_date,
      rows: (p.rows || [])
        .map(r => ({
          hotel_type: String(r.hotel_type || "").trim(),
          pax_1: Number(r.pax_1) || 0,
          pax_2: Number(r.pax_2) || 0,
          pax_4: Number(r.pax_4) || 0,
          pax_6: Number(r.pax_6) || 0,
          pax_8: Number(r.pax_8) || 0,
          extra_adult: Number(r.extra_adult) || 0,
          extra_child: Number(r.extra_child) || 0,
          extra_infant: Number(r.extra_infant) || 0,
        }))
        .filter(r => r.hotel_type),
    })),

    tcs: Math.max(Number(form.tcs) || 0, 0),
    other_charges: Math.max(Number(form.other_charges) || 0, 0),

    duration_days: Number(form.duration_days) || 0,

    pricing_basis: form.pricing_basis || "per_person",

    booking_min_advance_days: Math.max(
  Number(form.booking_min_advance_days) || 0,
  0
),

booking_max_advance_years: Math.max(
  Number(form.booking_max_advance_years) || 1,
  1
),

    // Departure availability
    departure_type: form.departure_type || "daily",

    departure_interval_days:
      form.departure_type === "recurring"
        ? Math.max(Number(form.departure_interval_days) || 0, 1)
        : null,

    departure_start_date:
      form.departure_type === "recurring"
        ? form.departure_start_date || null
        : null,

    departure_dates:
      form.departure_type === "fixed"
        ? (Array.isArray(form.departure_dates)
            ? form.departure_dates.filter(Boolean)
            : [])
        : [],

    // Admin-entered pricing inputs
base_price: basePrice,
discount_percent: discountPercent,
gst,

other_taxes: (form.other_taxes || [])
  .map(tax => ({
    name: String(tax.name || "").trim(),
    amount: Math.max(Number(tax.amount) || 0, 0)
  }))
  .filter(tax => tax.name || tax.amount > 0),

// Total of all Other Tax rows
other_tax: otherTax,

service_fee: serviceFee,

    // Derived values sent for display/backward compatibility.
    // Backend recalculates these authoritatively.
    discount_amount: calculatedDiscountAmount,
    selling_price: calculatedSellingPrice,
    final_price: calculatedFinalPrice,

    // Keep legacy columns synchronized.
    original_price: basePrice,
    discounted_price: calculatedSellingPrice,
    discount: discountPercent,

    rating: Number(form.rating) || 4.5,
    is_active: form.is_active ? 1 : 0,

    itinerary: itinerary.map((r, i) => ({
      day: Number(r.day) || i + 1,
      title: r.title?.trim() || "",
      description: r.description || "",
      city: String(r.city || "").trim(),
      image_url: String(r.image_url || "").trim(),
      activities: (r.activities || []).map(x => String(x).trim()).filter(Boolean),
      hotels: (r.hotels || []).map(x => String(x).trim()).filter(Boolean),
      transfers: (r.transfers || []).map(x => String(x).trim()).filter(Boolean),
      sort_order: i + 1
    })),

    inclusions,
    exclusions,
    highlights,

    // Facilities are stored by ID in package_facilities.
    facility_ids: facilityIds.map(Number).filter(Boolean),

    gallery: gallery
      .map(img => ({
        title: String(img.title || "").trim(),
        image_url: String(img.image_url || "").trim(),
        alt_text: String(img.alt_text || "").trim(),
      }))
      .filter(img => img.image_url)
  };

  const res = isEdit
    ? await apiPut(`/packages/${pkg.id}`, payload)
    : await apiPost("/packages", payload);

  if (res.ok) {
    onSaved(res.data);
    onClose();
  } else {
    setError(res.error || "Save failed.");
  }

  setSaving(false);
};
  const tabs = [
  { id: "basic", label: "Overview" },
  { id: "itinerary", label: "Itinerary" },
  { id: "gallery", label: "Tour Gallery" },
  { id: "policy", label: "Package Policy" },
  { id: "inclusions", label: "Inclusions" },
  { id: "pricing", label: "Pricing" },
  { id: "facilities", label: "Facilities" },
  { id: "details", label: "Details" },
  { id: "availability", label: "Availability" }
];
  const CHIP_ROW = { display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 6 };
  const chipStyle = (active) => ({
    padding: "5px 10px", borderRadius: 999, cursor: "pointer", fontSize: 11, fontWeight: 600,
    border: `1px solid ${active ? T.accent : T.border}`,
    background: active ? T.accentSoft : T.card,
    color: active ? T.accent : T.muted,
  });
  const smallBtn = {
    padding: "6px 10px", border: `1px solid ${T.border}`, borderRadius: 7,
    background: T.card, color: T.accent, fontSize: 11, fontWeight: 600, cursor: "pointer",
    display: "inline-flex", alignItems: "center", gap: 5,
  };
  const listEditor = (items, setItems, ph) => (
    <div>{items.map((item, i) => <div key={i} style={{ display: "flex", gap: 8, marginBottom: 6 }}><input value={item} onChange={e => setItems(v => v.map((x, idx) => idx === i ? e.target.value : x))} placeholder={ph} style={{ ...inputS, flex: 1 }} /><button onClick={() => setItems(v => v.filter((_, idx) => idx !== i))} style={{ padding: "6px 10px", border: `1px solid ${T.danger}55`, background: T.dangerSoft, borderRadius: 6, cursor: "pointer", color: T.danger, fontWeight: 500 }}>✕</button></div>)}
      <button onClick={() => setItems(v => [...v, ""])} style={{ padding: "7px", border: `1px dashed ${T.border}`, borderRadius: 8, background: T.bg, color: T.muted, cursor: "pointer", fontSize: 12, width: "100%", marginTop: 4 }}>+ Add Item</button></div>
  );

  // Per-day activities / hotels / transfers chips (reference Itinerary tab).
  const dayChipEditor = (dayIndex, key, ph) => {
    const values = itinerary[dayIndex]?.[key] || [];
    const update = (next) =>
      setItin(prev => prev.map((r, idx) => idx === dayIndex ? { ...r, [key]: next } : r));

    return (
      <div>
        <div style={CHIP_ROW}>
          {values.map((value, k) => (
            <span key={k} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "4px 8px", borderRadius: 999, background: T.card, border: `1px solid ${T.border}`, fontSize: 11, color: T.ink }}>
              {value}
              <button type="button" aria-label={`Remove ${key} ${k + 1}`} onClick={() => update(values.filter((_, idx) => idx !== k))} style={{ border: "none", background: "none", color: T.danger, cursor: "pointer", fontSize: 12, lineHeight: 1, padding: 0 }}>✕</button>
            </span>
          ))}
        </div>
        <ChipAdder onAdd={name => update([...values, name])} placeholder={ph} />
      </div>
    );
  };
  return (
    <Modal onClose={onClose} title={isEdit ? `Edit Package #${pkg.id}` : "Add New Package"} subtitle={isEdit ? pkg.title : "New holiday package"} maxWidth={920}>
      <div className="pkg-form-tabs" style={{ display: "flex", gap: 2, marginBottom: 18, borderBottom: `1px solid ${T.border}`, overflowX: "auto", flexShrink: 0 }}>
        {tabs.map(t => <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: "8px 16px", border: "none", borderBottom: `2px solid ${activeTab === t.id ? T.accent : "transparent"}`, background: "none", fontWeight: 500, fontSize: 13, whiteSpace: "nowrap", color: activeTab === t.id ? T.accent : T.muted, cursor: "pointer" }}>{t.label}</button>)}
      </div>
      {schemaPending.length > 0 && (
        <div style={{ background: T.warnSoft, border: `1px solid ${T.warn}55`, color: T.warn, padding: "9px 12px", borderRadius: 8, fontSize: 12, marginBottom: 14 }}>
          ⚠ Reference features pending database migration ({schemaPending.length} table(s) missing). Run <b>docs/migration-holiday-packages-reference-features.sql</b> in phpMyAdmin, then restart the backend — until then route map, policies, library selections and price periods save but are not stored.
        </div>
      )}
      {activeTab === "basic" && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <div style={{ gridColumn: "1/-1" }}><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Package Title *</label><input value={form.title} onChange={e => set("title", e.target.value)} style={inputS} placeholder="Bali Bliss Escape" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Location *</label><input value={form.location} onChange={e => set("location", e.target.value)} style={inputS} placeholder="Bali, Indonesia" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Category</label><select value={form.category_id} onChange={e => set("category_id", e.target.value)} style={inputS}>{catOpts.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}</select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Duration *</label><input value={form.duration} onChange={e => set("duration", e.target.value)} style={inputS} placeholder="6 Days / 5 Nights" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Duration (days, max 99)</label><input type="number" min="1" max="99" value={form.duration_days} onChange={e => set("duration_days", e.target.value)} style={inputS} placeholder="6" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Group Size</label><input value={form.group_size} onChange={e => set("group_size", e.target.value)} style={inputS} placeholder="2–10" /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Badge Label</label><input value={form.badge} onChange={e => set("badge", e.target.value)} style={inputS} placeholder="e.g. Bestseller" maxLength={40} /></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Badge Type</label><select value={form.badge_type} onChange={e => set("badge_type", e.target.value)} style={inputS}><option value="">None</option><option value="bestseller">Bestseller</option><option value="new">New</option><option value="trending">Trending</option><option value="discount">Discount</option></select></div>
        <div><label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Rating</label><input type="number" step="0.1" min="0" max="5" value={form.rating} onChange={e => set("rating", e.target.value)} style={inputS} /></div>
        <div>
          <label style={{ fontSize: 11, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 }}>Package Code</label>
          <input
            value={form.package_code || ""}
            onChange={e => set("package_code", e.target.value)}
            disabled={isEdit && !!pkg.package_code}
            style={{ ...inputS, background: T.bg, color: isEdit && pkg.package_code ? T.faint : T.ink }}
            placeholder="MAK0000018 — leave blank to auto-generate"
            maxLength={20}
          />
          <div style={{ fontSize: 10, color: T.faint, marginTop: 3 }}>
            {isEdit && pkg.package_code ? "Read-only — assigned when the package was created." : "Blank = generated automatically on save (MAK + id)."}
          </div>
        </div>
        <div style={{ gridColumn: "1/-1" }}>
          <ImageField
            label="Main Package Image *"
            value={form.image_url}
            onChange={v => set("image_url", v)}
            hint="Shown as the package card / hero image. Upload a file or paste a URL."
          />
        </div>
        <div style={{ gridColumn: "1/-1", display: "flex", alignItems: "center", gap: 10 }}><label style={{ fontSize: 13, fontWeight: 400, cursor: "pointer", display: "flex", alignItems: "center", gap: 8 }}><input type="checkbox" checked={!!form.is_active} onChange={e => set("is_active", e.target.checked)} style={{ width: 16, height: 16, accentColor: T.green }} />Active (visible to customers)</label></div>

        <div style={{ gridColumn: "1/-1" }}>
          <SecTitle>Route Map</SecTitle>
          <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
            Cities the tour moves through, with how many days are spent in each. Shown on the package details page and the PDF.
          </div>

          <div className="pkg-route-head" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 90px auto", gap: 8, fontSize: 10, fontWeight: 700, color: T.faint, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 4 }}>
            <span>City *</span><span>State</span><span>Country</span><span>Days</span><span />
          </div>

          {routeMap.map((row, index) => (
            <div key={index} className="pkg-route-row" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr 1fr 90px auto", gap: 8, marginBottom: 6 }}>
              <CitySuggestInput value={row.city_name || ""} onChange={v => setRouteMap(prev => prev.map((r, i) => i === index ? { ...r, city_name: v } : r))} style={{ ...inputS, padding: "7px 9px" }} placeholder="Goa — type to search" />
              <input value={row.state_name || ""} onChange={e => setRouteMap(prev => prev.map((r, i) => i === index ? { ...r, state_name: e.target.value } : r))} style={{ ...inputS, padding: "7px 9px" }} placeholder="Goa" />
              <input value={row.country_name || ""} onChange={e => setRouteMap(prev => prev.map((r, i) => i === index ? { ...r, country_name: e.target.value } : r))} style={{ ...inputS, padding: "7px 9px" }} placeholder="India" />
              <input type="number" min="0" step="0.5" value={row.no_of_days ?? ""} onChange={e => setRouteMap(prev => prev.map((r, i) => i === index ? { ...r, no_of_days: e.target.value } : r))} style={{ ...inputS, padding: "7px 9px" }} placeholder="2" />
              <button type="button" aria-label={`Remove route city ${index + 1}`} onClick={() => setRouteMap(prev => prev.filter((_, i) => i !== index))} style={{ border: `1px solid ${T.danger}55`, background: T.dangerSoft, color: T.danger, borderRadius: 7, cursor: "pointer", padding: "0 10px" }}>✕</button>
            </div>
          ))}

          {!routeMap.length && <div style={{ color: T.faint, fontSize: 12, padding: "4px 0 8px" }}>No route cities yet.</div>}

          <button type="button" onClick={() => setRouteMap(prev => [...prev, { city_name: "", state_name: "", country_name: "", no_of_days: 1 }])} style={{ ...smallBtn, borderStyle: "dashed", width: "100%", justifyContent: "center", marginBottom: 12 }}>
            <Plus size={13} /> Add Route City
          </button>

          <ImageField
            label="Route Map Image (optional)"
            value={form.route_map_url}
            onChange={v => set("route_map_url", v)}
            hint="An illustrative map image shown with the route table."
          />
        </div>
      </div>}
      {activeTab === "pricing" && (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>

    <div style={{ gridColumn: "1/-1" }}>
      <SecTitle>Currency</SecTitle>
      <select
        value={form.currency || "INR"}
        onChange={e => set("currency", e.target.value)}
        style={inputS}
      >
        {SUPPORTED_CURRENCIES.map(code => <option key={code} value={code}>{code}</option>)}
      </select>
      <div style={{ marginTop: 4, fontSize: 11, color: T.faint }}>
        Stored with the package and shown on the details page, the booking summary and the PDF. Booking charges remain settled in the payment gateway's currency.
      </div>
    </div>

    <div style={{ gridColumn: "1/-1" }}>
      <SecTitle>Pricing Basis</SecTitle>

      <select
        value={form.pricing_basis || "per_person"}
        onChange={e => set("pricing_basis", e.target.value)}
        style={inputS}
      >
        <option value="per_person">Per Person</option>
        <option value="per_room">Per Room</option>
        <option value="per_booking">Per Booking</option>
      </select>
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Base Price (₹) *
      </label>

      <input
        type="number"
        min="0"
        step="0.01"
        value={form.base_price}
        onChange={e => set("base_price", e.target.value)}
        style={inputS}
        placeholder="25000"
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Discount (%)
      </label>

      <input
        type="number"
        min="0"
        max="100"
        step="0.01"
        value={form.discount_percent}
        onChange={e => set("discount_percent", e.target.value)}
        style={inputS}
        placeholder="10"
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Discount Amount (₹)
      </label>

      <input
        type="number"
        value={calculatedDiscountAmount}
        readOnly
        style={{
          ...inputS,
          background: T.bg,
          color: T.muted
        }}
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Selling Price (₹)
      </label>

      <input
        type="number"
        value={calculatedSellingPrice}
        readOnly
        style={{
          ...inputS,
          background: T.greenSoft,
          color: T.green,
          fontWeight: 600
        }}
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        GST (₹)
      </label>

      <input
        type="number"
        min="0"
        step="0.01"
        value={form.gst}
        onChange={e => set("gst", e.target.value)}
        style={inputS}
        placeholder="0"
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Service / Convenience Fee (₹)
      </label>

      <input
        type="number"
        min="0"
        step="0.01"
        value={form.service_fee}
        onChange={e => set("service_fee", e.target.value)}
        style={inputS}
        placeholder="0"
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4,
        }}
      >
        TCS (₹)
      </label>

      <input
        type="number"
        min="0"
        step="0.01"
        value={form.tcs}
        onChange={e => set("tcs", e.target.value)}
        style={inputS}
        placeholder="0"
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4,
        }}
      >
        Other Charges (₹)
      </label>

      <input
        type="number"
        min="0"
        step="0.01"
        value={form.other_charges}
        onChange={e => set("other_charges", e.target.value)}
        style={inputS}
        placeholder="0"
      />
    </div>

    <div style={{ gridColumn: "1/-1" }}>
  <div
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      marginBottom: 8
    }}
  >
    <label
      style={{
        fontSize: 11,
        fontWeight: 600,
        color: T.muted
      }}
    >
      Other Taxes
    </label>

    <button
      type="button"
      onClick={() =>
        set("other_taxes", [
          ...(form.other_taxes || []),
          { name: "", amount: "" }
        ])
      }
      style={{
        border: `1px solid ${T.border}`,
        background: T.bg,
        color: T.accent,
        borderRadius: 7,
        padding: "5px 9px",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        fontSize: 11,
        fontWeight: 600
      }}
    >
      <Plus size={13} /> Add Other Tax
    </button>
  </div>

  {(form.other_taxes || []).map((tax, index) => (
    <div
      key={index}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
        gap: 8,
        marginBottom: 8,
        padding: 10,
        borderRadius: 8,
        background: T.bg,
        border: `1px solid ${T.border}`
      }}
    >
      <input
        value={tax.name || ""}
        onChange={e =>
          set(
            "other_taxes",
            (form.other_taxes || []).map((item, i) =>
              i === index
                ? { ...item, name: e.target.value }
                : item
            )
          )
        }
        style={inputS}
        placeholder="Tax Name"
      />

      <input
        type="number"
        min="0"
        step="0.01"
        value={tax.amount ?? ""}
        onChange={e =>
          set(
            "other_taxes",
            (form.other_taxes || []).map((item, i) =>
              i === index
                ? { ...item, amount: e.target.value }
                : item
            )
          )
        }
        style={inputS}
        placeholder="Tax Amount (₹)"
      />

      <button
        type="button"
        onClick={() =>
          set(
            "other_taxes",
            (form.other_taxes || []).filter(
              (_, i) => i !== index
            )
          )
        }
        style={{
          border: `1px solid ${T.danger}55`,
          background: T.dangerSoft,
          color: T.danger,
          borderRadius: 8,
          cursor: "pointer",
          padding: "0 11px"
        }}
      >
        ×
      </button>
    </div>
  ))}

  {(form.other_taxes || []).length > 0 && (
    <div
      style={{
        textAlign: "right",
        fontSize: 11,
        color: T.muted,
        marginTop: 4
      }}
    >
      Total Other Tax: <b style={{ color: T.ink }}>₹{otherTax.toFixed(2)}</b>
    </div>
  )}
</div>

    

    <div style={{ gridColumn: "1/-1" }}>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Final Price (₹)
      </label>

      <input
        type="number"
        value={calculatedFinalPrice}
        readOnly
        style={{
          ...inputS,
          background: T.accentSoft,
          color: T.accent,
          fontWeight: 700,
          fontSize: 16
        }}
      />

      <div
        style={{
          marginTop: 8,
          padding: "9px 12px",
          borderRadius: 8,
          background: T.bg,
          color: T.faint,
          fontSize: 11
        }}
      >
        Final Price = Selling Price + GST + Service Fee + Other Taxes
      </div>
    </div>

    <div style={{ gridColumn: "1/-1" }}>
      <SecTitle>Tax Library (Select Tax)</SecTitle>
      <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
        Tick the taxes that apply to this package (stored for display and the PDF). Use <b>+ Add as Other Tax</b> to fold a percentage into the priced Other Taxes total.
      </div>

      {!taxLib.length && (
        <div style={{ color: T.faint, fontSize: 13, marginBottom: 8 }}>
          {schemaPending.length ? "Tax library unavailable until the migration is run." : "No tax options in the library yet."}
        </div>
      )}

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {taxLib.map(tax => {
          const checked = taxIds.includes(Number(tax.id));
          const amount = Number((calculatedSellingPrice * (Number(tax.percentage) || 0)) / 100) || 0;
          return (
            <div key={tax.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "6px 10px", borderRadius: 8, border: `1px solid ${checked ? T.accent : T.border}`, background: checked ? T.accentSoft : T.bg }}>
              <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", fontSize: 12, color: T.ink, fontWeight: 600 }}>
                <input type="checkbox" checked={checked} onChange={() => toggleId(setTaxIds)(Number(tax.id))} style={{ width: 14, height: 14, accentColor: T.accent }} />
                {tax.name} · {Number(tax.percentage) || 0}%
              </label>
              <button
                type="button"
                title={`Add ${tax.name} (${amount.toFixed(2)}) to Other Taxes`}
                onClick={() => {
                  if (amount <= 0) return;
                  set("other_taxes", [...(form.other_taxes || []), { name: tax.name, amount: Number(amount.toFixed(2)) }]);
                  toggleId(setTaxIds)(Number(tax.id));
                }}
                style={{ border: "none", background: "none", color: T.green, fontWeight: 800, cursor: amount <= 0 ? "not-allowed" : "pointer", fontSize: 13, padding: 0, opacity: amount <= 0 ? 0.35 : 1 }}
                aria-label={`Add ${tax.name} as other tax`}
              >+
              </button>
            </div>
          );
        })}
      </div>
    </div>

    <div style={{ gridColumn: "1/-1" }}>
      <SecTitle>Date-Range Price Grid</SecTitle>
      <div style={{ fontSize: 11, color: T.faint, marginBottom: 10 }}>
        Seasonal rates by travel window. Each period lists per-hotel-type prices by occupancy (1/2/4/6/8 pax) plus extra adult, child and infant. Shown to customers as the seasonal price table; periods must not overlap.
      </div>

      {pricePeriods.map((period, pIndex) => (
        <div key={pIndex} style={{ padding: 12, borderRadius: 10, background: T.bg, border: `1px solid ${T.border}`, marginBottom: 12 }}>
          <div className="pkg-period-head" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8, alignItems: "end" }}>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: T.muted, display: "block", marginBottom: 3 }}>From *</label>
              <input type="date" value={period.from_date || ""} onChange={e => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, from_date: e.target.value } : p))} style={{ ...inputS, padding: "7px 9px" }} />
            </div>
            <div>
              <label style={{ fontSize: 10, fontWeight: 700, color: T.muted, display: "block", marginBottom: 3 }}>To *</label>
              <input type="date" value={period.to_date || ""} onChange={e => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, to_date: e.target.value } : p))} style={{ ...inputS, padding: "7px 9px" }} />
            </div>
            <button type="button" aria-label={`Remove price period ${pIndex + 1}`} onClick={() => setPricePeriods(prev => prev.filter((_, i) => i !== pIndex))} style={{ border: `1px solid ${T.danger}55`, background: T.dangerSoft, color: T.danger, borderRadius: 8, cursor: "pointer", padding: "7px 11px", height: 34 }}>✕</button>
          </div>

          <div style={{ overflowX: "auto" }}>
            <table className="pkg-price-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 11, minWidth: 720 }}>
              <thead>
                <tr style={{ color: T.faint, textTransform: "uppercase", fontSize: 9, letterSpacing: 0.4 }}>
                  <th style={{ textAlign: "left", padding: "5px 6px", borderBottom: `1px solid ${T.border}` }}>Hotel Type *</th>
                  {["1 Pax", "2 Pax", "4 Pax", "6 Pax", "8 Pax", "Extra Adult", "Extra Child", "Extra Infant"].map(h => (
                    <th key={h} style={{ textAlign: "right", padding: "5px 6px", borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {(period.rows || []).map((row, rIndex) => (
                  <tr key={rIndex}>
                    <td style={{ padding: "4px 6px" }}>
                      <input value={row.hotel_type || ""} onChange={e => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, rows: p.rows.map((r, ri) => ri === rIndex ? { ...r, hotel_type: e.target.value } : r) } : p))} style={{ ...inputS, padding: "5px 7px", fontSize: 11 }} placeholder="3 Star" />
                    </td>
                    {["pax_1", "pax_2", "pax_4", "pax_6", "pax_8", "extra_adult", "extra_child", "extra_infant"].map(key => (
                      <td key={key} style={{ padding: "4px 6px" }}>
                        <input type="number" min="0" step="0.01" value={row[key] ?? ""} onChange={e => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, rows: p.rows.map((r, ri) => ri === rIndex ? { ...r, [key]: e.target.value } : r) } : p))} style={{ ...inputS, padding: "5px 7px", fontSize: 11, textAlign: "right" }} placeholder="0" />
                      </td>
                    ))}
                    <td style={{ padding: "4px 6px" }}>
                      <button type="button" aria-label={`Remove hotel row ${rIndex + 1}`} onClick={() => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, rows: p.rows.filter((_, ri) => ri !== rIndex) } : p))} style={{ border: `1px solid ${T.danger}55`, background: T.dangerSoft, color: T.danger, borderRadius: 6, cursor: "pointer", padding: "3px 7px", fontSize: 11 }}>✕</button>
                    </td>
                  </tr>
                ))}
                {!(period.rows || []).length && (
                  <tr><td colSpan={10} style={{ color: T.faint, fontSize: 12, padding: "8px 6px" }}>No rows yet — add a hotel type below.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() => setPricePeriods(prev => prev.map((p, i) => i === pIndex ? { ...p, rows: [...(p.rows || []), { hotel_type: "", pax_1: 0, pax_2: 0, pax_4: 0, pax_6: 0, pax_8: 0, extra_adult: 0, extra_child: 0, extra_infant: 0 }] } : p))}
            style={{ ...smallBtn, borderStyle: "dashed", marginTop: 8, width: "100%", justifyContent: "center" }}
          >
            <Plus size={13} /> Add Hotel Type Row
          </button>
        </div>
      ))}

      {!pricePeriods.length && (
        <div style={{ color: T.faint, fontSize: 13, marginBottom: 8 }}>No date-range periods — the standard selling price applies to every date.</div>
      )}

      <button
        type="button"
        onClick={() => setPricePeriods(prev => [...prev, { from_date: "", to_date: "", rows: [{ hotel_type: "", pax_1: 0, pax_2: 0, pax_4: 0, pax_6: 0, pax_8: 0, extra_adult: 0, extra_child: 0, extra_infant: 0 }] }])}
        style={{ ...smallBtn, borderStyle: "dashed", width: "100%", justifyContent: "center" }}
      >
        <Plus size={13} /> Add Price Period
      </button>
    </div>

  </div>
)}

{activeTab === "availability" && (
  <div style={{ display: "grid", gap: 18 }}>

    <div>
  <SecTitle>Booking Window</SecTitle>

  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 12
    }}
  >
    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Valid From (Booking Start Date)
      </label>

      <input
        type="date"
        value={form.booking_start_date || ""}
        onChange={e =>
          set("booking_start_date", e.target.value)
        }
        style={inputS}
      />
    </div>

    <div>
      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Valid To (Booking End Date)
      </label>

      <input
        type="date"
        value={form.booking_end_date || ""}
        onChange={e =>
          set("booking_end_date", e.target.value)
        }
        style={inputS}
      />
    </div>
  </div>

  <div
    style={{
      marginTop: 8,
      fontSize: 11,
      color: T.faint
    }}
  >
    Example: Customers can book this package only between
    the selected booking start and end dates.
  </div>
</div>

    <div>
      <SecTitle>Departure Availability</SecTitle>

      <label
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: T.muted,
          display: "block",
          marginBottom: 4
        }}
      >
        Departure Type
      </label>

      <select
        value={form.departure_type || "daily"}
        onChange={e =>
          set("departure_type", e.target.value)
        }
        style={inputS}
      >
        <option value="daily">
          Daily — New group every day
        </option>

        <option value="recurring">
          Recurring — Every X days
        </option>

        <option value="fixed">
          Fixed Dates — Specific dates only
        </option>
      </select>
    </div>

    {form.departure_type === "recurring" && (
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12
        }}
      >
        <div>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: T.muted,
              display: "block",
              marginBottom: 4
            }}
          >
            Repeat Every (Days)
          </label>

          <input
            type="number"
            min="1"
            value={form.departure_interval_days}
            onChange={e =>
              set("departure_interval_days", e.target.value)
            }
            style={inputS}
            placeholder="5"
          />
        </div>

        <div>
          <label
            style={{
              fontSize: 11,
              fontWeight: 600,
              color: T.muted,
              display: "block",
              marginBottom: 4
            }}
          >
            First Departure Date
          </label>

         <input
  type="date"
  value={form.departure_start_date || ""}
  onChange={e =>
    set("departure_start_date", e.target.value)
  }
  style={inputS}
/>
        </div>

        <div
          style={{
            gridColumn: "1/-1",
            fontSize: 11,
            color: T.faint
          }}
        >
          Example: Start 5 Oct + every 5 days → 5 Oct, 10 Oct,
          15 Oct, 20 Oct...
        </div>
      </div>
    )}

    {form.departure_type === "fixed" && (
      <div>
        <label
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: T.muted,
            display: "block",
            marginBottom: 6
          }}
        >
          Available Departure Dates
        </label>

        {(form.departure_dates || []).map((date, index) => (
          <div
            key={index}
            style={{
  display: "flex",
  gap: 8,
  marginBottom: 8,
  minWidth: 0
}}
          >
            <input
  type="date"
  value={date}
  onChange={e =>
    set(
      "departure_dates",
      (form.departure_dates || []).map((d, i) =>
        i === index ? e.target.value : d
      )
    )
  }
  style={{ ...inputS }}
/>

            <button
              type="button"
              onClick={() =>
                set(
                  "departure_dates",
                  (form.departure_dates || []).filter(
                    (_, i) => i !== index
                  )
                )
              }
              style={{
                border: `1px solid ${T.danger}55`,
                background: T.dangerSoft,
                color: T.danger,
                borderRadius: 8,
                cursor: "pointer",
                padding: "0 12px"
              }}
            >
              ×
            </button>
          </div>
        ))}

        <Btn
          type="button"
          onClick={() =>
            set("departure_dates", [
              ...(form.departure_dates || []),
              ""
            ])
          }
          variant="ghost"
          size="sm"
        >
          <Plus size={13} /> Add Departure Date
        </Btn>
      </div>
    )}

  </div>
)}
      {activeTab === "details" && <div><SecTitle>Description (rich text)</SecTitle><RichTextEditor value={form.description || ""} onChange={v => set("description", v)} minHeight={150} /><div style={{ fontSize: 11, color: T.faint, marginTop: 4 }}>Formatted HTML — shown on the details page and the PDF. Scripts are stripped server-side.</div><SecTitle>Highlights</SecTitle>{listEditor(highlights, setHigh, "e.g. Tanah Lot Temple")}</div>}
      {activeTab === "itinerary" && <div>
        {itinerary.map((row, i) => (
          <div key={i} style={{ background: T.accentSoft, borderRadius: 10, padding: "12px", marginBottom: 10, position: "relative" }}>
            <button onClick={() => setItin(prev => prev.filter((_, idx) => idx !== i))} aria-label={`Remove day ${row.day || i + 1}`} style={{ position: "absolute", top: 8, right: 8, background: "none", border: "none", color: T.danger, cursor: "pointer", fontSize: 16, fontWeight: 600 }}>✕</button>
            <div className="pkg-itin-head" style={{ display: "grid", gridTemplateColumns: "60px 1fr 1fr", gap: 8, marginBottom: 8 }}>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: T.muted, display: "block", marginBottom: 3 }}>Day</label><input type="number" min="1" value={row.day} onChange={e => setItin(prev => prev.map((r, idx) => idx === i ? { ...r, day: e.target.value } : r))} style={{ ...inputS, padding: "7px 8px" }} /></div>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: T.muted, display: "block", marginBottom: 3 }}>Title *</label><input value={row.title} onChange={e => setItin(prev => prev.map((r, idx) => idx === i ? { ...r, title: e.target.value } : r))} style={{ ...inputS, padding: "7px 10px" }} placeholder="Arrival & Check-in" /></div>
              <div><label style={{ fontSize: 10, fontWeight: 600, color: T.muted, display: "block", marginBottom: 3 }}>City</label><input value={row.city || ""} onChange={e => setItin(prev => prev.map((r, idx) => idx === i ? { ...r, city: e.target.value } : r))} style={{ ...inputS, padding: "7px 10px" }} placeholder="North Goa" /></div>
            </div>
            <label style={{ fontSize: 10, fontWeight: 600, color: T.muted, display: "block", marginBottom: 3 }}>Description (rich text)</label>
            <RichTextEditor
              value={row.description || ""}
              onChange={v => setItin(prev => prev.map((r, idx) => idx === i ? { ...r, description: v } : r))}
              minHeight={90}
            />
            <ImageField
              label="Day Image (optional)"
              value={row.image_url || ""}
              onChange={v => setItin(prev => prev.map((r, idx) => idx === i ? { ...r, image_url: v } : r))}
              height={54}
            />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }} className="pkg-day-extras">
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Activities</label>
                {dayChipEditor(i, "activities", "Sunset cruise")}
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Hotels</label>
                {dayChipEditor(i, "hotels", "Beach Resort Goa")}
              </div>
              <div>
                <label style={{ fontSize: 10, fontWeight: 700, color: T.muted, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: 0.4 }}>Transfers</label>
                {dayChipEditor(i, "transfers", "Private AC cab")}
              </div>
            </div>
          </div>
        ))}
        <button onClick={() => setItin(v => [...v, { day: "", title: "", description: "", city: "", image_url: "", activities: [], hotels: [], transfers: [] }])} style={{ padding: "10px", border: `1.5px dashed ${T.border}`, borderRadius: 10, background: T.bg, color: T.muted, cursor: "pointer", fontSize: 13, width: "100%" }}>+ Add Day</button>
      </div>}

      {activeTab === "facilities" && <div>
        <SecTitle>Facilities & Amenities</SecTitle>

        {catalogError && (
          <div style={{ background: T.warnSoft, border: `1px solid ${T.warn}55`, color: T.warn, padding: "9px 12px", borderRadius: 8, fontSize: 12, marginBottom: 10 }}>
            ⚠ {catalogError}
          </div>
        )}

        {!facilityCatalog.length && !catalogError && (
          <div style={{ color: T.faint, fontSize: 13, padding: "10px 0" }}>Loading facilities…</div>
        )}

        {!!facilityCatalog.length && (
          <>
            <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
              {facilityIds.length} selected — tick every facility included in this package.
            </div>

            <div className="pkg-facility-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(190px,1fr))", gap: 8, maxHeight: 320, overflowY: "auto", padding: "2px 2px 8px" }}>
              {facilityCatalog.map(facility => {
                const checked = facilityIds.includes(Number(facility.id));
                return (
                  <label
                    key={facility.id}
                    title={facility.name}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      borderRadius: 8,
                      border: `1px solid ${checked ? T.accent : T.border}`,
                      background: checked ? T.accentSoft : T.bg,
                      cursor: "pointer",
                      fontSize: 12,
                      color: T.ink,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() =>
                        setFacilityIds(prev =>
                          checked
                            ? prev.filter(id => id !== Number(facility.id))
                            : [...prev, Number(facility.id)]
                        )
                      }
                      style={{ width: 15, height: 15, accentColor: T.accent, flexShrink: 0 }}
                    />
                    <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{facility.name}</span>
                  </label>
                );
              })}
            </div>
          </>
        )}
      </div>}
      {activeTab === "gallery" && <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
          <SecTitle>Tour Gallery Images</SecTitle>
          <button
            type="button"
            onClick={() => setGallery(v => [...v, { title: "", image_url: "", alt_text: "" }])}
            style={{ border: `1px solid ${T.border}`, background: T.bg, color: T.accent, borderRadius: 7, padding: "5px 9px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600 }}
          >
            <Plus size={13} /> Add Image
          </button>
        </div>

        <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
          Extra package images shown in the customer gallery, with an image name and alt text. The main package image is set in Overview.
        </div>

        {!gallery.length && (
          <div style={{ color: T.faint, fontSize: 13, padding: "8px 0" }}>No gallery images yet.</div>
        )}

        {gallery.map((image, index) => (
          <div key={index} className="pkg-gallery-row" style={{ marginBottom: 10, padding: 10, borderRadius: 8, background: T.bg, border: `1px solid ${T.border}` }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, alignItems: "start" }}>
              <ImageField
                label={`Image ${index + 1}`}
                value={image.image_url}
                onChange={v => setGallery(prev => prev.map((img, i) => i === index ? { ...img, image_url: v } : img))}
                height={54}
              />
              <button
                type="button"
                aria-label={`Remove gallery image ${index + 1}`}
                onClick={() => setGallery(prev => prev.filter((_, i) => i !== index))}
                style={{ border: `1px solid ${T.danger}55`, background: T.dangerSoft, color: T.danger, borderRadius: 8, cursor: "pointer", padding: "6px 10px", marginTop: 22 }}
              >
                ✕
              </button>
            </div>

            <div className="pkg-gallery-meta" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 8 }}>
              <input
                value={image.title || ""}
                onChange={e => setGallery(prev => prev.map((img, i) => i === index ? { ...img, title: e.target.value } : img))}
                style={inputS}
                placeholder="Image name (e.g. Baga Beach)"
              />
              <input
                value={image.alt_text}
                onChange={e => setGallery(prev => prev.map((img, i) => i === index ? { ...img, alt_text: e.target.value } : img))}
                style={inputS}
                placeholder="Alt text (describe the image)"
              />
            </div>
          </div>
        ))}
      </div>}

      {activeTab === "policy" && (
        <div style={{ display: "grid", gap: 16 }}>
          <div>
            <SecTitle>Package Policies (from library)</SecTitle>
            <div style={{ fontSize: 11, color: T.faint, marginBottom: 8 }}>
              Tick every policy that applies to this package. Policy content is shown on the customer details page and in the PDF. Create shared policies below so they can be reused across packages.
            </div>

            {!policyLib.length && (
              <div style={{ color: T.faint, fontSize: 13, padding: "6px 0" }}>
                {schemaPending.length ? "Policy library unavailable until the migration is run." : "No policies in the library yet — add the first one below."}
              </div>
            )}

            <div style={{ display: "grid", gap: 6 }}>
              {policyLib.map(policy => {
                const checked = policyIds.includes(Number(policy.id));
                return (
                  <div key={policy.id} style={{ display: "flex", gap: 6, alignItems: "stretch", borderRadius: 8, border: `1px solid ${checked ? T.accent : T.border}`, background: checked ? T.accentSoft : T.bg }}>
                    <label style={{ display: "flex", gap: 9, alignItems: "flex-start", padding: "9px 11px", cursor: "pointer", flex: 1, minWidth: 0 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleId(setPolicyIds)(Number(policy.id))} style={{ width: 15, height: 15, accentColor: T.accent, marginTop: 2 }} />
                      <span style={{ minWidth: 0 }}>
                        <span style={{ fontSize: 13, fontWeight: 600, color: T.ink, display: "block" }}>{policy.name}</span>
                        <span style={{ fontSize: 11, color: T.muted, display: "block", wordBreak: "break-word" }}>
                          {stripTags(policy.content).slice(0, 160) || "No content yet — edit it after saving."}
                        </span>
                      </span>
                    </label>
                    <button
                      type="button"
                      title={`View full content of "${policy.name}"`}
                      aria-label={`View policy ${policy.name}`}
                      onClick={() => setViewItem({ title: policy.name, content: policy.content || "" })}
                      style={{ border: "none", background: "none", color: T.green || T.accent, cursor: "pointer", padding: "0 12px", fontSize: 14, alignSelf: "center" }}
                    >👁</button>
                  </div>
                );
              })}
            </div>

            <div style={{ marginTop: 12, padding: 12, borderRadius: 8, background: T.bg, border: `1px dashed ${T.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 6 }}>New reusable policy</div>
              <input
                value={newPolicy.name}
                onChange={e => setNewPolicy(p => ({ ...p, name: e.target.value }))}
                style={{ ...inputS, marginBottom: 8 }}
                placeholder="e.g. Passport & Visa Policy"
                maxLength={120}
              />
              <RichTextEditor value={newPolicy.content} onChange={v => setNewPolicy(p => ({ ...p, content: v }))} minHeight={80} />
              <button type="button" onClick={addPolicy} disabled={libBusy} style={{ ...smallBtn, marginTop: 8, opacity: libBusy ? 0.6 : 1 }}>
                <Plus size={13} /> {libBusy ? "Saving…" : "Add Policy to Library"}
              </button>
            </div>
          </div>

          <div>
            <SecTitle>Frequently Asked Questions</SecTitle>
            {faqs.map((faq, index) => (
              <div key={index} style={{ padding: 10, borderRadius: 8, background: T.bg, border: `1px solid ${T.border}`, marginBottom: 8 }}>
                <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
                  <input
                    value={faq.question || ""}
                    onChange={e => setFaqs(prev => prev.map((f, i) => i === index ? { ...f, question: e.target.value } : f))}
                    style={{ ...inputS, fontWeight: 600 }}
                    placeholder="Is airfare included?"
                    maxLength={300}
                  />
                  <button type="button" aria-label={`Remove FAQ ${index + 1}`} onClick={() => setFaqs(prev => prev.filter((_, i) => i !== index))} style={{ border: `1px solid ${T.danger}55`, background: T.dangerSoft, color: T.danger, borderRadius: 8, cursor: "pointer", padding: "0 11px" }}>✕</button>
                </div>
                <RichTextEditor
                  value={faq.answer || ""}
                  onChange={v => setFaqs(prev => prev.map((f, i) => i === index ? { ...f, answer: v } : f))}
                  minHeight={70}
                />
              </div>
            ))}
            {!faqs.length && <div style={{ color: T.faint, fontSize: 13, marginBottom: 8 }}>No FAQs yet.</div>}
            <button type="button" onClick={() => setFaqs(prev => [...prev, { question: "", answer: "" }])} style={{ ...smallBtn, borderStyle: "dashed", width: "100%", justifyContent: "center" }}>
              <Plus size={13} /> Add FAQ
            </button>
          </div>
        </div>
      )}

      {activeTab === "inclusions" && (
        <div style={{ display: "grid", gap: 18 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="pkg-inc-exc-cols">
            <div>
              <SecTitle>✅ Included (custom list)</SecTitle>
              {listEditor(inclusions, setInc, "e.g. Return airfare")}
              <SecTitle>Library — Inclusions</SecTitle>
              {!incLib.length && <div style={{ color: T.faint, fontSize: 12 }}>{schemaPending.length ? "Unavailable until migration runs." : "Library is empty."}</div>}
              {incLib.map(option => {
                const checked = incIds.includes(Number(option.id));
                return (
                  <div key={option.id} style={{ display: "flex", gap: 4, alignItems: "center", border: `1px solid ${checked ? T.accent : T.border}`, background: checked ? T.accentSoft : T.bg, borderRadius: 7, marginBottom: 5 }}>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 9px", cursor: "pointer", fontSize: 12, flex: 1, minWidth: 0 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleId(setIncIds)(Number(option.id))} style={{ width: 14, height: 14, accentColor: T.accent }} />
                      <span style={{ color: T.ink }}>{option.name}</span>
                    </label>
                    <button type="button" title={`View "${option.name}"`} aria-label={`View inclusion ${option.name}`} onClick={() => setViewItem({ title: option.name, content: option.content || "" })} style={{ border: "none", background: "none", color: T.green || T.accent, cursor: "pointer", padding: "0 8px", fontSize: 13 }}>👁</button>
                  </div>
                );
              })}
            </div>
            <div>
              <SecTitle>❌ Not Included (custom list)</SecTitle>
              {listEditor(exclusions, setExc, "e.g. Personal expenses")}
              <SecTitle>Library — Exclusions</SecTitle>
              {!excLib.length && <div style={{ color: T.faint, fontSize: 12 }}>{schemaPending.length ? "Unavailable until migration runs." : "Library is empty."}</div>}
              {excLib.map(option => {
                const checked = excIds.includes(Number(option.id));
                return (
                  <div key={option.id} style={{ display: "flex", gap: 4, alignItems: "center", border: `1px solid ${checked ? T.accent : T.border}`, background: checked ? T.accentSoft : T.bg, borderRadius: 7, marginBottom: 5 }}>
                    <label style={{ display: "flex", gap: 8, alignItems: "center", padding: "7px 9px", cursor: "pointer", fontSize: 12, flex: 1, minWidth: 0 }}>
                      <input type="checkbox" checked={checked} onChange={() => toggleId(setExcIds)(Number(option.id))} style={{ width: 14, height: 14, accentColor: T.accent }} />
                      <span style={{ color: T.ink }}>{option.name}</span>
                    </label>
                    <button type="button" title={`View "${option.name}"`} aria-label={`View exclusion ${option.name}`} onClick={() => setViewItem({ title: option.name, content: option.content || "" })} style={{ border: "none", background: "none", color: T.green || T.accent, cursor: "pointer", padding: "0 8px", fontSize: 13 }}>👁</button>
                  </div>
                );
              })}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="pkg-inc-exc-cols">
            <div>
              <SecTitle>Inclusions display text (rich)</SecTitle>
              <RichTextEditor value={form.inclusion_display_text || ""} onChange={v => set("inclusion_display_text", v)} minHeight={110} />
            </div>
            <div>
              <SecTitle>Exclusions display text (rich)</SecTitle>
              <RichTextEditor value={form.exclusion_display_text || ""} onChange={v => set("exclusion_display_text", v)} minHeight={110} />
            </div>
          </div>
        </div>
      )}

      {viewItem && (
        <Modal onClose={() => setViewItem(null)} title={viewItem.title} subtitle="Library content" maxWidth={620}>
          {/<[a-z/][^>]*>/i.test(String(viewItem.content || "")) ? (
            <div className="holiday-rich-text" dangerouslySetInnerHTML={{ __html: viewItem.content }} />
          ) : (
            <p style={{ fontSize: 13, color: T.ink, whiteSpace: "pre-wrap", margin: 0 }}>
              {viewItem.content || "No content yet — edit it in the library."}
            </p>
          )}
        </Modal>
      )}
      {error && <div style={{ background: T.dangerSoft, border: `1px solid ${T.danger}55`, color: T.danger, padding: "10px 14px", borderRadius: 8, fontSize: 13, marginTop: 14 }}>⚠ {error}</div>}
      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <Btn onClick={onClose} variant="ghost">Cancel</Btn>
        <button onClick={handleSave} disabled={saving} style={{ padding: "9px 26px", border: "none", borderRadius: 8, background: T.accent, color: "#fff", fontSize: 13, fontWeight: 500, cursor: "pointer", opacity: saving ? .6 : 1 }}>{saving ? "Saving…" : isEdit ? "Save Changes" : "Add Package"}</button>
      </div>
    </Modal>
  );
}

function HolidayPaymentPolicyModal({ pkg, onClose, onSaved }) {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    payment_mode: "full",
    initial_payment_percent: 30,
installment_rules: [
  { percent: 10, days_before_travel: 60 },
  { percent: 60, days_before_travel: 30 }
],
    final_payment_days_before_travel: 0,
    grace_period_days: 0,
    overdue_action: "cancel",
    reminder_rules: [
      { type: "payment_approaching", days_before_due: 7 },
      { type: "payment_urgent", days_before_due: 3 },
      { type: "final_reminder", days_before_due: 1 }
    ],
    cancellation_policy: [
      { min_days_before_travel: 30, refund_percent: 100 },
      { min_days_before_travel: 15, refund_percent: 50 },
      { min_days_before_travel: 0, refund_percent: 0 }
    ],
    is_active: true,
  });

  const set = (key, value) =>
    setForm(prev => ({ ...prev, [key]: value }));

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      const res = await apiFetch(`/holiday-payment-policies/${pkg.id}`);

      if (mounted && res) {
        setForm(prev => ({
          ...prev,
          ...res,
          installment_rules: Array.isArray(res.installment_rules)
            ? res.installment_rules
            : prev.installment_rules,
          reminder_rules: Array.isArray(res.reminder_rules)
            ? res.reminder_rules
            : prev.reminder_rules,
          cancellation_policy: Array.isArray(res.cancellation_policy)
            ? res.cancellation_policy
            : prev.cancellation_policy,
          is_active: Number(res.is_active ?? 1) === 1,
        }));
      }

      if (mounted) setLoading(false);
    })();

    return () => {
      mounted = false;
    };
  }, [pkg.id]);

  const handleSave = async () => {
    setError(null);

    const paymentMode = form.payment_mode;

    const initialPercent =
      paymentMode === "full"
        ? 100
        : Number(form.initial_payment_percent);

    if (
      paymentMode === "partial" &&
      (!Number.isFinite(initialPercent) ||
        initialPercent <= 0 ||
        initialPercent >= 100)
    ) {
      setError("Initial payment percentage must be between 1% and 99%.");
      return;
    }

const installmentTotal = form.installment_rules.reduce(
  (sum, item) => sum + Number(item.percent || 0),
  0
);

if (paymentMode === "partial") {
  if (initialPercent <= 0 || initialPercent >= 100) {
    setError("Initial payment percentage must be greater than 0% and less than 100%.");
    return;
  }

  const remainingPercent = 100 - initialPercent;

  if (installmentTotal !== remainingPercent) {
    setError(
      `Installment percentages must total ${remainingPercent}% after the initial payment.`
    );
    return;
  }
}

    setSaving(true);

    const payload = {
      package_id: pkg.id,
      payment_mode: paymentMode,
      initial_payment_percent: initialPercent,
      installment_rules: form.installment_rules.map(item => ({
        percent: Number(item.percent || 0),
        days_before_travel: Number(item.days_before_travel || 0),
      })),
      final_payment_days_before_travel: Number(
        form.final_payment_days_before_travel || 0
      ),
      grace_period_days: Number(form.grace_period_days || 0),
      overdue_action: form.overdue_action,
      reminder_rules: form.reminder_rules.map(item => ({
        type: item.type,
        days_before_due: Number(item.days_before_due || 0),
      })),
      cancellation_policy: form.cancellation_policy.map(item => ({
        min_days_before_travel: Number(
          item.min_days_before_travel || 0
        ),
        refund_percent: Number(item.refund_percent || 0),
      })),
      is_active: form.is_active ? 1 : 0,
    };

    const res = await apiPut(
      `/holiday-payment-policies/${pkg.id}`,
      payload
    );

    if (!res.ok) {
      setError(res.error || "Failed to save payment policy.");
      setSaving(false);
      return;
    }

    onSaved?.(res.data);
    onClose();
    setSaving(false);
  };

  if (loading) {
    return (
      <Modal
        onClose={onClose}
        title="Holiday Payment Policy"
        subtitle={pkg.title}
        maxWidth={760}
      >
        <div style={{ padding: 40, textAlign: "center", color: T.faint }}>
          Loading payment policy…
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      onClose={onClose}
      title={`Payment Policy — ${pkg.title}`}
      subtitle={`Package #${pkg.id}`}
      maxWidth={760}
    >
      <SecTitle>Payment Mode</SecTitle>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        {[
          { value: "full", label: "Full Payment" },
          { value: "partial", label: "Partial / Installments" },
        ].map(option => (
          <button
            key={option.value}
            onClick={() => set("payment_mode", option.value)}
            style={{
              padding: "12px 14px",
              borderRadius: 9,
              border: `1px solid ${
                form.payment_mode === option.value
                  ? T.accent
                  : T.border
              }`,
              background:
                form.payment_mode === option.value
                  ? T.accentSoft
                  : T.card,
              color:
                form.payment_mode === option.value
                  ? T.accent
                  : T.text,
              cursor: "pointer",
              fontWeight: 600,
              textAlign: "left",
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {form.payment_mode === "partial" && (
        <>
          <SecTitle>Initial Payment</SecTitle>

          <label style={{ display: "grid", gap: 6 }}>
            <span style={{ fontSize: 12, color: T.muted }}>
              Initial Payment (%)
            </span>

            <input
              type="number"
              min="1"
              max="99"
              step="1"
              value={form.initial_payment_percent}
              onChange={e =>
                set("initial_payment_percent", e.target.value)
              }
              style={inputS}
            />
          </label>

          <SecTitle>Installment Rules</SecTitle>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: 8,
    marginBottom: 6,
    alignItems: "center",
  }}
>
  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Installment Percentage
  </span>

  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Days Before Travel
  </span>

  <span />
</div>

{form.installment_rules.map((rule, index) => (
  <div
    key={index}
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr auto",
      gap: 8,
      marginBottom: 8,
      alignItems: "center",
    }}
  >
    <div style={{ position: "relative" }}>
      <input
        type="number"
        min="1"
        max="100"
        placeholder="Percentage"
        value={rule.percent}
        onChange={e =>
          set(
            "installment_rules",
            form.installment_rules.map((x, i) =>
              i === index
                ? { ...x, percent: e.target.value }
                : x
            )
          )
        }
        style={{
          ...inputS,
          paddingRight: 32,
        }}
      />

      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: T.muted,
          pointerEvents: "none",
        }}
      >
        %
      </span>
    </div>

    <div style={{ position: "relative" }}>
      <input
        type="number"
        min="0"
        placeholder="Days before travel"
        value={rule.days_before_travel}
        onChange={e =>
          set(
            "installment_rules",
            form.installment_rules.map((x, i) =>
              i === index
                ? {
                    ...x,
                    days_before_travel: e.target.value,
                  }
                : x
            )
          )
        }
        style={{
          ...inputS,
          paddingRight: 55,
        }}
      />

      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: T.muted,
          fontSize: 12,
          pointerEvents: "none",
        }}
      >
        days
      </span>
    </div>

    <button
      onClick={() =>
        set(
          "installment_rules",
          form.installment_rules.filter(
            (_, i) => i !== index
          )
        )
      }
      style={{
        border: `1px solid ${T.danger}55`,
        background: T.dangerSoft,
        color: T.danger,
        borderRadius: 8,
        cursor: "pointer",
      }}
    >
      ×
    </button>
  </div>
))}

          <Btn
            onClick={() =>
              set("installment_rules", [
                ...form.installment_rules,
                { percent: 0, days_before_travel: 0 },
              ])
            }
            variant="ghost"
            size="sm"
          >
            <Plus size={13} /> Add Installment
          </Btn>
        </>
      )}

      <SecTitle>Final Payment & Overdue Rules</SecTitle>

      <DGrid>
        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            Final Payment Days Before Travel
          </span>
          <input
            type="number"
            min="0"
            value={form.final_payment_days_before_travel}
            onChange={e =>
              set(
                "final_payment_days_before_travel",
                e.target.value
              )
            }
            style={inputS}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            Grace Period (Days)
          </span>
          <input
            type="number"
            min="0"
            value={form.grace_period_days}
            onChange={e =>
              set("grace_period_days", e.target.value)
            }
            style={inputS}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            After Grace Period
          </span>

          <select
            value={form.overdue_action}
            onChange={e => set("overdue_action", e.target.value)}
            style={inputS}
          >
            <option value="suspend">Suspend Booking</option>
            <option value="cancel">Cancel Booking</option>
            <option value="default">Mark Defaulted</option>
          </select>
        </label>
      </DGrid>

      <SecTitle>Payment Reminders</SecTitle>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: 8,
    marginBottom: 6,
    alignItems: "center",
  }}
>
  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Reminder Type
  </span>

  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Schedule
  </span>

  <span />
</div>

{form.reminder_rules.map((rule, index) => {
  const isGracePeriod = rule.type === "grace_period";
  const isOverdue = rule.type === "overdue";

  return (
    <div
      key={index}
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr auto",
        gap: 8,
        marginBottom: 8,
        alignItems: "center",
      }}
    >
      <select
  value={rule.type}
  onChange={e =>
    set(
      "reminder_rules",
      form.reminder_rules.map((x, i) =>
        i === index ? { ...x, type: e.target.value } : x
      )
    )
  }
  style={inputS}
>
  <option value="payment_approaching">
    Payment Approaching
  </option>

  <option value="payment_urgent">
    Payment Urgent
  </option>

  <option value="final_reminder">
    Final Reminder
  </option>

  {(rule.type === "grace_period" ||
    !form.reminder_rules.some(
      (x, i) => i !== index && x.type === "grace_period"
    )) && (
    <option value="grace_period">
      Grace Period
    </option>
  )}

  {(rule.type === "overdue" ||
    !form.reminder_rules.some(
      (x, i) => i !== index && x.type === "overdue"
    )) && (
    <option value="overdue">
      Overdue 
    </option>
  )}
</select>

      {isGracePeriod ? (
        <select
          value={rule.frequency || "daily"}
          onChange={e =>
            set(
              "reminder_rules",
              form.reminder_rules.map((x, i) =>
                i === index
                  ? {
                      ...x,
                      frequency: e.target.value,
                      days_before_due: 0,
                    }
                  : x
              )
            )
          }
          style={inputS}
        >
          <option value="daily">Daily During Grace Period</option>
          <option value="one_time">One Time</option>
        </select>
      ) : isOverdue ? (
        <div
          style={{
            ...inputS,
            display: "flex",
            alignItems: "center",
            color: T.muted,
            background: T.surface,
          }}
        >
          After Grace Period
        </div>
      ) : (
        <div style={{ position: "relative" }}>
          <input
            type="number"
            min="0"
            value={rule.days_before_due}
            onChange={e =>
              set(
                "reminder_rules",
                form.reminder_rules.map((x, i) =>
                  i === index
                    ? {
                        ...x,
                        days_before_due: e.target.value,
                      }
                    : x
                )
              )
            }
            style={{
              ...inputS,
              paddingRight: 45,
            }}
            placeholder="Days"
          />

          <span
            style={{
              position: "absolute",
              right: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: T.muted,
              fontSize: 12,
              pointerEvents: "none",
            }}
          >
            days
          </span>
        </div>
      )}

      <button
        onClick={() =>
          set(
            "reminder_rules",
            form.reminder_rules.filter((_, i) => i !== index)
          )
        }
        style={{
          border: `1px solid ${T.danger}55`,
          background: T.dangerSoft,
          color: T.danger,
          borderRadius: 8,
          cursor: "pointer",
          padding: "0 12px",
          height: 38,
        }}
      >
        <X size={14} />
      </button>
    </div>
  );
})}

<Btn
  onClick={() =>
    set("reminder_rules", [
      ...form.reminder_rules,
      {
        type: "payment_approaching",
        days_before_due: 7,
      },
    ])
  }
  variant="ghost"
  size="sm"
>
  <Plus size={13} /> Add Reminder
</Btn>

      <SecTitle>Cancellation / Refund Policy</SecTitle>

<div
  style={{
    display: "grid",
    gridTemplateColumns: "1fr 1fr auto",
    gap: 8,
    marginBottom: 6,
    alignItems: "center",
  }}
>
  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Minimum Days Before Travel
  </span>

  <span style={{ fontSize: 12, color: T.muted, fontWeight: 600 }}>
    Refund Percentage
  </span>

  <span />
</div>

{form.cancellation_policy.map((rule, index) => (
  <div
    key={index}
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 1fr auto",
      gap: 8,
      marginBottom: 8,
      alignItems: "center",
    }}
  >
    <div style={{ position: "relative" }}>
      <input
        type="number"
        min="0"
        value={rule.min_days_before_travel}
        onChange={e =>
          set(
            "cancellation_policy",
            form.cancellation_policy.map((x, i) =>
              i === index
                ? {
                    ...x,
                    min_days_before_travel: e.target.value,
                  }
                : x
            )
          )
        }
        style={{
          ...inputS,
          paddingRight: 45,
        }}
        placeholder="Days"
      />

      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: T.muted,
          fontSize: 12,
          pointerEvents: "none",
        }}
      >
        days
      </span>
    </div>

    <div style={{ position: "relative" }}>
      <input
        type="number"
        min="0"
        max="100"
        value={rule.refund_percent}
        onChange={e =>
          set(
            "cancellation_policy",
            form.cancellation_policy.map((x, i) =>
              i === index
                ? { ...x, refund_percent: e.target.value }
                : x
            )
          )
        }
        style={{
          ...inputS,
          paddingRight: 32,
        }}
        placeholder="Percentage"
      />

      <span
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          color: T.muted,
          pointerEvents: "none",
        }}
      >
        %
      </span>
    </div>

    <button
      onClick={() =>
        set(
          "cancellation_policy",
          form.cancellation_policy.filter((_, i) => i !== index)
        )
      }
      style={{
        border: `1px solid ${T.danger}55`,
        background: T.dangerSoft,
        color: T.danger,
        borderRadius: 8,
        cursor: "pointer",
        padding: "0 12px",
      }}
    >
      <X size={14} />
    </button>
  </div>
))}

<Btn
  onClick={() =>
    set("cancellation_policy", [
      ...form.cancellation_policy,
      {
        min_days_before_travel: 0,
        refund_percent: 0,
      },
    ])
  }
  variant="ghost"
  size="sm"
>
  <Plus size={13} /> Add Cancellation Rule
</Btn>

      {error && (
        <div
          style={{
            background: T.dangerSoft,
            color: T.danger,
            border: `1px solid ${T.danger}55`,
            borderRadius: 8,
            padding: "10px 12px",
            marginTop: 16,
            fontSize: 13,
          }}
        >
          ⚠ {error}
        </div>
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 10,
          marginTop: 20,
          paddingTop: 14,
          borderTop: `1px solid ${T.border}`,
        }}
      >
        <Btn onClick={onClose} variant="ghost">
          Cancel
        </Btn>

        <Btn
          onClick={handleSave}
          variant="primary"
          disabled={saving}
        >
          {saving ? "Saving…" : "Save Payment Policy"}
        </Btn>
      </div>
    </Modal>
  );
}

/* ─── Package actions ─────────────────────────────────────────── */
function PackageDetailsModal({ pkg, onClose, onEdit }) {
  const T = useThemeColors();

  if (!pkg) return null;

  const section = (title, items, emptyText = "None") => (
    <div style={{ marginBottom: 14 }}>
      <SecTitle>{title}</SecTitle>
      {items && items.length ? (
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: T.text, lineHeight: 1.8 }}>
          {items.map((item, i) => <li key={i}>{item}</li>)}
        </ul>
      ) : (
        <div style={{ fontSize: 13, color: T.faint }}>{emptyText}</div>
      )}
    </div>
  );

  return (
    <Modal onClose={onClose} title={pkg.title} subtitle={`Package #${pkg.id} · ${pkg.location || "—"}`} maxWidth={760}>
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <Badge status={pkg.is_active ? "active" : "inactive"} />
        <span style={{ fontSize: 12, color: T.muted }}>{pkg.category_label || pkg.category_id}</span>
        <span style={{ fontSize: 12, color: T.muted }}>{pkg.duration} · {pkg.group_size}</span>
      </div>

      {pkg.image_url && (
        <img src={pkg.image_url} alt={pkg.title} style={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 10, marginBottom: 14 }} onError={e => { e.target.style.display = "none"; }} />
      )}

      <DGrid>
        <DRow label="Base Price" value={fmt(pkg.base_price)} />
        <DRow label="Discount" value={`${Number(pkg.discount_percent) || 0}% (${fmt(pkg.discount_amount)})`} />
        <DRow label="Selling Price" value={fmt(pkg.selling_price)} />
        <DRow label="GST" value={fmt(pkg.gst)} />
        <DRow label="Other Tax" value={fmt(pkg.other_tax)} />
        <DRow label="TCS" value={fmt(pkg.tcs)} />
        <DRow label="Service Fee" value={fmt(pkg.service_fee)} />
        <DRow label="Other Charges" value={fmt(pkg.other_charges)} />
        <DRow label="Final Price" value={fmt(pkg.final_price)} full />
        <DRow label="Pricing Basis" value={pkg.pricing_basis} />
        <DRow label="Rating" value={`${Number(pkg.rating) || 0} (${pkg.reviews || 0} reviews)`} />
        <DRow label="Booking Window" value={`${fmtDate(pkg.booking_start_date)} → ${fmtDate(pkg.booking_end_date)}`} full />
        <DRow label="Departure" value={
          pkg.departure_type === "recurring"
            ? `Every ${pkg.departure_interval_days} days from ${fmtDate(pkg.departure_start_date)}`
            : pkg.departure_type === "fixed"
              ? `Fixed dates: ${(Array.isArray(pkg.departure_dates) ? pkg.departure_dates : []).join(", ") || "—"}`
              : "Daily"
        } full />
      </DGrid>

      {pkg.description && (
        <div style={{ marginBottom: 14 }}>
          <SecTitle>Description</SecTitle>
          <p style={{ fontSize: 13, color: T.text, lineHeight: 1.7, margin: 0 }}>{pkg.description}</p>
        </div>
      )}

      {section("Highlights", pkg.highlights)}
      {section("Itinerary", (pkg.itinerary || []).map(day => `Day ${day.day}: ${day.title}`))}
      {section("Inclusions", pkg.inclusions)}
      {section("Exclusions", pkg.exclusions)}
      {section("Facilities", (pkg.facilities || []).map(f => typeof f === "string" ? f : f.name))}
      {section("Gallery Images", (pkg.gallery || []).map(img => img.alt_text ? `${img.image_url} (${img.alt_text})` : img.image_url))}

      {Array.isArray(pkg.other_taxes) && pkg.other_taxes.length > 0 && (
        <div>
          <SecTitle>Other Tax Breakdown</SecTitle>
          {pkg.other_taxes.map((tax, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 13, color: T.text, padding: "3px 0" }}>
              <span>{tax.name || "Tax"}</span>
              <span>{fmt(tax.amount)}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 18, paddingTop: 14, borderTop: `1px solid ${T.border}` }}>
        <Btn onClick={onClose} variant="ghost">Close</Btn>
        <Btn onClick={() => { onEdit(pkg); onClose(); }} variant="primary"><Edit size={13} /> Edit</Btn>
      </div>
    </Modal>
  );
}

function ConfirmDialog({ title, message, confirmLabel = "Confirm", danger = false, busy = false, onConfirm, onCancel }) {
  const T = useThemeColors();

  return (
    <div onClick={onCancel} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
      <div onClick={e => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={title} style={{ background: T.card, borderRadius: 14, width: "100%", maxWidth: 430, padding: "22px", boxShadow: "0 20px 60px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: T.ink, marginBottom: 8 }}>{title}</div>
        <div style={{ fontSize: 13, color: T.muted, lineHeight: 1.6, marginBottom: 18 }}>{message}</div>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn onClick={onCancel} variant="ghost" disabled={busy}>Cancel</Btn>
          <Btn onClick={onConfirm} variant={danger ? "danger" : "primary"} disabled={busy}>
            {busy ? "Working…" : confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
}

/* Per-package ⋯ menu — keeps every action discoverable without crowding the card. */
function PackageActionMenu({ pkg, items }) {
  const T = useThemeColors();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const close = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(v => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`More actions for ${pkg.title}`}
        title="More actions"
        style={{ padding: "7px 10px", border: `1px solid ${T.border}`, borderRadius: 7, background: T.card, fontSize: 14, cursor: "pointer", color: T.muted, lineHeight: 1 }}
      >
        ⋯
      </button>

      {open && (
        <div role="menu" style={{ position: "absolute", bottom: "110%", right: 0, background: T.card, border: `1px solid ${T.border}`, borderRadius: 10, boxShadow: "0 10px 30px rgba(0,0,0,0.15)", zIndex: 300, minWidth: 190, overflow: "hidden" }}>
          {items.map(item => (
            <button
              key={item.label}
              role="menuitem"
              onClick={() => { setOpen(false); item.onClick(); }}
              style={{ display: "block", width: "100%", textAlign: "left", padding: "9px 14px", border: "none", background: "none", fontSize: 12.5, cursor: "pointer", color: item.danger ? T.danger : T.text }}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function HolidaysSection() {
  const T = useThemeColors();
  const [packages, setPackages] = useState([]);
const [categories, setCategories] = useState([]);
const [loading, setLoading] = useState(true);
const [search, setSearch] = useState("");
const [filter, setFilter] = useState("all");
const [catFilter, setCatFilter] = useState("all");const [selected, setSelected] = useState(null); // package details modal
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [paymentPolicyPkg, setPaymentPolicyPkg] = useState(null);
  const [toast, setToast] = useState(null);

  // Confirmation dialogs: { type: "delete" | "toggle", pkg }
  const [confirm, setConfirm] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [menuBusyId, setMenuBusyId] = useState(null);

  const showToast = (msg, ok = true) => { setToast({ msg, ok }); setTimeout(() => setToast(null), 3000); };
  const load = useCallback(async () => { setLoading(true); const [pkgs, cats] = await Promise.all([apiFetch("/packages?admin=1"), apiFetch("/packages/categories")]); if (pkgs) setPackages(Array.isArray(pkgs) ? pkgs : []); else setPackages([]); if (cats) setCategories(Array.isArray(cats) ? cats : []); setLoading(false); }, []);
  useEffect(() => { load(); }, [load]);
  const handleSaved = p => { setPackages(prev => { const i = prev.findIndex(x => x.id === p.id); if (i >= 0) { const n = [...prev]; n[i] = p; return n; } return [p, ...prev]; }); showToast(editing ? "Updated!" : "Added!"); };

  const handleToggle = async p => {
    const a = p.is_active ? 0 : 1;
    setConfirmBusy(true);
    const res = await apiPatch(`/packages/${p.id}/status`, { is_active: a });
    setConfirmBusy(false);
    setConfirm(null);

    if (res.ok) {
      setPackages(prev => prev.map(x => x.id === p.id ? { ...x, is_active: a } : x));
      showToast(`Package ${a ? "activated" : "deactivated"}.`);
    } else {
      showToast(res.error || "Failed to update status.", false);
    }
  };

  const handleDelete = async p => {
    setConfirmBusy(true);
    const res = await apiDelete(`/packages/${p.id}`);
    setConfirmBusy(false);
    setConfirm(null);

    if (res.ok) {
      setPackages(prev => prev.filter(x => x.id !== p.id));
      setSelected(null);
      showToast("Package deleted.");
    } else {
      // 409 = package still has bookings; server returns the explanation.
      showToast(res.error || "Delete failed.", false);
      load(); // refresh in case the server refused or state changed
    }
  };

  const handleDuplicate = async p => {
    if (menuBusyId) return;
    setMenuBusyId(p.id);
    const res = await apiPost(`/packages/${p.id}/duplicate`, {});
    setMenuBusyId(null);

    if (res.ok && res.data?.id) {
      showToast(`Copied as “${res.data.title}” (inactive draft).`);
      await load();
      setEditing(res.data);
      setShowForm(true);
    } else {
      showToast(res.error || "Duplicate failed.", false);
    }
  };

  // admin=1 lets admins generate PDFs for inactive packages too; the server
  // still requires a valid admin token and ignores the flag for active ones.
  const pdfUrl = (p, disposition) =>
    `/packages/${p.id}/pdf?disposition=${disposition}&admin=1`;

  const handleGeneratePdf = async p => {
    if (menuBusyId) return;
    setMenuBusyId(p.id);

    // Open the tab synchronously (before any await) so popup blockers
    // allow it, then point it at the generated PDF once it is ready.
    const win = window.open("about:blank", "_blank");

    // Fetch with the admin token so inactive packages can be previewed.
    const res = await apiDownloadBlob(pdfUrl(p, "inline"));
    setMenuBusyId(null);

    if (res.ok) {
      const url = URL.createObjectURL(res.blob);
      if (win) {
        win.location = url;
        win.opener = null;
      } else {
        window.location.href = url; // popup blocked — same tab fallback
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } else {
      if (win) win.close();
      showToast(res.error || "PDF generation failed.", false);
    }
  };

  const handleDownloadPdf = async p => {
    if (menuBusyId) return;
    setMenuBusyId(p.id);
    const res = await apiDownload(pdfUrl(p, "attachment"), `${p.title.replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "holiday-package"}.pdf`);
    setMenuBusyId(null);

    if (!res.ok) showToast(res.error || "PDF download failed.", false);
  };
  const filtered = packages.filter(p => { const q = search.toLowerCase(); return (!q || p.title?.toLowerCase().includes(q) || p.location?.toLowerCase().includes(q)) && (filter === "all" || (filter === "active" ? p.is_active : !p.is_active)) && (catFilter === "all" || p.category_id === catFilter); });
  const catOpts = [
  { value: "all", label: "All Categories" },
  ...(categories.length
    ? categories
        .filter(c => String(c.id) !== "all")
        .map(c => ({
          value: c.id,
          label: c.label
        }))
    : PKG_CATS)
];
  return (
    <div>
      {toast && <div style={{ position: "fixed", top: 20, right: 24, zIndex: 2000, background: toast.ok ? T.greenSoft : T.dangerSoft, border: `1px solid ${toast.ok ? T.green : T.danger}`, color: toast.ok ? T.green : T.danger, padding: "12px 18px", borderRadius: 10, fontSize: 13, fontWeight: 500 }}>{toast.ok ? "✓" : "⚠"} {toast.msg}</div>}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search package, location…" />
        <Sel value={catFilter} onChange={setCatFilter} options={catOpts} />
        <Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "active", label: "Active" }, { value: "inactive", label: "Inactive" }]} />
        <span style={{ fontSize: 13, color: T.faint }}>{filtered.length} packages</span>
        <Btn onClick={load} variant="ghost"><RefreshCw size={14} /></Btn>
        <Btn onClick={() => { setEditing(null); setShowForm(true); }} variant="primary"><Plus size={14} />Add Package</Btn>
      </div>
      {loading ? <div style={{ textAlign: "center", padding: 60, color: T.faint }}>Loading…</div> : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 12, marginBottom: 20 }}>
            <StatCard icon={<Palmtree size={18} />} label="Total" value={packages.length} color={T.accent} />
            <StatCard icon={<CheckCircle size={18} />} label="Active" value={packages.filter(p => p.is_active).length} color={T.green} />
            <StatCard icon={<AlertTriangle size={18} />} label="Inactive" value={packages.filter(p => !p.is_active).length} color={T.warn} />
            <StatCard icon={<Star size={18} />} label="Avg Rating" value={(packages.reduce((s, p) => s + Number(p.rating || 0), 0) / Math.max(packages.length, 1)).toFixed(1)} color="#f59e0b" />
          </div>
          {filtered.length === 0 ? <div style={{ textAlign: "center", padding: 60, color: T.faint }}>No packages found.</div> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 16 }}>
              {filtered.map(pkg => (
                <div key={pkg.id} style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, overflow: "hidden", display: "flex", flexDirection: "column", opacity: pkg.is_active ? 1 : .65 }}>
                  <div style={{ position: "relative", height: 140, overflow: "hidden" }}>
                    <img src={pkg.image_url || "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&q=70"} alt={pkg.title} style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => { e.target.src = "https://images.unsplash.com/photo-1469854523086-cc02fe5d8800?w=400&q=70"; }} />
                    <span style={{ position: "absolute", top: 8, left: 8, background: T.accent, color: "#fff", fontSize: 10, fontWeight: 500, padding: "3px 9px", borderRadius: 20 }}>{pkg.badge}</span>
                    <span style={{ position: "absolute", top: 8, right: 8 }}><Badge status={pkg.is_active ? "active" : "inactive"} /></span>
                  </div>
                  <div style={{ padding: "12px 14px", flex: 1 }}>
                    <div style={{ fontSize: 11, color: T.faint, marginBottom: 2 }}>📍 {pkg.location}</div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 4 }}>{pkg.title}</div>
                    <div style={{ display: "flex", gap: 10, fontSize: 11, color: T.muted, marginBottom: 8 }}>
  <span>⏱ {pkg.duration}</span>
  <span>👥 {pkg.group_size}</span>
  <span>⭐ {pkg.rating}</span>
</div>

<div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
  {Number(pkg.discount_percent) > 0 && (
    <span
      style={{
        fontSize: 11,
        color: T.faint,
        textDecoration: "line-through"
      }}
    >
      {fmt(pkg.base_price)}
    </span>
  )}

  <span
    style={{
      fontSize: 14,
      fontWeight: 700,
      color: T.ink
    }}
  >
    {fmt(pkg.selling_price)}
  </span>

  {Number(pkg.discount_percent) > 0 && (
    <span
      style={{
        fontSize: 11,
        background: T.greenSoft,
        color: T.green,
        fontWeight: 500,
        padding: "2px 6px",
        borderRadius: 5
      }}
    >
      {Number(pkg.discount_percent)}% OFF
    </span>
  )}
</div>

<div
  style={{
    marginTop: 6,
    fontSize: 11,
    color: T.muted,
    lineHeight: 1.6
  }}
>
  <div>
  GST: {fmt(pkg.gst)} · Service Fee: {fmt(pkg.service_fee)}
</div>

  <div style={{ fontWeight: 700, color: T.ink, marginTop: 2 }}>
    Total to Pay: {fmt(pkg.final_price)}
  </div>
</div>
                  </div>
                  <div
  style={{
    display: "flex",
    gap: 6,
    padding: "10px 14px",
    borderTop: `1px solid ${T.bg}`,
    flexWrap: "wrap",
  }}
>
  <button
    onClick={() => {
      setEditing(pkg);
      setShowForm(true);
    }}
    aria-label={`Edit ${pkg.title}`}
    style={{
      flex: 1,
      padding: "7px",
      border: `1px solid ${T.border}`,
      borderRadius: 7,
      background: T.accentSoft,
      fontSize: 12,
      fontWeight: 500,
      cursor: "pointer",
      color: T.accent,
    }}
  >
    ✏ Edit
  </button>

  <button
    onClick={() => setSelected(pkg)}
    aria-label={`View ${pkg.title}`}
    title="View package details"
    style={{
      padding: "7px 10px",
      border: `1px solid ${T.border}`,
      borderRadius: 7,
      background: T.card,
      fontSize: 12,
      cursor: "pointer",
      color: T.muted,
    }}
  >
    <Eye size={14} />
  </button>

  <PackageActionMenu
    pkg={pkg}
    items={[
      { label: "⧉ Copy / Duplicate", onClick: () => handleDuplicate(pkg) },
      { label: "📄 Generate PDF", onClick: () => handleGeneratePdf(pkg) },
      { label: "⬇ Download PDF", onClick: () => handleDownloadPdf(pkg) },
      { label: "₹ Payment Policy", onClick: () => setPaymentPolicyPkg(pkg) },
      {
        label: pkg.is_active ? "⏸ Deactivate" : "▶ Activate",
        onClick: () => setConfirm({ type: "toggle", pkg }),
      },
      { label: "🗑 Delete", danger: true, onClick: () => setConfirm({ type: "delete", pkg }) },
    ]}
  />
</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      {selected && (
        <PackageDetailsModal
          pkg={selected}
          onClose={() => setSelected(null)}
          onEdit={p => { setEditing(p); setShowForm(true); }}
        />
      )}

      {confirm && (
        <ConfirmDialog
          title={confirm.type === "delete" ? "Delete package?" : confirm.pkg.is_active ? "Deactivate package?" : "Activate package?"}
          message={
            confirm.type === "delete"
              ? `Type-confirmed deletion of “${confirm.pkg.title}”. This removes the package and its itinerary, inclusions, highlights, facilities and gallery. Packages with existing bookings cannot be deleted — cancel first or deactivate instead. This cannot be undone.`
              : confirm.pkg.is_active
                ? `“${confirm.pkg.title}” will be hidden from the customer website and cannot be newly booked. Existing bookings and payments are unaffected.`
                : `“${confirm.pkg.title}” will be published and visible to customers again.`
          }
          confirmLabel={confirm.type === "delete" ? "Delete" : confirm.pkg.is_active ? "Deactivate" : "Activate"}
          danger={confirm.type === "delete" || confirm.pkg.is_active}
          busy={confirmBusy}
          onConfirm={() => confirm.type === "delete" ? handleDelete(confirm.pkg) : handleToggle(confirm.pkg)}
          onCancel={() => !confirmBusy && setConfirm(null)}
        />
      )}

      {showForm && <PackageFormModal pkg={editing} categories={categories} onClose={() => { setShowForm(false); setEditing(null); }} onSaved={handleSaved} />}
        {paymentPolicyPkg && (
  <HolidayPaymentPolicyModal
    pkg={paymentPolicyPkg}
    onClose={() => setPaymentPolicyPkg(null)}
    onSaved={() => {
      showToast("Payment policy saved.");
      setPaymentPolicyPkg(null);
      load();
    }}
  />
)}
    </div>
  );
}

function CouponsSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState("");

  const emptyForm = {
    code: "",
    name: "",
    description: "",
    discount_type: "percentage",
    discount_value: "",
    max_discount_amount: "",
    minimum_booking_amount: "0",
    start_at: "",
    end_at: "",
    usage_limit: "",
    usage_per_user: "1",
    applicable_to: "all",
    applicable_value: "",
    new_user_only: false,
    combinable: true,
    is_active: true,
  };

  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      const data = await apiFetch("/coupons");
      setCoupons(Array.isArray(data) ? data : data?.coupons || []);
    } catch (err) {
      setToast(err.message || "Failed to load coupons");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (coupon) => {
    setEditing(coupon);
    setForm({
      code: coupon.code || "",
      name: coupon.name || "",
      description: coupon.description || "",
      discount_type: coupon.discount_type || "percentage",
      discount_value: coupon.discount_value ?? "",
      max_discount_amount: coupon.max_discount_amount ?? "",
      minimum_booking_amount: coupon.minimum_booking_amount ?? "0",
      start_at: coupon.start_at
        ? String(coupon.start_at).slice(0, 16)
        : "",
      end_at: coupon.end_at
        ? String(coupon.end_at).slice(0, 16)
        : "",
      usage_limit: coupon.usage_limit ?? "",
      usage_per_user: coupon.usage_per_user ?? "1",
      applicable_to: coupon.applicable_to || "all",
      applicable_value: coupon.applicable_value || "",
      new_user_only: Boolean(coupon.new_user_only),
      combinable: coupon.combinable !== false,
      is_active: coupon.is_active !== false,
    });
    setShowForm(true);
  };

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setToast("Code and name are required.");
      return;
    }

    if (
      form.discount_value === "" ||
      Number(form.discount_value) <= 0
    ) {
      setToast("Discount value must be greater than 0.");
      return;
    }

    if (!form.start_at || !form.end_at) {
      setToast("Start and end date are required.");
      return;
    }

    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      discount_value: Number(form.discount_value),
      max_discount_amount:
        form.max_discount_amount === ""
          ? null
          : Number(form.max_discount_amount),
      minimum_booking_amount:
        form.minimum_booking_amount === ""
          ? 0
          : Number(form.minimum_booking_amount),
      usage_limit:
        form.usage_limit === ""
          ? null
          : Number(form.usage_limit),
      usage_per_user:
        form.usage_per_user === ""
          ? null
          : Number(form.usage_per_user),
      applicable_value:
        form.applicable_to === "all"
          ? null
          : form.applicable_value.trim(),
      new_user_only: Boolean(form.new_user_only),
      combinable: Boolean(form.combinable),
      is_active: Boolean(form.is_active),
    };

    try {
      if (editing?.id) {
        await apiFetch(`/coupons/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        setToast("Coupon updated successfully.");
      } else {
        await apiFetch("/coupons", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        setToast("Coupon created successfully.");
      }

      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setToast(err.message || "Failed to save coupon");
    }
  };

  const toggleActive = async (coupon) => {
    try {
      await apiFetch(`/coupons/${coupon.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          is_active: !coupon.is_active,
        }),
      });

      setToast(
        coupon.is_active
          ? "Coupon deactivated."
          : "Coupon activated."
      );

      await load();
    } catch (err) {
      setToast(err.message || "Failed to update coupon");
    }
  };

  const filtered = coupons.filter((coupon) => {
    const q = search.trim().toLowerCase();

    if (!q) return true;

    return (
      String(coupon.code || "").toLowerCase().includes(q) ||
      String(coupon.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: T.text }}>
            Coupons
          </h2>
          <p
            style={{
              margin: "6px 0 0",
              color: T.muted,
            }}
          >
            Create and manage holiday booking discount coupons.
          </p>
        </div>

        <Btn onClick={openCreate}>
          <Plus size={16} />
          Add Coupon
        </Btn>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="Total Coupons"
          value={coupons.length}
          icon={<Tag size={18} />}
        />
        <StatCard
          label="Active"
          value={coupons.filter((c) => c.is_active).length}
          icon={<CheckCircle size={18} />}
        />
        <StatCard
          label="Inactive"
          value={coupons.filter((c) => !c.is_active).length}
          icon={<Ban size={18} />}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search coupons by code or name..."
        />
      </div>

      {loading ? (
        <div style={{ color: T.muted }}>Loading coupons...</div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            color: T.muted,
          }}
        >
          No coupons found.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((coupon) => (
            <div
              key={coupon.id}
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 16,
                padding: 18,
                background: T.card,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div>
                  <strong
                    style={{
                      color: T.text,
                      fontSize: 17,
                    }}
                  >
                    {coupon.code}
                  </strong>

                  <div
                    style={{
                      color: T.muted,
                      marginTop: 4,
                    }}
                  >
                    {coupon.name}
                  </div>
                </div>

                <Badge
                  tone={coupon.is_active ? "success" : "muted"}
                >
                  {coupon.is_active ? "Active" : "Inactive"}
                </Badge>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  color: T.muted,
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                <div>
                  Discount:{" "}
                  <strong style={{ color: T.text }}>
                    {coupon.discount_type === "percentage"
                      ? `${coupon.discount_value}%`
                      : `₹${coupon.discount_value}`}
                  </strong>
                </div>

                <div>
                  Minimum booking: ₹
                  {Number(
                    coupon.minimum_booking_amount || 0
                  ).toLocaleString("en-IN")}
                </div>

                <div>
                  Usage:{" "}
                  {coupon.usage_count ?? 0}
                  {coupon.usage_limit
                    ? ` / ${coupon.usage_limit}`
                    : " / Unlimited"}
                </div>

                <div>
                  Applicable to:{" "}
                  {coupon.applicable_to || "all"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Btn
                  variant="secondary"
                  onClick={() => openEdit(coupon)}
                >
                  <Edit size={14} />
                  Edit
                </Btn>

                <Btn
                  variant="secondary"
                  onClick={() => toggleActive(coupon)}
                >
                  {coupon.is_active ? (
                    <>
                      <Ban size={14} />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} />
                      Activate
                    </>
                  )}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}

      {showForm && (
  <Modal
    onClose={() => setShowForm(false)}
    title={editing ? "Edit Coupon" : "Create Coupon"}
    maxWidth={760}
  >
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
        gap: 14,
        maxHeight: "70vh",
        overflowY: "auto",
        paddingRight: 4,
      }}
    >
      {/* KEEP YOUR EXISTING COUPON FORM CONTENT HERE */}
    </div>

    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 10,
        marginTop: 22,
      }}
    >
      <Btn
        variant="secondary"
        onClick={() => setShowForm(false)}
      >
        Cancel
      </Btn>

      <Btn onClick={save}>
        {editing ? "Update Coupon" : "Create Coupon"}
      </Btn>
    </div>
  </Modal>
)}

      {toast && (
        <div
          style={{
            position: "fixed",
            right: 20,
            bottom: 20,
            zIndex: 9999,
            padding: "12px 16px",
            borderRadius: 10,
            background: T.card,
            border: `1px solid ${T.border}`,
            color: T.text,
            boxShadow: "0 10px 30px rgba(0,0,0,.15)",
          }}
          onClick={() => setToast("")}
        >
          {toast}
        </div>
      )}
    </div>
  );
}

function GiftCardsSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState("");

  const emptyForm = {
    code: "",
    name: "",
    description: "",
    initial_value: "",
    minimum_booking_amount: "0",
    maximum_redemption_amount: "",
    start_at: "",
    expires_at: "",
    usage_type: "multiple",
    max_redemptions: "",
    applicable_to: "all",
    applicable_value: "",
    allow_partial_redemption: true,
    allow_coupon_combination: true,
    recipient_name: "",
    recipient_email: "",
    recipient_phone: "",
    personal_message: "",
    is_active: true,
  };

  const [form, setForm] = useState(emptyForm);

  const load = async () => {
    try {
      setLoading(true);
      const data = await apiFetch("/gift-cards");
      setGiftCards(
        Array.isArray(data)
          ? data
          : data?.giftCards || data?.gift_cards || []
      );
    } catch (err) {
      setToast(err.message || "Failed to load gift cards");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (giftCard) => {
    setEditing(giftCard);

    setForm({
      code: giftCard.code || "",
      name: giftCard.name || "",
      description: giftCard.description || "",
      initial_value: giftCard.initial_value ?? "",
      minimum_booking_amount:
        giftCard.minimum_booking_amount ?? "0",
      maximum_redemption_amount:
        giftCard.maximum_redemption_amount ?? "",
      start_at: giftCard.start_at
        ? String(giftCard.start_at).slice(0, 16)
        : "",
      expires_at: giftCard.expires_at
        ? String(giftCard.expires_at).slice(0, 16)
        : "",
      usage_type: giftCard.usage_type || "multiple",
      max_redemptions: giftCard.max_redemptions ?? "",
      applicable_to: giftCard.applicable_to || "all",
      applicable_value: giftCard.applicable_value || "",
      allow_partial_redemption:
        giftCard.allow_partial_redemption !== false,
      allow_coupon_combination:
        giftCard.allow_coupon_combination !== false,
      recipient_name: giftCard.recipient_name || "",
      recipient_email: giftCard.recipient_email || "",
      recipient_phone: giftCard.recipient_phone || "",
      personal_message: giftCard.personal_message || "",
      is_active: giftCard.is_active !== false,
    });

    setShowForm(true);
  };

  const updateField = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const save = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setToast("Code and name are required.");
      return;
    }

    if (
      form.initial_value === "" ||
      Number(form.initial_value) <= 0
    ) {
      setToast("Initial value must be greater than 0.");
      return;
    }

    if (!form.start_at || !form.expires_at) {
      setToast("Start and expiry date are required.");
      return;
    }

    const payload = {
      ...form,
      code: form.code.trim().toUpperCase(),
      name: form.name.trim(),
      initial_value: Number(form.initial_value),
      minimum_booking_amount:
        form.minimum_booking_amount === ""
          ? 0
          : Number(form.minimum_booking_amount),
      maximum_redemption_amount:
        form.maximum_redemption_amount === ""
          ? null
          : Number(form.maximum_redemption_amount),
      max_redemptions:
        form.max_redemptions === ""
          ? null
          : Number(form.max_redemptions),
      applicable_value:
        form.applicable_to === "all"
          ? null
          : form.applicable_value.trim(),
      allow_partial_redemption:
        Boolean(form.allow_partial_redemption),
      allow_coupon_combination:
        Boolean(form.allow_coupon_combination),
      is_active: Boolean(form.is_active),
    };

    // The backend intentionally does not update initial_value
    // for an existing gift card.
    if (editing?.id) {
      delete payload.initial_value;
    }

    try {
      if (editing?.id) {
        await apiFetch(`/gift-cards/${editing.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        setToast("Gift card updated successfully.");
      } else {
        await apiFetch("/gift-cards", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });

        setToast("Gift card created successfully.");
      }

      setShowForm(false);
      setEditing(null);
      setForm(emptyForm);
      await load();
    } catch (err) {
      setToast(err.message || "Failed to save gift card");
    }
  };

  const toggleActive = async (giftCard) => {
    try {
      await apiFetch(`/gift-cards/${giftCard.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          is_active: !giftCard.is_active,
        }),
      });

      setToast(
        giftCard.is_active
          ? "Gift card deactivated."
          : "Gift card activated."
      );

      await load();
    } catch (err) {
      setToast(err.message || "Failed to update gift card");
    }
  };

  const filtered = giftCards.filter((giftCard) => {
    const q = search.trim().toLowerCase();

    if (!q) return true;

    return (
      String(giftCard.code || "").toLowerCase().includes(q) ||
      String(giftCard.name || "").toLowerCase().includes(q)
    );
  });

  return (
    <div style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 16,
          marginBottom: 20,
          flexWrap: "wrap",
        }}
      >
        <div>
          <h2 style={{ margin: 0, color: T.text }}>
            Gift Cards
          </h2>
          <p
            style={{
              margin: "6px 0 0",
              color: T.muted,
            }}
          >
            Create and manage holiday booking gift cards.
          </p>
        </div>

        <Btn onClick={openCreate}>
          <Plus size={16} />
          Add Gift Card
        </Btn>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          gap: 14,
          marginBottom: 20,
        }}
      >
        <StatCard
          label="Total Gift Cards"
          value={giftCards.length}
          icon={<IndianRupee size={18} />}
        />

        <StatCard
          label="Active"
          value={giftCards.filter((g) => g.is_active).length}
          icon={<CheckCircle size={18} />}
        />

        <StatCard
          label="Inactive"
          value={giftCards.filter((g) => !g.is_active).length}
          icon={<Ban size={18} />}
        />
      </div>

      <div style={{ marginBottom: 18 }}>
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search gift cards by code or name..."
        />
      </div>

      {loading ? (
        <div style={{ color: T.muted }}>
          Loading gift cards...
        </div>
      ) : filtered.length === 0 ? (
        <div
          style={{
            padding: 32,
            textAlign: "center",
            border: `1px solid ${T.border}`,
            borderRadius: 14,
            color: T.muted,
          }}
        >
          No gift cards found.
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fill, minmax(300px, 1fr))",
            gap: 16,
          }}
        >
          {filtered.map((giftCard) => (
            <div
              key={giftCard.id}
              style={{
                border: `1px solid ${T.border}`,
                borderRadius: 16,
                padding: 18,
                background: T.card,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 10,
                  marginBottom: 12,
                }}
              >
                <div>
                  <strong
                    style={{
                      color: T.text,
                      fontSize: 17,
                    }}
                  >
                    {giftCard.code}
                  </strong>

                  <div
                    style={{
                      color: T.muted,
                      marginTop: 4,
                    }}
                  >
                    {giftCard.name}
                  </div>
                </div>

                <Badge
                  tone={
                    giftCard.is_active
                      ? "success"
                      : "muted"
                  }
                >
                  {giftCard.is_active
                    ? "Active"
                    : "Inactive"}
                </Badge>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 8,
                  color: T.muted,
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                <div>
                  Initial Value:{" "}
                  <strong style={{ color: T.text }}>
                    ₹
                    {Number(
                      giftCard.initial_value || 0
                    ).toLocaleString("en-IN")}
                  </strong>
                </div>

                <div>
                  Current Balance: ₹
                  {Number(
                    giftCard.current_balance || 0
                  ).toLocaleString("en-IN")}
                </div>

                <div>
                  Minimum booking: ₹
                  {Number(
                    giftCard.minimum_booking_amount || 0
                  ).toLocaleString("en-IN")}
                </div>

                <div>
                  Usage:{" "}
                  {giftCard.usage_count ?? 0}
                  {giftCard.max_redemptions
                    ? ` / ${giftCard.max_redemptions}`
                    : " / Unlimited"}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                }}
              >
                <Btn
                  variant="secondary"
                  onClick={() => openEdit(giftCard)}
                >
                  <Edit size={14} />
                  Edit
                </Btn>

                <Btn
                  variant="secondary"
                  onClick={() =>
                    toggleActive(giftCard)
                  }
                >
                  {giftCard.is_active ? (
                    <>
                      <Ban size={14} />
                      Deactivate
                    </>
                  ) : (
                    <>
                      <CheckCircle size={14} />
                      Activate
                    </>
                  )}
                </Btn>
              </div>
            </div>
          ))}
        </div>
      )}
{showForm && (
  <Modal
    onClose={() => setShowForm(false)}
    title={editing ? "Edit Gift Card" : "Create Gift Card"}
    maxWidth={820}
  >
    {/* KEEP ALL YOUR EXISTING GIFT CARD FORM CONTENT HERE */}

    {toast && (
      <div
        style={{
          position: "fixed",
          right: 20,
          bottom: 20,
          zIndex: 9999,
          padding: "12px 16px",
          borderRadius: 10,
          background: T.card,
          border: `1px solid ${T.border}`,
          color: T.text,
          boxShadow: "0 10px 30px rgba(0,0,0,.15)",
        }}
        onClick={() => setToast("")}
      >
        {toast}
      </div>
    )}
  </Modal>
)}

    </div>
  );
}
/* ══════════════════════════════════════════════════════════════════════
   FLIGHTS / HOTELS / BUSES / TRAINS / VISA / CONTACT / HOLIDAY BOOKINGS
══════════════════════════════════════════════════════════════════════ */

const isRoundTripBooking = (booking) => {
  if (!booking) return false;

  // Best source: stored return date
  if (booking.return_date) return true;

  // Fallback for special round-trip bookings where return_date
  // may not have been stored correctly but itinerary_legs exists.
  try {
    const legs =
      typeof booking.itinerary_legs === "string"
        ? JSON.parse(booking.itinerary_legs)
        : booking.itinerary_legs;

    if (Array.isArray(legs) && legs.length > 1) {
      return true;
    }

    if (
      Array.isArray(legs) &&
      legs.some(
        (leg) =>
          leg?.returnFlight === true ||
          String(leg?.direction || "").toLowerCase() === "return"
      )
    ) {
      return true;
    }
  } catch {
    // Ignore invalid itinerary JSON and use the normal one-way display.
  }

  return false;
};


/*
 * Multi City: one booking containing 2..5 legs stored in itinerary_legs.
 * These helpers let FlightModal render every leg using the existing layout.
 */
const isMultiCityBooking = (booking) =>
  String(booking?.trip_type || "").toLowerCase() === "multi_city";

const getModalItineraryLegs = (booking) => {
  try {
    const legs =
      typeof booking.itinerary_legs === "string"
        ? JSON.parse(booking.itinerary_legs)
        : booking.itinerary_legs;

    return Array.isArray(legs) ? legs : [];
  } catch {
    return [];
  }
};

const getModalLegRoute = (leg) => {
  if (!leg) return null;

  const from =
    leg.departCode ||
    leg.depart_code ||
    leg.origin ||
    leg.Origin ||
    null;

  const to =
    leg.arriveCode ||
    leg.arrive_code ||
    leg.destination ||
    leg.Destination ||
    null;

  return from && to ? `${from} → ${to}` : null;
};

const getModalLegDate = (leg) => {
  if (!leg) return null;

  return (
    leg.travelDate ||
    leg.travel_date ||
    leg.departureDate ||
    leg.departure_date ||
    leg.departureDateTime ||
    null
  );
};

function FlightModal({ booking, onClose, onStatusChange }) {
    const isRoundTrip = isRoundTripBooking(booking);
  const isMultiCity = isMultiCityBooking(booking);
  const legsForModal = isMultiCity ? getModalItineraryLegs(booking) : [];

  // Multi City is stored with return_date (last leg) so isRoundTripBooking
  // is true for it — but the modify form is a single requested-date form,
  // same as One Way. Only genuine Round Trip needs a return date.
  const isRoundTripModify = isRoundTripBooking(booking) && !isMultiCity;
  const T = useThemeColors();

  const [status, setStatus] = useState(booking.status);
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState(null); // tracks which download is in progress: "eticket" | "invoice" | null
const [modifyDate, setModifyDate] = useState("");
const [modifyReturnDate, setModifyReturnDate] = useState("");
const [modifyTime, setModifyTime] = useState("");
const [modifyRemarks, setModifyRemarks] = useState("");
const [modifySaving, setModifySaving] = useState(false);

const handleModifyRequest = async () => {
  if (!modifyDate) {
    alert("Please select the requested travel date.");
    return;
  }

  if (isRoundTripModify && !modifyReturnDate) {
    alert("Please select the requested return date.");
    return;
  }

  if (
    isRoundTripModify &&
    modifyReturnDate &&
    new Date(modifyReturnDate) <= new Date(modifyDate)
  ) {
    alert("Return date must be after the outbound date.");
    return;
  }

  setModifySaving(true);

  try {
    const res = await apiPost(
      `/flight-bookings/${booking.id}/admin-reschedule-request`,
      {
  requestedDate: modifyDate,
  requestedReturnDate: isRoundTripModify
    ? modifyReturnDate
    : null,
  requestedTime: modifyTime,
  remarks: modifyRemarks,
}
    );

    if (!res.ok) {
      alert(`Failed to submit request: ${res.error}`);
      return;
    }

    alert(
      "Modification request submitted. The team will confirm availability and charges."
    );

    setModifyDate("");
setModifyReturnDate("");
setModifyTime("");
setModifyRemarks("");
  } finally {
    setModifySaving(false);
  }
};


  const handle = async s => {
    setSaving(true);
    const res = await apiPatch(`/flight-bookings/${booking.id}?admin=1`, { status: s });
    if (res.ok) { setStatus(s); onStatusChange(booking.id, s); }
    setSaving(false);
  };

  // Ticket/invoice only exist once a booking is upcoming or completed
  const canDownload = ["upcoming", "completed"].includes(status);

  const handleDownload = async (type) => {
    setDl(type);
    const res = await apiDownload(
      `/flight-bookings/${booking.id}/${type}?admin=1`,
      `${type}-${booking.booking_reference}.pdf`
    );
    if (!res.ok) alert(res.error);
    setDl(null);
  };

  return (
    <Modal onClose={onClose} title="Flight Booking" subtitle={booking.booking_reference}>
      <DGrid>
        <DRow label="Passenger" value={booking.passenger_name} />
        <DRow label="Email" value={booking.passenger_email} />
        <DRow label="Airline" value={booking.airline} />
        <DRow label="Flight No." value={booking.flight_numbers} />        <DRow
  label="Route"
  value={
    isMultiCity ? (
      legsForModal.map((leg, i) => (
        <div key={`mc-route-${i}`}>
          Leg {i + 1}: {getModalLegRoute(leg)}
        </div>
      ))
    ) : isRoundTrip ? (
      <>
        <div>
          Outbound: {booking.depart_code} → {booking.arrive_code}
        </div>
        <div>
          Return: {booking.arrive_code} → {booking.depart_code}
        </div>
      </>
    ) : (
      `${booking.depart_code} → ${booking.arrive_code}`
    )
  }
  full={isRoundTrip || isMultiCity}
/>
        {isMultiCity ? (
  <DRow
    label="Travel Dates"
    value={
      legsForModal.length
        ? legsForModal.map((leg, i) => (
            <div key={`mc-date-${i}`}>
              Leg {i + 1}: {fmtDate(getModalLegDate(leg))}
            </div>
          ))
        : fmtDate(booking.travel_date)
    }
    full
  />
) : isRoundTrip ? (
  <DRow
    label="Travel Dates"
    value={
      <>
        <div>Outbound: {fmtDate(booking.travel_date)}</div>
        <div>Return: {fmtDate(booking.return_date)}</div>
      </>
    }
    full
  />
) : (
  <DRow
    label="Date"
    value={fmtDate(booking.travel_date)}
  />
)}
        <DRow label="Class" value={booking.class_type} />
        <DRow label="Total" value={fmt(booking.total_price)} />
      </DGrid>

      <SecTitle>Documents</SecTitle>
      <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
        <Btn onClick={() => handleDownload("eticket")} variant="ghost">
          <Download size={14} />{dl === "eticket" ? "Downloading…" : "Download Ticket"}
        </Btn>
        <Btn onClick={() => handleDownload("invoice")} variant="ghost">
          <Download size={14} />{dl === "invoice" ? "Downloading…" : "Download Invoice"}
        </Btn>
      </div>
      {!canDownload && (
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
          Documents available once the ticket has been issued (status: upcoming/completed).
        </div>
      )}

      <SecTitle>Modify Booking</SecTitle>

<div
  style={{
    background: T.accentSoft,
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 14,
    fontSize: 12,
    color: T.accent,
  }}
>
  Submit a modification request to the support team. The existing
  booking will not be changed until the team confirms the request.
</div>

<DGrid>
  {isMultiCity ? (
  <DRow
    label="Current Travel Dates"
    full
    value={
      legsForModal.length
        ? legsForModal.map((leg, i) => (
            <div key={`mc-cur-date-${i}`}>
              <strong>Leg {i + 1}:</strong>{" "}
              {fmtDate(getModalLegDate(leg))}
            </div>
          ))
        : fmtDate(booking.travel_date)
    }
  />
) : isRoundTripBooking(booking) ? (
  <DRow
    label="Current Travel Dates"
    full
    value={
      <>
        <div>
          <strong>Outbound:</strong>{" "}
          {fmtDate(booking.travel_date)}
        </div>
        <div style={{ marginTop: 4 }}>
          <strong>Return:</strong>{" "}
          {fmtDate(booking.return_date)}
        </div>
      </>
    }
  />
) : (
  <DRow
    label="Current Travel Date"
    value={fmtDate(booking.travel_date)}
  />
)}

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
  {isRoundTripModify
    ? "Requested Outbound Date"
    : "Requested Travel Date"}
</span>

    <input
      type="date"
      value={modifyDate}
      onChange={(e) => setModifyDate(e.target.value)}
      min={new Date().toISOString().split("T")[0]}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
      }}
    />
    </label>

  {isRoundTripModify && (
    <label style={{ display: "grid", gap: 6 }}>
      <span style={{ fontSize: 12, color: T.muted }}>
        Requested Return Date
      </span>

      <input
        type="date"
        value={modifyReturnDate}
        onChange={(e) => setModifyReturnDate(e.target.value)}
        min={
          modifyDate ||
          new Date().toISOString().split("T")[0]
        }
        style={{
          width: "100%",
          padding: "9px 10px",
          border: `1px solid ${T.border}`,
          borderRadius: 8,
          background: T.card,
          color: T.ink,
        }}
      />
    </label>
  )}

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
      Requested Time
    </span>

    <input
      type="time"
      value={modifyTime}
      onChange={(e) => setModifyTime(e.target.value)}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
      }}
    />
  </label>

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
      Remarks
    </span>

    <textarea
      value={modifyRemarks}
      onChange={(e) => setModifyRemarks(e.target.value)}
      placeholder="Enter modification request details..."
      rows={3}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
        resize: "vertical",
        fontFamily: "inherit",
      }}
    />
  </label>
</DGrid>

<Btn
  onClick={handleModifyRequest}
  disabled={
  modifySaving ||
  !modifyDate ||
  (isRoundTripModify && !modifyReturnDate)
}
  variant="primary"
>
  {modifySaving
    ? "Submitting..."
    : "Submit Modification Request"}
</Btn>

      <SecTitle>Update Status</SecTitle>
      <StatusActions
  statuses={["upcoming", "completed"]}
  current={status}
  onSelect={handle}
  saving={saving}
/>
    </Modal>
  );
}

function FlightBookingCard({ booking, onView }) {
  const T = useThemeColors();

  const pnrs = getPnrDisplay(booking);
  const connecting = isConnectingFlight(booking);
  const travel = getTravelDisplay(booking);

  const tripType = formatTripType(booking.trip_type);
  const travelType = formatTravelType(booking.travel_type);

  return (
    <div
      style={{
        width: "100%",
        background: T.card,
        border: `1px solid ${T.border}`,
        borderRadius: 12,
        overflow: "hidden",
        boxShadow: "0 2px 8px rgba(15, 23, 42, 0.04)",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "13px 16px",
          borderBottom: `1px solid ${T.border}`,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontFamily: "monospace",
              fontSize: 13,
              fontWeight: 700,
              color: T.ink,
              whiteSpace: "nowrap",
            }}
          >
            {booking.booking_reference || "—"}
          </div>

          <div
            style={{
              marginTop: 3,
              fontSize: 11,
              color: T.faint,
            }}
          >
            Booked {fmtDate(booking.created_at)}
          </div>
        </div>

        <Badge status={booking.status} />
      </div>

      {/* Booking content */}
      <div
        style={{
          padding: "14px 16px",
          display: "grid",
          gridTemplateColumns: "minmax(220px, 1.3fr) minmax(300px, 2fr)",
          gap: 18,
          alignItems: "start",
        }}
      >
        {/* Journey */}
        <div
          style={{
            minWidth: 0,
            paddingRight: 4,
          }}
        >
          <div
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.7px",
              color: T.faint,
              marginBottom: 7,
            }}
          >
            Travel
          </div>

          {/* Outbound */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <div
              style={{
                fontSize: 13,
                fontWeight: 700,
                color: T.ink,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {travel.outboundRoute || "—"}
            </div>

            <div
              style={{
                fontSize: 11,
                color: T.faint,
              }}
            >
              Outbound: {fmtDate(travel.outboundDate)}
            </div>
          </div>

          {/* Return */}
          {travel.returnRoute && travel.returnDate && (
            <div
              style={{
                marginTop: 9,
                paddingTop: 8,
                borderTop: `1px solid ${T.border}`,
              }}
            >
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 700,
                  color: T.ink,
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {travel.returnRoute}
              </div>

              <div
                style={{
                  marginTop: 2,
                  fontSize: 11,
                  color: T.faint,
                }}
              >
                Return: {fmtDate(travel.returnDate)}
              </div>
            </div>
          )}
        </div>

        {/* Booking information */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(4, minmax(90px, 1fr))",
            gap: "12px 16px",
            minWidth: 0,
          }}
        >
          {/* Airline */}
          <div style={{ minWidth: 0 }}>
            <div
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              Airline
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {booking.airline || "—"}
            </div>

            {booking.flight_numbers && (
              <div
                style={{
                  marginTop: 2,
                  fontSize: 10,
                  color: T.faint,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {booking.flight_numbers}
              </div>
            )}
          </div>

          {/* Class */}
          <div>
            <div
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              Class
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {booking.class_type || "—"}
            </div>
          </div>

          {/* Passengers */}
          <div>
            <div
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              Passengers
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {booking.num_passengers ?? 0}
            </div>
          </div>

          {/* Travel Type */}
          <div>
            <div
              style={{
                fontSize: 10,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              Travel Type
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {travelType}
            </div>
          </div>

          {/* PNR */}
          <div
            style={{
              minWidth: 0,
              gridColumn: "span 2",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              PNR
            </div>

            {pnrs.length ? (
              <div
                style={{
                  marginTop: 3,
                  display: "flex",
                  flexDirection: "column",
                  gap: 3,
                  fontFamily: "monospace",
                  fontSize: 11,
                  color: T.ink,
                }}
              >
                {pnrs.map((item, index) => (
                  <div
                    key={`${item?.pnr || item}-${index}`}
                    style={{
                      fontWeight: 600,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {typeof item === "object"
                      ? `${item.direction}: ${item.pnr}`
                      : item}
                  </div>
                ))}
              </div>
            ) : (
              <div
                style={{
                  marginTop: 3,
                  fontSize: 11,
                  color: T.faint,
                }}
              >
                —
              </div>
            )}
          </div>

          {/* Trip Type */}
          <div
            style={{
              minWidth: 0,
              gridColumn: "span 2",
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
                color: T.faint,
              }}
            >
              Trip Type
            </div>

            <div
              style={{
                marginTop: 3,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {tripType}
            </div>

            {connecting && (
              <div
                style={{
                  marginTop: 2,
                  fontSize: 10,
                  color: T.faint,
                }}
              >
                Connecting Flight
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          padding: "11px 16px",
          borderTop: `1px solid ${T.border}`,
          background: T.accentSoft,
          flexWrap: "wrap",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 22,
            flexWrap: "wrap",
          }}
        >
          <div>
            <div
              style={{
                fontSize: 10,
                color: T.faint,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              Payment
            </div>

            <div
              style={{
                marginTop: 2,
                fontSize: 12,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {formatPaymentStatus(booking.payment_status)}
            </div>
          </div>

          <div>
            <div
              style={{
                fontSize: 10,
                color: T.faint,
                textTransform: "uppercase",
                letterSpacing: "0.6px",
              }}
            >
              Amount
            </div>

            <div
              style={{
                marginTop: 2,
                fontSize: 13,
                fontWeight: 700,
                color: T.ink,
              }}
            >
              {fmt(booking.total_price)}
            </div>
          </div>
        </div>

        <ViewBtn
          onClick={() => onView(booking)}
        />
      </div>
    </div>
  );
}

const formatTripType = (tripType) => {
  switch (tripType) {
    case "round_trip":
      return "Round Trip";

    case "special_round_trip":
      return "Special Round Trip";

    case "one_way":
      return "One Way";

    default:
      return "—";
  }
};

const formatTravelType = (travelType) => {
  if (
    travelType === 0 ||
    String(travelType).toLowerCase() === "domestic"
  ) {
    return "Domestic";
  }

  if (
    travelType === 1 ||
    String(travelType).toLowerCase() === "international"
  ) {
    return "International";
  }

  return "—";
};

const getPnrDisplay = (booking) => {
  const tripType = String(booking.trip_type || "").toLowerCase();

  /*
   * Special Round Trip is one complete supplier booking.
   * It has ONE Airline PNR for the complete journey.
   */
  if (tripType === "special_round_trip") {
    const pnr =
      booking.airline_pnr ||
      booking.record_locator ||
      booking.crs_pnr ||
      null;

    return pnr ? [pnr] : [];
  }

  /*
   * Normal Round Trip / One Way.
   * Use all available stored PNR values and remove duplicates.
   */
  const pnrs = [
    booking.airline_pnr,
    booking.record_locator,
    booking.crs_pnr,
  ].filter(Boolean);

  return [...new Set(pnrs)];
};

const isConnectingFlight = (booking) => {
  const tripType = String(booking.trip_type || "").toLowerCase();

  /*
   * Special Round Trip itself is NOT automatically a connecting flight.
   * It is considered connecting only when the actual flight data
   * contains multiple segments or stops.
   */
  if (tripType === "special_round_trip") {
    return Number(booking.stops || 0) > 0;
  }

  /*
   * Multi City legs are separate journeys (DEL→NMI, NMI→BLR, …), not
   * connections inside a single journey. booking.stops stores the
   * intermediate stops of the whole itinerary, so never show the chip.
   */
  if (tripType === "multi_city") {
    return false;
  }

  if (Number(booking.stops || 0) > 0) {
    return true;
  }

  const flights = String(booking.flight_numbers || "")
    .split(/[+,]/)
    .map((v) => v.trim())
    .filter(Boolean);

  return flights.length > 1;
};

const getTravelDisplay = (booking) => {
  let legs = [];

  try {
    legs = booking.itinerary_legs
      ? JSON.parse(booking.itinerary_legs)
      : [];
  } catch {
    legs = [];
  }

  if (!Array.isArray(legs)) {
    legs = [];
  }

  const outbound =
    legs.find(
      (leg) =>
        String(leg?.direction || "").toLowerCase() === "outbound"
    ) || legs[0];

  const returnLeg =
    legs.find(
      (leg) =>
        String(leg?.direction || "").toLowerCase() === "return"
    ) || null;

  const getRoute = (leg) => {
    if (!leg) return null;

    const from =
      leg.departCode ||
      leg.origin ||
      leg.depart_code ||
      null;

    const to =
      leg.arriveCode ||
      leg.destination ||
      leg.arrive_code ||
      null;

    return from && to ? `${from} → ${to}` : null;
  };

  return {
    outboundRoute:
      getRoute(outbound) ||
      `${booking.depart_code || "—"} → ${booking.arrive_code || "—"}`,

    returnRoute: getRoute(returnLeg),

    outboundDate:
      outbound?.travelDate ||
      outbound?.departureDate ||
      booking.travel_date ||
      null,

    returnDate:
      returnLeg?.travelDate ||
      returnLeg?.departureDate ||
      booking.return_date ||
      null,
  };
};

const formatPaymentStatus = (status) => {
  if (!status) return "—";

  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

function FlightsSection({ data, onStatusChange }) {
  const T = useThemeColors();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  // Controls whether flight bookings are displayed as a table or cards.
  const [viewMode, setViewMode] = useState("table");

  const filtered = data.filter((b) => {
    const q = search.toLowerCase().trim();

    const searchable = [
      b.booking_reference,
      b.airline,
      b.depart_code,
      b.arrive_code,
      b.airline_pnr,
      b.record_locator,
      b.crs_pnr,
      b.trip_type,
      b.travel_type,
      b.status,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();

    return (
      (!q || searchable.includes(q)) &&
      (filter === "all" || b.status === filter)
    );
  });

const formatTripType = (tripType) => {
  switch (String(tripType || "").toLowerCase()) {
    case "round_trip":
      return "Round Trip";

    case "special_round_trip":
      return "Special Round Trip";

    case "one_way":
      return "One Way";

    case "multi_city":
      return "Multi City";

    default:
      return "—";
  }
};

const formatTravelType = (travelType) => {
  const value = String(travelType ?? "").toLowerCase();

  if (
    travelType === 0 ||
    value === "0" ||
    value === "domestic"
  ) {
    return "Domestic";
  }

  if (
    travelType === 1 ||
    value === "1" ||
    value === "international"
  ) {
    return "International";
  }

  return "—";
};

const parseItineraryLegs = (booking) => {
  if (!booking?.itinerary_legs) return [];

  try {
    const parsed =
      typeof booking.itinerary_legs === "string"
        ? JSON.parse(booking.itinerary_legs)
        : booking.itinerary_legs;

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const getJourneyLegs = (booking) => {
  const legs = parseItineraryLegs(booking);

  if (!legs.length) {
    return {
      outbound: null,
      returnLeg: null,
    };
  }

  const outbound =
    legs.find(
      (leg) =>
        String(leg?.direction || "").toLowerCase() === "outbound"
    ) || legs[0];

  const returnLeg =
    legs.find(
      (leg) =>
        String(leg?.direction || "").toLowerCase() === "return"
    ) || null;

  return {
    outbound,
    returnLeg,
  };
};

const getRoute = (leg) => {
  if (!leg) return null;

  const from =
    leg.departCode ||
    leg.depart_code ||
    leg.origin ||
    leg.Origin ||
    null;

  const to =
    leg.arriveCode ||
    leg.arrive_code ||
    leg.destination ||
    leg.Destination ||
    null;

  return from && to ? `${from} → ${to}` : null;
};

const getLegDate = (leg) => {
  if (!leg) return null;

  return (
    leg.travelDate ||
    leg.travel_date ||
    leg.departureDate ||
    leg.departure_date ||
    leg.departureDateTime ||
    null
  );
};

/*
 * Multi City: legs after the first one.
 *
 * The standard Travel cell shows the outbound leg and Round Trip adds a
 * Return row. Multi City keeps the same layout and appends every remaining
 * leg (2..5) in journey order. Non multi-city bookings return an empty
 * array so One Way / Round Trip / Special Round Trip are untouched.
 */
const getAdditionalTravelLegs = (booking) => {
  if (String(booking.trip_type || "").toLowerCase() !== "multi_city") {
    return [];
  }

  const legs = parseItineraryLegs(booking);

  return legs
    .slice(1)
    .map((leg) => ({
      route: getRoute(leg),
      date: getLegDate(leg),
    }))
    .filter((leg) => leg.route || leg.date);
};

const getTravelDisplay = (booking) => {
  const { outbound, returnLeg } = getJourneyLegs(booking);

  return {
    outboundRoute:
      getRoute(outbound) ||
      `${booking.depart_code || "—"} → ${booking.arrive_code || "—"}`,

    outboundDate:
      getLegDate(outbound) ||
      booking.travel_date ||
      null,

    returnRoute:
      getRoute(returnLeg) ||
      (
        booking.return_date &&
        String(booking.trip_type || "").toLowerCase() !== "one_way"
          ? `${booking.arrive_code || "—"} → ${booking.depart_code || "—"}`
          : null
      ),

    returnDate:
      getLegDate(returnLeg) ||
      booking.return_date ||
      null,
  };
};

const getPnrDisplay = (booking) => {
  const tripType = String(booking.trip_type || "").toLowerCase();
  const legs = parseItineraryLegs(booking);

  /*
   * Special Round Trip:
   * This is one supplier booking and therefore one Airline PNR
   * applies to the complete journey.
   */
  if (tripType === "special_round_trip") {
    const pnr =
      booking.airline_pnr ||
      booking.record_locator ||
      booking.crs_pnr ||
      null;

    if (pnr) {
      return [
        {
          direction: null,
          pnr: String(pnr).trim(),
        },
      ];
    }

    /*
     * Some special-round-trip records may keep the PNR only
     * inside itinerary_legs. Take the first unique PNR because
     * the complete special round trip uses one supplier PNR.
     */
    const legPnrs = [];

    legs.forEach((leg) => {
      const pnr =
        leg?.airlinePnr ||
        leg?.airline_pnr ||
        leg?.recordLocator ||
        leg?.record_locator ||
        leg?.crsPnr ||
        leg?.crs_pnr ||
        null;

      if (pnr) {
        legPnrs.push(String(pnr).trim());
      }
    });

    const unique = [...new Set(legPnrs)];

    return unique.length
      ? [{ direction: null, pnr: unique[0] }]
      : [];
  }

  /*
   * Normal One Way / Round Trip:
   * PNRs are associated with individual itinerary legs.
   * This allows us to correctly show:
   *
   * Outbound: ABC123
   * Return:   XYZ789
   */
  const journeyPnrs = [];

  legs.forEach((leg, index) => {
    const pnr =
      leg?.airlinePnr ||
      leg?.airline_pnr ||
      leg?.recordLocator ||
      leg?.record_locator ||
      leg?.crsPnr ||
      leg?.crs_pnr ||
      null;

    if (!pnr) return;

    const direction =
      tripType === "multi_city"
        ? `Leg ${index + 1}`
        : String(leg?.direction || "").toLowerCase() === "return"
          ? "Return"
          : "Onward";

    journeyPnrs.push({
      direction,
      pnr: String(pnr).trim(),
    });
  });

  /*
   * Remove duplicate PNRs while preserving journey order.
   *
   * This is important when multiple connecting segments
   * belong to the same journey and share the same PNR.
   */
  const unique = [];
  const seen = new Set();

  for (const item of journeyPnrs) {
    const key = `${item.direction}:${item.pnr}`;

    if (!seen.has(key)) {
      seen.add(key);
      unique.push(item);
    }
  }

  /*
   * Fallback for older bookings where itinerary_legs does
   * not contain PNR information.
   */
  if (!unique.length) {
    const fallbackPnrs = [
      booking.airline_pnr,
      booking.record_locator,
      booking.crs_pnr,
    ]
      .filter(Boolean)
      .map((pnr) => String(pnr).trim());

    return [...new Set(fallbackPnrs)].map((pnr, index) => ({
      direction: index === 0 ? "Onward" : "Return",
      pnr,
    }));
  }

  return unique;
};

const isConnectingFlight = (booking) => {
  const tripType = String(booking.trip_type || "").toLowerCase();

  // Special Round Trip can contain multiple flight numbers because
  // outbound and return are stored in one booking.
  // It is NOT automatically a connecting flight.
  if (tripType === "special_round_trip") {
    return Number(booking.stops || 0) > 0;
  }

  // Multi City legs are separate journeys, not connections; see the
  // card-level isConnectingFlight above for details.
  if (tripType === "multi_city") {
    return false;
  }

  // For every other booking, use the actual stop count.
  return Number(booking.stops || 0) > 0;
};

const formatPaymentStatus = (status) => {
  if (!status) return "—";

  return String(status)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

  return (
    <div>
      {/* Search + Status Filter */}
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search booking ref, airline, route, PNR…"
        />

        <Sel
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "upcoming", label: "Upcoming" },
            { value: "completed", label: "Completed" },
            { value: "cancelled", label: "Cancelled" },
            { value: "pending_payment", label: "Pending Payment" },
            { value: "pending_ticket", label: "Pending Ticket" },
            { value: "inprocess", label: "In Process" },
            { value: "held", label: "Held" },
          ]}
        />

                <span
          style={{
            fontSize: 13,
            color: T.faint,
            alignSelf: "center",
          }}
        >
          {filtered.length} records
        </span>

        {/* Table / Card view toggle */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: 3,
            border: `1px solid ${T.border}`,
            borderRadius: 9,
            background: T.card,
            marginLeft: "auto",
          }}
        >
          <button
            type="button"
            onClick={() => setViewMode("table")}
            style={{
              border: "none",
              borderRadius: 7,
              padding: "7px 12px",
              background:
                viewMode === "table" ? T.accentSoft : "transparent",
              color:
                viewMode === "table" ? T.accent : T.muted,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Table
          </button>

          <button
            type="button"
            onClick={() => setViewMode("card")}
            style={{
              border: "none",
              borderRadius: 7,
              padding: "7px 12px",
              background:
                viewMode === "card" ? T.accentSoft : "transparent",
              color:
                viewMode === "card" ? T.accent : T.muted,
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Card
          </button>
        </div>
      </div>

      {/* Desktop */}
      {viewMode === "table" ? (
  <div className="admin-flight-bookings-desktop">
    <TableWrap>
      <thead>
        <tr>
          <Th>Booking Ref</Th>
          <Th>Booking Date</Th>
          <Th>Travel Date</Th>
          <Th>PNR</Th>
          <Th>Airline</Th>
          <Th>Class</Th>
          <Th>Trip</Th>
          <Th>Passengers</Th>
          <Th>Travel Type</Th>
          <Th>Payment</Th>
          <Th>Amount</Th>
          <Th>Status</Th>
          <Th>Action</Th>
        </tr>
      </thead>

      <tbody>
        {filtered.length === 0 ? (
          <tr>
            <td
              colSpan={13}
              style={{
                textAlign: "center",
                padding: 40,
                color: T.faint,
              }}
            >
              No flight bookings
            </td>
          </tr>
        ) : (
          filtered.map((b) => {
            const pnrs = getPnrDisplay(b);
            const connecting = isConnectingFlight(b);

            return (
              <TRow key={b.id}>
                {/* Booking Ref */}
                <Td>
                  <div
                    style={{
                      fontFamily: "monospace",
                      fontSize: 12,
                      color: T.muted,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.booking_reference || "—"}
                  </div>
                </Td>

                {/* Booking Date */}
                <Td
                  style={{
                    fontSize: 12,
                    color: T.faint,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmtDate(b.created_at)}
                </Td>

                {/* Travel Date */}
                <Td>
                  {(() => {
                    const travel = getTravelDisplay(b);
                    const additionalLegs = getAdditionalTravelLegs(b);

                    return (
                      <div style={{ minWidth: 145 }}>
                        {/* Outbound */}
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: T.ink,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {travel.outboundRoute}
                        </div>

                        <div
                          style={{
                            marginTop: 3,
                            fontSize: 11,
                            color: T.faint,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Outbound: {fmtDate(travel.outboundDate)}
                        </div>

                        {/* Multi City: remaining legs (2..5) */}
                        {additionalLegs.map((leg, index) => (
                          <div
                            key={`mc-leg-${index + 2}`}
                            style={{
                              marginTop: 7,
                              paddingTop: 6,
                              borderTop: `1px solid ${T.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: T.ink,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {leg.route}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                fontSize: 11,
                                color: T.faint,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {leg.date
                                ? `Leg ${index + 2}: ${fmtDate(leg.date)}`
                                : `Leg ${index + 2}`}
                            </div>
                          </div>
                        ))}

                        {/* Return */}
                        {travel.returnRoute && travel.returnDate && (
                          <div
                            style={{
                              marginTop: 7,
                              paddingTop: 6,
                              borderTop: `1px solid ${T.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: T.ink,
                                whiteSpace: "nowrap",
                              }}
                            >
                              {travel.returnRoute}
                            </div>

                            <div
                              style={{
                                marginTop: 3,
                                fontSize: 11,
                                color: T.faint,
                                whiteSpace: "nowrap",
                              }}
                            >
                              Return: {fmtDate(travel.returnDate)}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </Td>

                {/* PNR */}
                <Td>
                  {pnrs.length ? (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 4,
                        fontFamily: "monospace",
                        fontSize: 11,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {pnrs.map((item, index) => (
                        <div
                          key={`${item.pnr || item}-${index}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 5,
                          }}
                        >
                          {pnrs.length > 1 &&
                            typeof item === "object" && (
                              <span
                                style={{
                                  color: T.faint,
                                  fontSize: 10,
                                }}
                              >
                                {item.direction}:
                              </span>
                            )}

                          <span
                            style={{
                              color: T.ink,
                              fontWeight: 600,
                            }}
                          >
                            {typeof item === "object"
                              ? item.pnr
                              : item}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span style={{ color: T.faint }}>—</span>
                  )}
                </Td>

                {/* Airline */}
                <Td>
                  <div
                    style={{
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {b.airline || "—"}
                  </div>

                  {b.flight_numbers && (
                    <div
                      style={{
                        fontSize: 11,
                        color: T.faint,
                        marginTop: 3,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {b.flight_numbers}
                    </div>
                  )}
                </Td>

                {/* Class */}
                <Td style={{ whiteSpace: "nowrap" }}>
                  {b.class_type || "—"}
                </Td>

                {/* Trip */}
                <Td>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {formatTripType(b.trip_type)}
                  </div>

                  {connecting && (
                    <div
                      style={{
                        marginTop: 3,
                        fontSize: 10,
                        color: T.faint,
                        whiteSpace: "nowrap",
                      }}
                    >
                      Connecting Flight
                    </div>
                  )}
                </Td>

                {/* Passenger Count */}
                <Td
                  style={{
                    textAlign: "center",
                    fontWeight: 600,
                  }}
                >
                  {b.num_passengers ?? 0}
                </Td>

                {/* Travel Type */}
                <Td style={{ whiteSpace: "nowrap" }}>
                  {formatTravelType(b.travel_type)}
                </Td>

                {/* Payment */}
                <Td style={{ whiteSpace: "nowrap" }}>
                  {b.payment_status ? (
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                      }}
                    >
                      {formatPaymentStatus(b.payment_status)}
                    </span>
                  ) : (
                    <span style={{ color: T.faint }}>—</span>
                  )}
                </Td>

                {/* Amount */}
                <Td
                  style={{
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                  }}
                >
                  {fmt(b.total_price)}
                </Td>

                {/* Status */}
                <Td>
                  <Badge status={b.status} />
                </Td>

                {/* Action */}
                <Td>
                  <ViewBtn
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelected(b);
                    }}
                  />
                </Td>
              </TRow>
            );
          })
        )}
      </tbody>
    </TableWrap>
  </div>
) : (
  <div
  style={{
    display: "flex",
    flexDirection: "column",
    gap: 12,
    width: "100%",
  }}
>
    {filtered.length === 0 ? (
      <div
        style={{
          gridColumn: "1 / -1",
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 12,
          padding: 40,
          textAlign: "center",
          color: T.faint,
          fontSize: 13,
        }}
      >
        No flight bookings
      </div>
    ) : (
      filtered.map((b) => (
        <FlightBookingCard
          key={b.id}
          booking={b}
          onView={(booking) => setSelected(booking)}
        />
      ))
    )}
  </div>
)}

      {selected && (
        <FlightModal
          booking={selected}
          onClose={() => setSelected(null)}
          onStatusChange={(id, s) => {
            onStatusChange(id, s);
            setSelected((v) =>
              v?.id === id ? { ...v, status: s } : v
            );
          }}
        />
      )}
    </div>
  );
}
function HotelModal({ booking, onClose, onStatusChange }) {
  const T = useThemeColors();

  const [status, setStatus] = useState(booking.status);
  const [saving, setSaving] = useState(false);

  const [modifyCheckIn, setModifyCheckIn] = useState("");
  const [modifyCheckOut, setModifyCheckOut] = useState("");
  const [modifyRemarks, setModifyRemarks] = useState("");
  const [modifySaving, setModifySaving] = useState(false);

  const handle = async s => {
    setSaving(true);

    const res = await apiPatch(
      `/hotel-bookings/${booking.id}?admin=1`,
      { status: s }
    );

    if (res.ok) {
      setStatus(s);
      onStatusChange(booking.id, s);
    }

    setSaving(false);
  };

  const handleModifyRequest = async () => {
    if (!modifyCheckIn) {
      alert("Please select the requested check-in date.");
      return;
    }

    if (!modifyCheckOut) {
      alert("Please select the requested check-out date.");
      return;
    }

    if (new Date(modifyCheckOut) <= new Date(modifyCheckIn)) {
      alert("Check-out date must be after check-in date.");
      return;
    }

    setModifySaving(true);

    try {
      const res = await apiPost(
        `/hotel-bookings/${booking.id}/admin-reschedule-request`,
        {
          requestedCheckIn: modifyCheckIn,
          requestedCheckOut: modifyCheckOut,
          remarks: modifyRemarks,
        }
      );

      if (!res.ok) {
        alert(`Failed to submit request: ${res.error}`);
        return;
      }

      alert(
        "Modification request submitted. The team will confirm availability and charges."
      );

      setModifyCheckIn("");
      setModifyCheckOut("");
      setModifyRemarks("");
    } finally {
      setModifySaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      title="Hotel Booking"
      subtitle={booking.booking_reference}
    >
      <DGrid>
        <DRow
          label="Guest"
          value={`${booking.guest_first_name} ${booking.guest_last_name}`}
        />
        <DRow label="Email" value={booking.guest_email} />
        <DRow label="Hotel" value={booking.hotel_name} />
        <DRow label="Check-in" value={fmtDate(booking.check_in)} />
        <DRow label="Check-out" value={fmtDate(booking.check_out)} />
        <DRow label="Nights" value={booking.nights} />
        <DRow label="Room" value={booking.room_type} />
        <DRow label="Total" value={fmt(booking.grand_total)} />
      </DGrid>

      <SecTitle>Modify Booking</SecTitle>

      <div
        style={{
          background: T.accentSoft,
          borderRadius: 10,
          padding: "12px 14px",
          marginBottom: 14,
          fontSize: 12,
          color: T.accent,
        }}
      >
        Submit a modification request to the support team. The existing
        booking will not be changed until the team confirms the request.
      </div>

      <DGrid>
        <DRow
          label="Current Check-in"
          value={fmtDate(booking.check_in)}
        />

        <DRow
          label="Current Check-out"
          value={fmtDate(booking.check_out)}
        />

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            Requested Check-in
          </span>

          <input
            type="date"
            value={modifyCheckIn}
            onChange={e => setModifyCheckIn(e.target.value)}
            min={new Date().toISOString().split("T")[0]}
            style={{
              width: "100%",
              padding: "9px 10px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: T.card,
              color: T.ink,
            }}
          />
        </label>

        <label style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 12, color: T.muted }}>
            Requested Check-out
          </span>

          <input
            type="date"
            value={modifyCheckOut}
            onChange={e => setModifyCheckOut(e.target.value)}
            min={
              modifyCheckIn ||
              new Date().toISOString().split("T")[0]
            }
            style={{
              width: "100%",
              padding: "9px 10px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: T.card,
              color: T.ink,
            }}
          />
        </label>

        <label
          style={{
            display: "grid",
            gap: 6,
            gridColumn: "1 / -1",
          }}
        >
          <span style={{ fontSize: 12, color: T.muted }}>
            Remarks
          </span>

          <textarea
            value={modifyRemarks}
            onChange={e => setModifyRemarks(e.target.value)}
            rows={3}
            placeholder="Optional modification remarks"
            style={{
              width: "100%",
              padding: "9px 10px",
              border: `1px solid ${T.border}`,
              borderRadius: 8,
              background: T.card,
              color: T.ink,
              resize: "vertical",
            }}
          />
        </label>
      </DGrid>

      <button
        type="button"
        onClick={handleModifyRequest}
        disabled={modifySaving || booking.status === "cancelled"}
        style={{
          width: "100%",
          marginTop: 12,
          padding: "10px 14px",
          border: "none",
          borderRadius: 8,
          background: T.accent,
          color: "#fff",
          fontSize: 13,
          fontWeight: 600,
          cursor: modifySaving ? "not-allowed" : "pointer",
          opacity: modifySaving ? 0.7 : 1,
        }}
      >
        {modifySaving
          ? "Submitting..."
          : "Submit Modification Request"}
      </button>

      <SecTitle>Update Status</SecTitle>

<StatusActions
  statuses={["upcoming", "completed"]}
  current={status}
  onSelect={handle}
  saving={saving}
/>
    </Modal>
  );
}
function HotelsSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(b => { const q = search.toLowerCase(); return (!q || `${b.guest_first_name} ${b.guest_last_name}`.toLowerCase().includes(q) || b.hotel_name?.toLowerCase().includes(q) || b.booking_reference?.toLowerCase().includes(q)) && (filter === "all" || b.status === filter); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search guest, hotel, ref…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "upcoming", label: "Upcoming" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} records</span></div>
    <TableWrap><thead>
  <tr>
    <Th>Ref</Th>
    <Th>Passenger</Th>
    <Th>Package</Th>
    <Th>Travel Date</Th>
    <Th>Travellers</Th>
    <Th>Final Amount</Th>
    <Th>Paid</Th>
    <Th>Due</Th>
    <Th>Payment</Th>
    <Th>Cancellation</Th>
    <Th>Action</Th>
  </tr>
</thead><tbody>{filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>No hotel bookings</td></tr> : filtered.map(b => <TRow key={b.id} onClick={() => setSelected(b)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b.booking_reference}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={b.guest_first_name} bg="#fce7f3" tc="#be185d" /><div><div style={{ fontWeight: 500 }}>{b.guest_first_name} {b.guest_last_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.guest_email}</div></div></div></Td><Td><div style={{ fontWeight: 500 }}>{b.hotel_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.hotel_location}</div></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b.check_in)}</Td><Td>{b.nights}N</Td><Td style={{ fontWeight: 600 }}>{fmt(b.grand_total)}</Td><Td><Badge status={b.status} /></Td><Td><ViewBtn onClick={() => setSelected(b)} /></Td></TRow>)}</tbody></TableWrap>
    {selected && <HotelModal booking={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}</div>);
}
function BusModal({ booking, onClose, onStatusChange }) {
  const T = useThemeColors();
  const [status, setStatus] = useState(booking.status); const [saving, setSaving] = useState(false);
  const [modifyDate, setModifyDate] = useState("");
const [modifyTime, setModifyTime] = useState("");
const [modifyRemarks, setModifyRemarks] = useState("");
const [modifySaving, setModifySaving] = useState(false);
  const handle = async s => { setSaving(true); const res = await apiPatch(`/bus-bookings/${booking.id}?admin=1`, { status: s }); if (res.ok) { setStatus(s); onStatusChange(booking.id, s); } setSaving(false); };
  const handleModifyRequest = async () => {
  if (!modifyDate) {
    alert("Please select the requested travel date.");
    return;
  }

  setModifySaving(true);

  try {
    const res = await apiPost(
      `/bus-bookings/${booking.id}/admin-reschedule-request`,
      {
        requestedDate: modifyDate,
        requestedTime: modifyTime,
        remarks: modifyRemarks,
      }
    );

    if (!res.ok) {
      alert(`Failed to submit request: ${res.error}`);
      return;
    }

    alert(
      "Modification request submitted. The team will confirm availability and charges."
    );

    setModifyDate("");
    setModifyTime("");
    setModifyRemarks("");
  } finally {
    setModifySaving(false);
  }
};
  return (<Modal onClose={onClose} title="Bus Booking" subtitle={booking.booking_reference}><DGrid><DRow label="Passenger" value={booking.passenger_name} /><DRow label="Operator" value={booking.operator} /><DRow label="Route" value={`${booking.from_city} → ${booking.to_city}`} /><DRow label="Date" value={fmtDate(booking.travel_date)} /><DRow label="Bus Type" value={booking.bus_type} /><DRow label="Seats" value={booking.seat_numbers} /><DRow label="Total" value={fmt(booking.total_price)} /></DGrid>
  <SecTitle>Modify Booking</SecTitle>

<div
  style={{
    background: T.accentSoft,
    borderRadius: 10,
    padding: "12px 14px",
    marginBottom: 14,
    fontSize: 12,
    color: T.accent,
  }}
>
  Submit a modification request to the support team. The existing
  booking will not be changed until the team confirms the request.
</div>

<DGrid>
  <DRow
    label="Current Travel Date"
    value={fmtDate(booking.travel_date)}
  />

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
      Requested Travel Date
    </span>

    <input
      type="date"
      value={modifyDate}
      onChange={(e) => setModifyDate(e.target.value)}
      min={new Date().toISOString().split("T")[0]}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
      }}
    />
  </label>

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
      Requested Time
    </span>

    <input
      type="time"
      value={modifyTime}
      onChange={(e) => setModifyTime(e.target.value)}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
      }}
    />
  </label>

  <label style={{ display: "grid", gap: 6 }}>
    <span style={{ fontSize: 12, color: T.muted }}>
      Remarks
    </span>

    <textarea
      value={modifyRemarks}
      onChange={(e) => setModifyRemarks(e.target.value)}
      placeholder="Enter modification request details..."
      rows={3}
      style={{
        width: "100%",
        padding: "9px 10px",
        border: `1px solid ${T.border}`,
        borderRadius: 8,
        background: T.card,
        color: T.ink,
        resize: "vertical",
        fontFamily: "inherit",
      }}
    />
  </label>
</DGrid>

<Btn
  onClick={handleModifyRequest}
  disabled={modifySaving || !modifyDate}
  variant="primary"
>
  {modifySaving
    ? "Submitting..."
    : "Submit Modification Request"}
</Btn>
  <SecTitle>Update Status</SecTitle>

<StatusActions
  statuses={["upcoming", "completed"]}
  current={status}
  onSelect={handle}
  saving={saving}
/>
</Modal>);
}

function BusesSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(b => { const q = search.toLowerCase(); return (!q || b.passenger_name?.toLowerCase().includes(q) || b.booking_reference?.toLowerCase().includes(q) || `${b.from_city} ${b.to_city}`.toLowerCase().includes(q)) && (filter === "all" || b.status === filter); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search passenger, route…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "upcoming", label: "Upcoming" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} records</span></div>
    <TableWrap><thead><tr><Th>Ref</Th><Th>Passenger</Th><Th>Route</Th><Th>Operator</Th><Th>Date</Th><Th>Seats</Th><Th>Total</Th><Th>Status</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={9} style={{ textAlign: "center", padding: 40, color: T.faint }}>No bus bookings</td></tr> : filtered.map(b => <TRow key={b.id} onClick={() => setSelected(b)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b.booking_reference}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={b.passenger_name} bg="#d1fae5" tc="#065f46" /><div><div style={{ fontWeight: 500 }}>{b.passenger_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.passenger_email}</div></div></div></Td><Td><span style={{ fontWeight: 600, color: T.green }}>{b.from_city} → {b.to_city}</span></Td><Td>{b.operator}</Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b.travel_date)}</Td><Td style={{ fontSize: 12 }}>{b.seat_numbers}</Td><Td style={{ fontWeight: 600 }}>{fmt(b.total_price)}</Td><Td><Badge status={b.status} /></Td><Td><ViewBtn onClick={() => setSelected(b)} /></Td></TRow>)}</tbody></TableWrap>
    {selected && <BusModal booking={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}</div>);
}
function TrainModal({ booking, onClose, onStatusChange }) {
  const [status, setStatus] = useState(booking.status);
  return (<Modal onClose={onClose} title="Train Booking" subtitle={booking.booking_reference}><DGrid><DRow label="Passenger" value={booking.passenger_name} /><DRow label="Train" value={`${booking.train_name} (${booking.train_number})`} /><DRow label="Route" value={`${booking.from_station} → ${booking.to_station}`} /><DRow label="Date" value={fmtDate(booking.travel_date)} /><DRow label="Class" value={booking.class} /><DRow label="PNR" value={booking.pnr} /><DRow label="Total" value={fmt(booking.total_price)} /></DGrid><SecTitle>Update Status</SecTitle><StatusActions statuses={["upcoming", "completed", "cancelled"]} current={status} onSelect={s => { setStatus(s); onStatusChange(booking.id, s); }} saving={false} /></Modal>);
}
function TrainsSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(b => { const q = search.toLowerCase(); return (!q || b.passenger_name?.toLowerCase().includes(q) || b.pnr?.includes(q) || b.train_name?.toLowerCase().includes(q)) && (filter === "all" || b.status === filter); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search passenger, PNR, train…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "upcoming", label: "Upcoming" }, { value: "completed", label: "Completed" }, { value: "cancelled", label: "Cancelled" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} records</span></div>
    <TableWrap><thead><tr><Th>Ref</Th><Th>Passenger</Th><Th>Train</Th><Th>Route</Th><Th>Date</Th><Th>Class</Th><Th>PNR</Th><Th>Total</Th><Th>Status</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={10} style={{ textAlign: "center", padding: 40, color: T.faint }}>No train bookings</td></tr> : filtered.map(b => <TRow key={b.id} onClick={() => setSelected(b)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b.booking_reference}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={b.passenger_name} bg="#ede9fe" tc="#6d28d9" /><div><div style={{ fontWeight: 500 }}>{b.passenger_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.passenger_email}</div></div></div></Td><Td><div style={{ fontWeight: 500 }}>{b.train_name}</div><div style={{ fontSize: 11, color: T.faint }}>#{b.train_number}</div></Td><Td><span style={{ fontWeight: 600, color: "#7c3aed" }}>{b.from_station} → {b.to_station}</span></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b.travel_date)}</Td><Td>{b.class}</Td><Td style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 500 }}>{b.pnr}</Td><Td style={{ fontWeight: 600 }}>{fmt(b.total_price)}</Td><Td><Badge status={b.status} /></Td><Td><ViewBtn onClick={() => setSelected(b)} /></Td></TRow>)}</tbody></TableWrap>
    {selected && <TrainModal booking={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}</div>);
}
function HolidayBookingModal({ booking, onClose }) {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const [refund, setRefund] = useState(null);
  const [loadingRefund, setLoadingRefund] = useState(false);
  const [refundAmount, setRefundAmount] = useState("");
  const [refundReason, setRefundReason] = useState("");
  const [processing, setProcessing] = useState(false);

  const finalAmount = Number(
    booking.final_amount ??
    booking.payable_total ??
    booking.total_price ??
    0
  );

  const amountPaid = Number(booking.amount_paid || 0);
  const amountDue = Number(
    booking.amount_due ?? Math.max(finalAmount - amountPaid, 0)
  );

  const paymentStatus =
    booking.payment_status || "pending";

  const bookingStatus =
    booking.booking_payment_status ||
    booking.status ||
    "payment_pending";

  const loadRefund = async () => {
    setLoadingRefund(true);

    const res = await apiFetch(
      `/admin/reports/refunds/holiday/${booking.id}`
    );

    if (res) {
      setRefund(res);
      setRefundAmount(
        Number(
          res.final_refund_amount ??
          res.supplier_refund_amount ??
          0
        ).toFixed(2)
      );
    }

    setLoadingRefund(false);
  };

  useEffect(() => {
    if (
      booking.cancellation_status === "requested" ||
      booking.cancellation_status === "pending_admin_review"
    ) {
      loadRefund();
    }
  }, [booking.id]);

  const approveRefund = async () => {
    const amount = Number(refundAmount);

    if (!Number.isFinite(amount) || amount < 0) {
      alert("Enter a valid refund amount.");
      return;
    }

    if (amount > amountPaid) {
      alert(
        `Refund cannot exceed the amount actually paid: ${fmt(
          amountPaid
        )}`
      );
      return;
    }

    setProcessing(true);

    const res = await apiPost(
      `/admin/reports/refunds/${refund.id}/process`,
      {
        final_refund_amount: amount,
        reason: refundReason.trim() || null,
      }
    );

    if (!res.ok) {
      alert(res.error || "Refund approval failed.");
      setProcessing(false);
      return;
    }

    alert(
      res.data?.message ||
      `Refund of ${fmt(amount)} initiated successfully.`
    );

    await loadRefund();
    setProcessing(false);
  };

  return (
    <Modal
      onClose={onClose}
      title="Holiday Booking"
      subtitle={booking.booking_reference}
      maxWidth={760}
    >
      <SecTitle>Booking</SecTitle>

      <DGrid>
        <DRow
          label="Package"
          value={booking.package_title || booking.title || "—"}
        />

        <DRow
          label="Location"
          value={
            booking.package_location ||
            booking.location ||
            "—"
          }
        />

        <DRow
          label="Passenger"
          value={booking.passenger_name}
        />

        <DRow
          label="Email"
          value={booking.passenger_email}
        />

        <DRow
          label="Travel Date"
          value={fmtDate(booking.travel_date)}
        />

        <DRow
          label="Travellers"
          value={booking.num_travellers}
        />

        <DRow
          label="Booking Payment Status"
          value={<Badge status={bookingStatus} />}
        />

        <DRow
          label="Payment Status"
          value={<Badge status={paymentStatus} />}
        />
      </DGrid>

      <SecTitle>Financial Summary</SecTitle>

      <DGrid>
        <DRow
          label="Amount Before Discounts"
          value={fmt(
            booking.amount_before_discounts ??
            finalAmount
          )}
        />

        <DRow
          label="Coupon Discount"
          value={fmt(booking.coupon_discount || 0)}
        />

        <DRow
          label="Gift Card Discount"
          value={fmt(booking.gift_card_discount || 0)}
        />

        <DRow
          label="Final Amount"
          value={fmt(finalAmount)}
        />

        <DRow
          label="Amount Paid"
          value={fmt(amountPaid)}
        />

        <DRow
          label="Amount Due"
          value={fmt(amountDue)}
        />

        <DRow
          label="Final Payment Deadline"
          value={fmtDT(booking.final_payment_deadline)}
        />

        <DRow
          label="Grace Period Until"
          value={fmtDT(booking.grace_period_until)}
        />
      </DGrid>

      {booking.special_requests && (
        <>
          <SecTitle>Special Requests</SecTitle>

          <div
            style={{
              background: T.accentSoft,
              borderRadius: 10,
              padding: "12px 14px",
              color: T.text,
              fontSize: 13,
              whiteSpace: "pre-wrap",
            }}
          >
            {booking.special_requests}
          </div>
        </>
      )}

      <SecTitle>Cancellation & Refund</SecTitle>

      <DGrid>
        <DRow
          label="Cancellation Status"
          value={
            <Badge
              status={
                booking.cancellation_status ||
                "not_requested"
              }
            />
          }
        />

        <DRow
          label="Cancellation Requested"
          value={fmtDT(
            booking.cancellation_requested_at
          )}
        />

        <DRow
          label="Cancellation Reason"
          value={booking.cancellation_reason || "—"}
          full
        />
      </DGrid>

      {booking.cancellation_status === "pending_admin_review" ||
        booking.cancellation_status === "requested" ? (
        <>
          <div
            style={{
              marginTop: 14,
              padding: 14,
              borderRadius: 10,
              background: T.warnSoft,
              border: `1px solid ${T.warn}55`,
              color: T.text,
              fontSize: 13,
            }}
          >
            This cancellation requires Admin refund review.
            The refund amount must be based on the amount
            actually paid and the saved cancellation policy.
          </div>

          <div
            style={{
              marginTop: 14,
              display: "flex",
              justifyContent: "flex-end",
            }}
          >
            <Btn
              onClick={loadRefund}
              variant="ghost"
              disabled={loadingRefund}
            >
              <RefreshCw size={13} />
              {loadingRefund
                ? "Loading…"
                : "Load Refund Review"}
            </Btn>
          </div>
        </>
      ) : null}

      {refund && (
        <>
          <SecTitle>Refund Review</SecTitle>

          <DGrid>
            <DRow
              label="Amount Actually Paid"
              value={fmt(amountPaid)}
            />

            <DRow
              label="Supplier Refund"
              value={fmt(
                refund.supplier_refund_amount || 0
              )}
            />

            <DRow
              label="Cancellation Charges"
              value={fmt(
                refund.supplier_cancellation_charges || 0
              )}
            />

            <DRow
              label="Current Refund Status"
              value={
                <Badge
                  status={
                    refund.refund_status || "pending_admin_review"
                  }
                />
              }
            />
          </DGrid>

          {refund.refund_status === "pending_admin_review" && (
            <>
              <label
                style={{
                  display: "grid",
                  gap: 6,
                  marginTop: 14,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: T.muted,
                  }}
                >
                  Approved Refund Amount (₹)
                </span>

                <input
                  type="number"
                  min="0"
                  max={amountPaid}
                  step="0.01"
                  value={refundAmount}
                  onChange={e =>
                    setRefundAmount(e.target.value)
                  }
                  style={inputS}
                />
              </label>

              <label
                style={{
                  display: "grid",
                  gap: 6,
                  marginTop: 10,
                }}
              >
                <span
                  style={{
                    fontSize: 12,
                    color: T.muted,
                  }}
                >
                  Admin Reason
                </span>

                <textarea
                  rows={3}
                  value={refundReason}
                  onChange={e =>
                    setRefundReason(e.target.value)
                  }
                  style={{
                    ...inputS,
                    resize: "vertical",
                  }}
                  placeholder="Reason for approved refund amount"
                />
              </label>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  marginTop: 14,
                }}
              >
                <Btn
                  onClick={approveRefund}
                  variant="primary"
                  disabled={processing}
                >
                  {processing
                    ? "Processing…"
                    : `Approve Refund ${fmt(
                        Number(refundAmount || 0)
                      )}`}
                </Btn>
              </div>
            </>
          )}

          {["initiated", "processing"].includes(
            refund.refund_status
          ) && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 9,
                background: T.accentSoft,
                color: T.accent,
                fontSize: 13,
              }}
            >
              Refund has been initiated and is currently
              being processed by Easebuzz.
            </div>
          )}

          {refund.refund_status === "refunded" && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 9,
                background: T.greenSoft,
                color: T.green,
                fontSize: 13,
              }}
            >
              Refund completed:{" "}
              <strong>
                {fmt(refund.final_refund_amount || 0)}
              </strong>
            </div>
          )}

          {refund.refund_status === "failed" && (
            <div
              style={{
                marginTop: 14,
                padding: 12,
                borderRadius: 9,
                background: T.dangerSoft,
                color: T.danger,
                fontSize: 13,
              }}
            >
              Refund failed:{" "}
              {refund.failure_reason || "Unknown failure"}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}
function HolidayBookingsSection({ data }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(b => { const q = search.toLowerCase(); return (!q || b.passenger_name?.toLowerCase().includes(q) || b.booking_reference?.toLowerCase().includes(q) || (b.package_title || b.title)?.toLowerCase().includes(q)) && (
  filter === "all" ||
  b.payment_status === filter
); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search name, package, ref…" /><Sel
  value={filter}
  onChange={setFilter}
  options={[
    { value: "all", label: "All" },
    { value: "pending", label: "Payment Pending" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "paid", label: "Paid" },
    { value: "overdue", label: "Overdue" },
    { value: "defaulted", label: "Defaulted" },
    { value: "refunded", label: "Refunded" },
  ]}
/><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} records</span></div>
    <TableWrap><thead><tr><Th>Ref</Th><Th>Passenger</Th><Th>Package</Th><Th>Travel Date</Th><Th>Travellers</Th><Th>Total</Th><Th>Status</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={11} style={{ textAlign: "center", padding: 40, color: T.faint }}>No holiday bookings</td></tr> : filtered.map(b => <TRow key={b.id} onClick={() => setSelected(b)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{b.booking_reference}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={b.passenger_name} bg="#fef9c3" tc="#92400e" /><div><div style={{ fontWeight: 500 }}>{b.passenger_name}</div><div style={{ fontSize: 11, color: T.faint }}>{b.passenger_email}</div></div></div></Td><Td><div style={{ fontWeight: 500 }}>{b.package_title || b.title || "—"}</div><div style={{ fontSize: 11, color: T.faint }}>{b.package_location || b.location}</div></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(b.travel_date)}</Td><Td>{b.num_travellers}</Td><Td style={{ fontWeight: 600 }}>
  {fmt(
    b.final_amount ??
    b.payable_total ??
    b.total_price ??
    0
  )}
</Td>

<Td style={{ fontWeight: 600, color: T.green }}>
  {fmt(b.amount_paid || 0)}
</Td>

<Td style={{ fontWeight: 600, color: T.warn }}>
  {fmt(b.amount_due || 0)}
</Td>

<Td>
  <Badge
    status={
      b.payment_status ||
      "pending"
    }
  />
</Td>

<Td>
  <Badge
    status={
      b.cancellation_status ||
      "not_requested"
    }
  />
</Td>

<Td>
  <ViewBtn onClick={() => setSelected(b)} />
</Td></TRow>)}</tbody></TableWrap>
    {selected && (
  <HolidayBookingModal
    booking={selected}
    onClose={() => setSelected(null)}
  />
)}</div>);
}
function VisaModal({ app, onClose, onStatusChange }) {
  const [status, setStatus] = useState(app.status); const [saving, setSaving] = useState(false);
  const handle = async s => { setSaving(true); await apiPatch(`/visa-applications/${app.application_id}/status`, { status: s }); setStatus(s); onStatusChange(app.application_id, s); setSaving(false); };
  return (<Modal onClose={onClose} title="Visa Application" subtitle={app.reference_number}><DGrid><DRow label="Country" value={`${app.country_flag || ""} ${app.country_name || "—"}`} /><DRow label="Visa Type" value={app.visa_type} /><DRow label="Processing" value={app.processing_time} /><DRow label="Fee" value={fmt(app.total_fee || 0)} /><DRow label="Full Name" value={`${app.first_name || ""} ${app.last_name || ""}`.trim()} /><DRow label="Email" value={app.email} /><DRow label="Passport" value={app.passport_number} /><DRow label="Expiry" value={fmtDate(app.passport_expiry)} /></DGrid><SecTitle>Update Status</SecTitle><StatusActions statuses={["in_progress", "submitted", "approved", "rejected"]} current={status} onSelect={handle} saving={saving} /></Modal>);
}
function VisaSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(a => { const q = search.toLowerCase(); return (!q || `${a.first_name} ${a.last_name}`.toLowerCase().includes(q) || a.email?.toLowerCase().includes(q) || a.country_name?.toLowerCase().includes(q)) && (filter === "all" || a.status === filter); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search name, email, country…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "draft", label: "Draft" }, { value: "in_progress", label: "In Progress" }, { value: "submitted", label: "Submitted" }, { value: "approved", label: "Approved" }, { value: "rejected", label: "Rejected" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} records</span></div>
    <TableWrap><thead><tr><Th>Ref</Th><Th>Applicant</Th><Th>Destination</Th><Th>Type</Th><Th>Fee</Th><Th>Status</Th><Th>Date</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>No visa applications</td></tr> : filtered.map(a => <TRow key={a.application_id} onClick={() => setSelected(a)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted, fontWeight: 500 }}>{a.reference_number || "—"}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={a.first_name} /><div><div style={{ fontWeight: 500 }}>{`${a.first_name || ""} ${a.last_name || ""}`.trim()}</div><div style={{ fontSize: 11, color: T.faint }}>{a.email}</div></div></div></Td><Td>{a.country_flag} {a.country_name}</Td><Td>{a.visa_type}</Td><Td style={{ fontWeight: 600 }}>{fmt(a.total_fee || 0)}</Td><Td><Badge status={a.status} /></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(a.created_at)}</Td><Td><ViewBtn onClick={() => setSelected(a)} /></Td></TRow>)}</tbody></TableWrap>
    {selected && <VisaModal app={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.application_id === id ? { ...v, status: s } : v); }} />}</div>);
}
function ContactModal({ msg, onClose, onStatusChange }) {
  const T = useThemeColors();
  const [status, setStatus] = useState(msg.status); const [saving, setSaving] = useState(false);
  const handle = async s => { setSaving(true); await apiPatch(`/contact/${msg.id}/status`, { status: s }); setStatus(s); onStatusChange(msg.id, s); setSaving(false); };
  return (<Modal onClose={onClose} title="Contact Message" subtitle={`#${msg.id} · ${fmtDT(msg.created_at)}`}><DGrid><DRow label="Name" value={msg.name} /><DRow label="Email" value={msg.email} /><DRow label="Phone" value={msg.phone || "—"} /><DRow label="Subject" value={msg.subject || "—"} /></DGrid><SecTitle>Message</SecTitle><div style={{ background: T.accentSoft, borderRadius: 10, padding: "12px 14px", fontSize: 14, color: T.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{msg.message}</div><SecTitle>Quick Reply</SecTitle><a href={`mailto:${msg.email}?subject=Re: ${msg.subject || "Your enquiry"}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", background: T.accent, color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 500 }}><Mail size={14} />Reply via Email</a><SecTitle>Update Status</SecTitle><StatusActions statuses={["new", "read", "replied", "closed"]} current={status} onSelect={handle} saving={saving} /></Modal>);
}
function ContactSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(m => { const q = search.toLowerCase(); return (!q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q) || m.subject?.toLowerCase().includes(q)) && (filter === "all" || m.status === filter); });
  return (<div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search name, email…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "new", label: "New" }, { value: "read", label: "Read" }, { value: "replied", label: "Replied" }, { value: "closed", label: "Closed" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} messages</span></div>
    <TableWrap><thead><tr><Th>#</Th><Th>Sender</Th><Th>Subject</Th><Th>Preview</Th><Th>Status</Th><Th>Received</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={7} style={{ textAlign: "center", padding: 40, color: T.faint }}>No messages</td></tr> : filtered.map(m => <TRow key={m.id} onClick={() => setSelected(m)}><Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted }}>#{m.id}</Td><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={m.name} bg="#fce7f3" tc="#be185d" /><div><div style={{ fontWeight: 500 }}>{m.name}</div><div style={{ fontSize: 11, color: T.faint }}>{m.email}</div></div></div></Td><Td><span style={{ background: T.accentSoft, color: T.accent, fontSize: 11, fontWeight: 500, padding: "3px 8px", borderRadius: 6 }}>{m.subject || "General"}</span></Td><Td style={{ color: T.muted, maxWidth: 160 }}>{m.message?.slice(0, 50)}{m.message?.length > 50 ? "…" : ""}</Td><Td><Badge status={m.status} /></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDT(m.created_at)}</Td><Td><ViewBtn onClick={() => setSelected(m)} /></Td></TRow>)}</tbody></TableWrap>
    {selected && <ContactModal msg={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}</div>);
}

function GroupInquiryModal({ inquiry, onClose, onStatusChange }) {
  const T = useThemeColors();
  const [status, setStatus] = useState(inquiry.status);
  const [saving, setSaving] = useState(false);
  const handle = async s => { setSaving(true); const res = await apiPatch(`/admin/group-inquiries/${inquiry.id}/status`, { status: s }); if (res.ok) { setStatus(s); onStatusChange(inquiry.id, s); } setSaving(false); };
  return <Modal onClose={onClose} title="Group Booking Notification" subtitle={`#${inquiry.id} · ${fmtDT(inquiry.created_at)}`}>
    <DGrid>
      <DRow label="Customer" value={inquiry.name} /><DRow label="Passengers" value={inquiry.passengers} />
      <DRow label="Email" value={inquiry.email} /><DRow label="Phone" value={inquiry.phone} />
      <DRow label="Route" value={`${inquiry.depart_code} → ${inquiry.arrive_code}`} /><DRow label="Travel date" value={fmtDate(inquiry.travel_date)} />
      <DRow label="Airline" value={inquiry.airline} /><DRow label="Flight" value={inquiry.flight_numbers} />
      <DRow label="Departure" value={`${inquiry.depart_time || "—"} · ${inquiry.depart_airport || inquiry.depart_code}`} /><DRow label="Arrival" value={`${inquiry.arrive_time || "—"} · ${inquiry.arrive_airport || inquiry.arrive_code}`} />
    </DGrid>
    {inquiry.remark && <><SecTitle>Customer remark</SecTitle><div style={{ background: T.accentSoft, borderRadius: 10, padding: "12px 14px", fontSize: 14, color: T.text, lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{inquiry.remark}</div></>}
    <SecTitle>Contact customer</SecTitle><div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}><a href={`mailto:${inquiry.email}?subject=Your group flight booking enquiry`} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: T.accent, color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 500 }}><Mail size={14} />Email</a><a href={`https://wa.me/${String(inquiry.phone || "").replace(/\D/g, "")}`} target="_blank" rel="noreferrer" style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 14px", background: T.green, color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 500 }}>WhatsApp</a></div>
    <SecTitle>Update notification</SecTitle><StatusActions statuses={["new", "read", "replied", "closed"]} current={status} onSelect={handle} saving={saving} />
  </Modal>;
}
function GroupInquiriesSection({ data, onStatusChange }) {
  const T = useThemeColors();
  const [search, setSearch] = useState(""); const [filter, setFilter] = useState("all"); const [selected, setSelected] = useState(null);
  const filtered = data.filter(i => { const q = search.toLowerCase(); return (!q || i.name?.toLowerCase().includes(q) || i.email?.toLowerCase().includes(q) || `${i.depart_code} ${i.arrive_code}`.toLowerCase().includes(q)) && (filter === "all" || i.status === filter); });
  return <div><div style={{ background: T.warnSoft, border: `1px solid ${T.warn}55`, borderRadius: 10, padding: "10px 13px", fontSize: 13, color: T.text, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 }}><Bell size={16} color={T.warn} /> New group-booking requests appear here as notifications. Mark them read, replied, or closed as you follow up.</div><div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}><SearchBar value={search} onChange={setSearch} placeholder="Search customer, email, route…" /><Sel value={filter} onChange={setFilter} options={[{ value: "all", label: "All" }, { value: "new", label: "New" }, { value: "read", label: "Read" }, { value: "replied", label: "Replied" }, { value: "closed", label: "Closed" }]} /><span style={{ fontSize: 13, color: T.faint, alignSelf: "center" }}>{filtered.length} notifications</span></div><TableWrap><thead><tr><Th>Customer</Th><Th>Route</Th><Th>Travel date</Th><Th>Passengers</Th><Th>Flight</Th><Th>Status</Th><Th>Received</Th><Th>Action</Th></tr></thead><tbody>{filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>No group-booking notifications</td></tr> : filtered.map(i => <TRow key={i.id} onClick={() => setSelected(i)}><Td><div style={{ display: "flex", alignItems: "center", gap: 10 }}><Avatar name={i.name} bg="#fef3c7" tc="#92400e" /><div><div style={{ fontWeight: 500 }}>{i.name}</div><div style={{ fontSize: 11, color: T.faint }}>{i.email} · {i.phone}</div></div></div></Td><Td style={{ fontWeight: 600, color: T.accent }}>{i.depart_code} → {i.arrive_code}</Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDate(i.travel_date)}</Td><Td>{i.passengers}</Td><Td><div style={{ fontWeight: 500 }}>{i.airline}</div><div style={{ fontSize: 11, color: T.faint }}>{i.flight_numbers || "—"}</div></Td><Td><Badge status={i.status} /></Td><Td style={{ fontSize: 12, color: T.faint }}>{fmtDT(i.created_at)}</Td><Td><ViewBtn onClick={() => setSelected(i)} /></Td></TRow>)}</tbody></TableWrap>{selected && <GroupInquiryModal inquiry={selected} onClose={() => setSelected(null)} onStatusChange={(id, s) => { onStatusChange(id, s); setSelected(v => v?.id === id ? { ...v, status: s } : v); }} />}</div>;
}

function VisaEnquiriesSection({ data, onStatusChange }) {
  const T = useThemeColors();

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const filtered = data.filter((i) => {
    const q = search.toLowerCase();

    return (
      (!q ||
        i.full_name?.toLowerCase().includes(q) ||
        i.email?.toLowerCase().includes(q) ||
        i.phone?.toLowerCase().includes(q) ||
        i.destination_country?.toLowerCase().includes(q) ||
        i.visa_type?.toLowerCase().includes(q)) &&
      (filter === "all" || i.status === filter)
    );
  });

  return (
    <div>
      <div
        style={{
          display: "flex",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <SearchBar
          value={search}
          onChange={setSearch}
          placeholder="Search name, email, destination…"
        />

        <Sel
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: "All" },
            { value: "new", label: "New" },
            { value: "read", label: "Read" },
            { value: "replied", label: "Replied" },
            { value: "closed", label: "Closed" },
          ]}
        />

        <span
          style={{
            fontSize: 13,
            color: T.faint,
            alignSelf: "center",
          }}
        >
          {filtered.length} enquiries
        </span>
      </div>

      <TableWrap>
        <thead>
          <tr>
            <Th>Customer</Th>
            <Th>Destination</Th>
            <Th>Visa Type</Th>
            <Th>Travel Date</Th>
            <Th>Travellers</Th>
            <Th>Status</Th>
            <Th>Received</Th>
            <Th>Action</Th>
          </tr>
        </thead>

        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td
                colSpan={8}
                style={{
                  textAlign: "center",
                  padding: 40,
                  color: T.faint,
                }}
              >
                No visa enquiries
              </td>
            </tr>
          ) : (
            filtered.map((i) => (
              <TRow
                key={i.id}
                onClick={() => setSelected(i)}
              >
                <Td>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                    }}
                  >
                    <Avatar
                      name={i.full_name}
                      bg="#ede9fe"
                      tc="#6d28d9"
                    />

                    <div>
                      <div style={{ fontWeight: 500 }}>
                        {i.full_name}
                      </div>

                      <div
                        style={{
                          fontSize: 11,
                          color: T.faint,
                        }}
                      >
                        {i.email} · {i.phone}
                      </div>
                    </div>
                  </div>
                </Td>

                <Td style={{ fontWeight: 600 }}>
                  {i.destination_country}
                </Td>

                <Td>{i.visa_type}</Td>

                <Td>{fmtDate(i.travel_date)}</Td>

                <Td>{i.travellers}</Td>

                <Td>
                  <Badge status={i.status} />
                </Td>

                <Td
                  style={{
                    fontSize: 12,
                    color: T.faint,
                  }}
                >
                  {fmtDT(i.created_at)}
                </Td>

                <Td>
                  <ViewBtn
                    onClick={() => setSelected(i)}
                  />
                </Td>
              </TRow>
            ))
          )}
        </tbody>
      </TableWrap>

      {selected && (
        <Modal
          onClose={() => setSelected(null)}
          title="Visa Enquiry"
          subtitle={`#${selected.id} · ${fmtDT(selected.created_at)}`}
        >
          <DGrid>
            <DRow
              label="Name"
              value={selected.full_name}
            />

            <DRow
              label="Email"
              value={selected.email}
            />

            <DRow
              label="Phone"
              value={selected.phone}
            />

            <DRow
              label="Destination"
              value={selected.destination_country}
            />

            <DRow
              label="Visa Type"
              value={selected.visa_type}
            />

            <DRow
              label="Travellers"
              value={selected.travellers}
            />

            <DRow
              label="Travel Date"
              value={fmtDate(selected.travel_date)}
            />
          </DGrid>

          {selected.message && (
            <>
              <SecTitle>Message</SecTitle>

              <div
                style={{
                  background: T.accentSoft,
                  borderRadius: 10,
                  padding: "12px 14px",
                  fontSize: 14,
                  color: T.text,
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                }}
              >
                {selected.message}
              </div>
            </>
          )}

          <SecTitle>Contact Customer</SecTitle>

          <a
  href={`https://mail.google.com/mail/?view=cm&fs=1&to=${encodeURIComponent(
    selected.email
  )}&su=${encodeURIComponent("Re: Visa Enquiry")}`}
  target="_blank"
  rel="noopener noreferrer"
  style={{
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    padding: "9px 18px",
    background: T.accent,
    color: "#fff",
    borderRadius: 8,
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 500,
  }}
>
  <Mail size={14} />
  Reply via Email
</a>

          <SecTitle>Update Status</SecTitle>

          <StatusActions
            statuses={["new", "read", "replied", "closed"]}
            current={selected.status}
            onSelect={(status) => {
              onStatusChange(selected.id, status);

              setSelected((current) =>
                current
                  ? { ...current, status }
                  : current
              );
            }}
          />
        </Modal>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   AI CONVERSATIONS (MakeBeeTrip AI chat history)
════════════════════════════════════════════════════════════════════ */
function AIConversationModal({ conversationId, onClose }) {
  const T = useThemeColors();
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/admin/ai-conversations/${conversationId}`, { headers: authH() });
        const d = await r.json();
        if (!r.ok) throw new Error(d?.error || `Request failed (${r.status})`);
        if (!cancelled) setDetail(d.data || null);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [conversationId]);

  return (
    <Modal
      onClose={onClose}
      title={`AI Conversation #${conversationId}`}
      subtitle={detail?.conversation?.session_id}
      maxWidth={720}
    >
      {loading && <div style={{ textAlign: "center", padding: 40, color: T.faint }}>Loading conversation…</div>}
      {error && <div style={{ textAlign: "center", padding: 40, color: T.danger }}>{error}</div>}
      {detail && (
        <>
          <DGrid>
            <DRow label="Customer" value={detail.conversation.customer_name || "Guest (not logged in)"} />
            <DRow label="Email" value={detail.conversation.customer_email || "—"} />
            <DRow label="Phone" value={detail.conversation.customer_phone || "—"} />
            <DRow label="User ID" value={detail.conversation.user_id ? `#${detail.conversation.user_id}` : "—"} />
            <DRow label="Service" value={detail.conversation.service || "General"} />
            <DRow label="Session" value={detail.conversation.session_id} />
            <DRow label="Started" value={fmtDT(detail.conversation.created_at)} />
            <DRow label="Last Activity" value={fmtDT(detail.conversation.updated_at)} />
          </DGrid>

          <SecTitle>Message Timeline</SecTitle>

          {detail.messages.length === 0 ? (
            <div style={{ textAlign: "center", padding: 24, color: T.faint, fontSize: 13 }}>No messages stored for this conversation.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {detail.messages.map(m => (
                <div
                  key={m.id}
                  style={{
                    alignSelf: m.role === "user" ? "flex-end" : "flex-start",
                    maxWidth: "85%",
                    background: m.role === "user" ? T.accentSoft : T.bg,
                    border: `1px solid ${T.border}`,
                    borderRadius: 12,
                    padding: "10px 14px",
                  }}
                >
                  <div style={{ fontSize: 10, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", color: m.role === "user" ? T.accent : T.faint, marginBottom: 4 }}>
                    {m.role === "user" ? "User" : "MakeBeeTrip AI"} · {fmtDT(m.created_at)}
                  </div>
                  <div style={{ fontSize: 13, color: T.ink, lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{m.content}</div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </Modal>
  );
}

function AIConversationsSection() {
  const T = useThemeColors();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [serviceFilter, setServiceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 1 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  const load = useCallback(async (opts = {}) => {
    const targetPage = opts.page ?? page;
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(targetPage));
      params.set("limit", "20");
      if (search.trim()) params.set("search", search.trim());
      if (serviceFilter !== "all") params.set("service", serviceFilter);

      const r = await fetch(`${API_BASE}/admin/ai-conversations?${params.toString()}`, { headers: authH() });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.error || `Request failed (${r.status})`);

      setRows(Array.isArray(d.data) ? d.data : []);
      setPagination(d.pagination || { page: targetPage, total: 0, totalPages: 1 });
      setPage(d.pagination?.page || targetPage);
    } catch (e) {
      setError(e.message);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, serviceFilter]);

  useEffect(() => { load(); }, [page]);

  const svcColors = {
    flights: { bg: T.accentSoft, c: T.accent },
    hotels: { bg: "#fce7f3", c: "#be185d" },
    buses: { bg: T.greenSoft, c: T.green },
    holidays: { bg: T.warnSoft, c: T.warn },
    visa: { bg: "#ede9fe", c: "#7c3aed" },
    general: { bg: T.bg, c: T.muted },
  };

  const renderServiceCell = (service) => {
    const list = String(service || "general")
      .split(",")
      .map(s => s.trim())
      .filter(Boolean);

    return list.map(s => {
      const svc = svcColors[s.toLowerCase()] || { bg: T.bg, c: T.muted };
      return (
        <span
          key={s}
          style={{ background: svc.bg, color: svc.c, fontSize: 11, fontWeight: 500, padding: "3px 9px", borderRadius: 20, textTransform: "capitalize", display: "inline-block", marginRight: 4 }}
        >
          {s}
        </span>
      );
    });
  };

  return (
    <div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 12, marginBottom: 20 }}>
        <StatCard icon={<Bot size={18} />} label="Total Conversations" value={pagination.total} color={T.accent} />
      </div>

      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <SearchBar value={search} onChange={v => { setSearch(v); setPage(1); }} placeholder="Search customer name, email, phone, title…" />
        <Sel
          value={serviceFilter}
          onChange={v => { setServiceFilter(v); setPage(1); }}
          options={[
            { value: "all", label: "All Services" },
            { value: "flights", label: "Flights" },
            { value: "hotels", label: "Hotels" },
            { value: "buses", label: "Buses" },
            { value: "holidays", label: "Holidays" },
            { value: "visa", label: "Visa" },
            { value: "general", label: "General" },
          ]}
        />
        <Btn onClick={() => load({ page: 1 })} variant="ghost" disabled={loading}><RefreshCw size={14} />Refresh</Btn>
      </div>

      {error && <div style={{ background: T.warnSoft, border: `1px solid ${T.warn}55`, color: T.warn, padding: "10px 14px", borderRadius: 10, marginBottom: 16, fontSize: 13 }}>⚠ {error}</div>}

      <TableWrap>
        <thead>
          <tr>
            <Th>S.No.</Th>
            <Th>Customer</Th>
            <Th>Title</Th>
            <Th>Service</Th>
            <Th>Messages</Th>
            <Th>Session</Th>
            <Th>Last Activity</Th>
            <Th>Action</Th>
          </tr>
        </thead>
        <tbody>
          {loading ? (
            <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>Loading…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan={8} style={{ textAlign: "center", padding: 40, color: T.faint }}>No AI conversations found</td></tr>
          ) : (
            rows.map((c, index) => {
              const serialNumber = (pagination.page - 1) * pagination.limit + index + 1;
              return (
                <TRow key={c.id} onClick={() => setSelectedId(c.id)}>
                  <Td style={{ fontFamily: "monospace", fontSize: 12, color: T.muted }}>{serialNumber}</Td>
                  <Td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <Avatar name={c.customer_name || "G"} bg="#ede9fe" tc="#6d28d9" />
                      <div>
                        <div style={{ fontWeight: 500, color: T.ink }}>{c.customer_name || "Guest"}</div>
                        <div style={{ fontSize: 11, color: T.faint }}>{c.customer_email || (c.user_id ? `User #${c.user_id}` : "Not logged in")}</div>
                      </div>
                    </div>
                  </Td>
                  <Td style={{ maxWidth: 220 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: T.ink }}>{c.title || c.first_user_message || "—"}</div>
                  </Td>
                  <Td>
                    {renderServiceCell(c.service)}
                  </Td>
                  <Td style={{ fontWeight: 600, color: T.ink }}>{c.message_count}</Td>
                  <Td style={{ fontFamily: "monospace", fontSize: 11, color: T.faint, maxWidth: 150 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{c.session_id}</div>
                  </Td>
                  <Td style={{ fontSize: 12, color: T.faint }}>{fmtDT(c.updated_at)}</Td>
                  <Td><ViewBtn onClick={() => setSelectedId(c.id)} /></Td>
                </TRow>
              );
            })
          )}
        </tbody>
      </TableWrap>

      {/* Pagination */}
      {!loading && pagination.totalPages > 1 && (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 16, flexWrap: "wrap", gap: 10 }}>
          <span style={{ fontSize: 12, color: T.faint }}>
            Page {pagination.page} of {pagination.totalPages} · {pagination.total} conversations
          </span>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn onClick={() => setPage(p => Math.max(1, p - 1))} disabled={pagination.page <= 1} variant="ghost" size="sm">← Prev</Btn>
            <Btn onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))} disabled={pagination.page >= pagination.totalPages} variant="ghost" size="sm">Next →</Btn>
          </div>
        </div>
      )}

      {selectedId && <AIConversationModal conversationId={selectedId} onClose={() => setSelectedId(null)} />}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   OVERVIEW
══════════════════════════════════════════════════════════════════════ */
function Overview({ visaApps, contactMsgs, flights, hotels, buses, trains, packages, holidayBookings, users, setTab }) {
  const T = useThemeColors();
  const totalRev = [...flights, ...hotels, ...buses, ...trains].reduce((s, b) => s + Number(b.total_price || b.grand_total || 0), 0);
  const upcoming = [...flights, ...hotels, ...buses, ...trains].filter(b => b.status === "upcoming").length;
  const pendVisa = visaApps.filter(a => a.status === "submitted").length;

  const recent = [
    ...flights.map(b => ({ type: "Flight", name: b.passenger_name, detail: `${b.depart_code}→${b.arrive_code}`, amount: b.total_price, status: b.status, date: b.created_at, c: T.accent })),
    ...hotels.map(b => ({ type: "Hotel", name: `${b.guest_first_name || ""} ${b.guest_last_name || ""}`, detail: b.hotel_name, amount: b.grand_total, status: b.status, date: b.created_at, c: "#be185d" })),
    ...buses.map(b => ({ type: "Bus", name: b.passenger_name, detail: `${b.from_city}→${b.to_city}`, amount: b.total_price, status: b.status, date: b.created_at, c: T.green })),
    ...trains.map(b => ({ type: "Train", name: b.passenger_name, detail: b.train_name, amount: b.total_price, status: b.status, date: b.created_at, c: "#7c3aed" })),
  ].sort((a, b) => new Date(b.date) - new Date(a.date)).slice(0, 8);

  return (
    <div>
      <div style={{ background: `linear-gradient(120deg,${T.greenSoft},${T.card})`, border: `1px solid ${T.border}`, borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700, color: T.ink }}>Welcome Back, Admin</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 4 }}>Here's what's happening across MakeBeeTrip today.</div>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(170px,1fr))", gap: 14, marginBottom: 24 }}>
        <StatCard icon={<IndianRupee size={18} />} label="Total Revenue" value={fmt(totalRev)} sub="All services" color={T.accent} />
        <StatCard icon={<Users size={18} />} label="Registered Users" value={users.length} sub={`${users.filter(u => (u.status || "active") === "active").length} active`} color={T.green} />
        <StatCard icon={<CalendarDays size={18} />} label="Upcoming Bookings" value={upcoming} sub="Across all services" color={T.teal} />
        <StatCard icon={<Palmtree size={18} />} label="Holiday Packages" value={packages.filter(p => p.is_active).length} sub={`${packages.length} total`} color="#7c3aed" />
        <StatCard icon={<FileText size={18} />} label="Visa Apps" value={visaApps.length} sub={`${pendVisa} pending`} color="#f59e0b" />
        <StatCard icon={<Mail size={18} />} label="Messages" value={contactMsgs.length} sub={`${contactMsgs.filter(m => m.status === "new").length} new`} color={T.danger} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(300px,1fr))", gap: 20 }}>
        <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, padding: 20 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>Recent Bookings</div>
            <button onClick={() => setTab("flights")} style={{ fontSize: 12, color: T.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>See all →</button>
          </div>
          {recent.map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px", borderRadius: 8, cursor: "default", borderLeft: `3px solid ${b.c}` }}
              onMouseEnter={e => e.currentTarget.style.background = T.accentSoft}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: b.c + "18", color: b.c, fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{b.type[0]}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.name}</div>
                <div style={{ fontSize: 11, color: T.faint }}>{b.type} · {b.detail}</div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{fmt(b.amount)}</div>
                <Badge status={b.status} />
              </div>
            </div>
          ))}
          {recent.length === 0 && <div style={{ textAlign: "center", padding: 24, color: T.faint, fontSize: 13 }}>No bookings yet</div>}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, padding: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 14 }}>Service Breakdown</div>
            {[
              { label: "Flights", count: flights.length, total: flights.reduce((s, b) => s + Number(b.total_price || 0), 0), c: T.accent },
              { label: "Hotels", count: hotels.length, total: hotels.reduce((s, b) => s + Number(b.grand_total || 0), 0), c: "#be185d" },
              { label: "Holiday Bookings", count: holidayBookings.length, total: holidayBookings.reduce((s, b) => s + Number(b.total_price || 0), 0), c: T.warn },
              { label: "Visa", count: visaApps.length, total: visaApps.reduce((s, a) => s + Number(a.total_fee || 0), 0), c: "#7c3aed" },
              { label: "Messages", count: contactMsgs.length, total: 0, c: T.danger },
            ].map(s => {
              const pct = totalRev > 0 ? Math.round((s.total / totalRev) * 100) : 0;
              return (
                <div key={s.label} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                  <span style={{ fontSize: 12, color: T.muted, minWidth: 130 }}>{s.label}</span>
                  <div style={{ flex: 1, height: 6, background: T.bg, borderRadius: 4, overflow: "hidden" }}>
                    <div style={{ height: "100%", background: s.c, borderRadius: 4, width: `${Math.max(pct, s.count > 0 ? 5 : 0)}%`, transition: "width .4s" }} />
                  </div>
                  <span style={{ fontSize: 12, color: T.muted, fontWeight: 500, minWidth: 24, textAlign: "right" }}>{s.count}</span>
                </div>
              );
            })}
          </div>

          <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.border}`, padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>Recent Users</div>
              <button onClick={() => setTab("users")} style={{ fontSize: 12, color: T.accent, background: "none", border: "none", cursor: "pointer", fontWeight: 500 }}>See all →</button>
            </div>
            {users.slice(0, 5).map(u => (
              <div key={u.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderBottom: `1px solid ${T.bg}` }}>
                {u.avatar
                  ? <img src={u.avatar} alt={u.name} style={{ width: 26, height: 26, borderRadius: "50%", objectFit: "cover" }} onError={e => { e.target.style.display = "none"; }} />
                  : <Avatar name={u.name} size={26} />
                }
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: T.ink }}>{u.name || "—"}</div>
                  <div style={{ fontSize: 11, color: T.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{u.email}</div>
                </div>
                <Badge status={u.status || "active"} />
              </div>
            ))}
            {users.length === 0 && <div style={{ textAlign: "center", padding: 10, color: T.faint, fontSize: 12 }}>No users yet</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   MAIN DASHBOARD
══════════════════════════════════════════════════════════════════════ */
const NAV = [
  { id: "overview", icon: <BarChart2 size={17} />, label: "Overview" },
  { id: "users", icon: <Users size={17} />, label: "Users" },
  { id: "booking_mgmt", icon: <BookOpen size={17} />, label: "Booking Management" },
  { id: "flights", icon: <Plane size={17} />, label: "Flights" },
  { id: "hotels", icon: <Hotel size={17} />, label: "Hotels" },
  { id: "hotel_pricing", icon: <IndianRupee size={17} />, label: "Hotel Pricing" },
  { id: "flight_pricing", icon: <Plane size={17} />, label: "Flight Pricing" },
  { id: "bus_pricing", icon: <Bus size={17} />, label: "Bus Pricing" },
  { id: "buses", icon: <Bus size={17} />, label: "Buses" },
  { id: "trains", icon: <Train size={17} />, label: "Trains" },
  { id: "holidays", icon: <Palmtree size={17} />, label: "Holiday Packages" },
  { id: "holiday_bookings", icon: <CalendarDays size={17} />, label: "Holiday Bookings" },
  { id: "coupons", icon: <Tag size={17} />, label: "Coupons" },
{ id: "gift_cards", icon: <IndianRupee size={17} />, label: "Gift Cards" },
  { id: "activities", icon: <Activity size={17} />, label: "Activities" },
  // {id:"activity_cities",icon:<MapPin size={17}/>,label:"Activity Cities"},
  // {id:"activity_categories",icon:<Tag size={17}/>,label:"Activity Categories"},
  // {id:"activity_analytics",icon:<TrendingUp size={17}/>,label:"Activity Analytics"},
  { id: "activity_bookings", icon: <CalendarDays size={17} />, label: "Activity Bookings" },
  { id: "visa", icon: <FileText size={17} />, label: "Visa" },
  { id: "ai_conversations", icon: <Bot size={17} />, label: "AI Conversations" },
  { id: "group_inquiries", icon: <Bell size={17} />, label: "Group Booking Notifications" },
  {
  id: "visa_enquiries",
  icon: <FileText size={17} />,
  label: "Visa Enquiries"
},
  { id: "contact", icon: <Mail size={17} />, label: "Contact" },
  { id: "reports", icon: <TrendingUp size={17} />, label: "Reports" },
];

const LABELS = {
  overview: "Dashboard Overview", users: "Registered Users", booking_mgmt: "Booking Management",
  flights: "Flight Bookings", hotels: "Hotel Bookings", buses: "Bus Bookings", trains: "Train Bookings",
  holidays: "Holiday Packages", holiday_bookings: "Holiday Bookings", activities: "City Activities",
  activity_cities: "Activity Cities", activity_categories: "Activity Categories",
  activity_analytics: "Activity Analytics", activity_bookings: "Activity Bookings",
  coupons: "Coupons",
gift_cards: "Gift Cards",
  visa: "Visa Applications", visa_enquiries: "Visa Enquiries", ai_conversations: "AI Conversations", group_inquiries: "Group Booking Notifications", contact: "Contact Queries", reports: "Reports & Analytics",
  hotel_pricing: "Hotel Pricing (Global)",
  flight_pricing: "Flight Pricing (Global)",
  bus_pricing: "Bus Pricing (Global)"
};

// ─────────────────────────────────────────────────────────────
// HOTEL PRICING (GLOBAL) — admin-managed rules applied to FUTURE hotel
// quotes only. Existing quotes/bookings keep their immutable snapshots.
// ─────────────────────────────────────────────────────────────
const HOTEL_PRICING_SYSTEM_CODES = ["gst", "service_fee", "convenience_fee"];
const HOTEL_PRICING_TYPES = [
  { value: "fixed", label: "Fixed ₹" },
  { value: "percent", label: "Percentage %" },
];
const HOTEL_PRICING_BASES = [
  { value: "per_booking", label: "Per Booking" },
  { value: "per_room", label: "Per Room" },
  { value: "per_night", label: "Per Night" },
  { value: "per_guest", label: "Per Guest" },
];
const HOTEL_PRICING_APPLY_ON = [
  { value: "room_total", label: "Supplier room total" },
  { value: "running_total", label: "Running total" },
];

function HotelPricingSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);
  const [systemCodes, setSystemCodes] = useState(HOTEL_PRICING_SYSTEM_CODES);
  const [taxNote, setTaxNote] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [dirty, setDirty] = useState(false);

  const toFormRow = (charge, isSystem) => ({
    code: charge.code,
    name: charge.name || "",
    chargeType: charge.chargeType === "percent" ? "percent" : "fixed",
    value: String(charge.value ?? 0),
    basis: charge.basis || "per_booking",
    applyOn: "room_total",
    isTax: !!charge.isTax,
    taxable: !!charge.taxable,
    enabled: charge.enabled !== false,
    sortOrder: String(charge.sortOrder ?? 0),
    isSystem,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/hotel-pricing`, { headers: authH() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Load failed (${res.status})`);
      const codes = Array.isArray(body.systemCodes) ? body.systemCodes : HOTEL_PRICING_SYSTEM_CODES;
      setSystemCodes(codes);
      setRows((body.charges || []).map((c) => toFormRow(c, codes.includes(c.code))));
      setVersion(body.version);
      setTaxNote(body.taxNote || "");
      setDirty(false);
    } catch (err) {
      setError(
        `${err.message}. If the pricing migration has not run yet, execute backend/db/hotelPricingSchema.sql first.`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setDirty(true);
    setInfo("");
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        code: "",
        name: "",
        chargeType: "fixed",
        value: "0",
        basis: "per_booking",
        applyOn: "room_total",
        isTax: false,
        taxable: false,
        enabled: true,
        sortOrder: String(prev.length + 1),
        isSystem: false,
      },
    ]);
    setDirty(true);
    setInfo("");
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    setInfo("");
  };

  const validate = () => {
    const errs = [];
    const codes = rows.map((r) => r.code);
    systemCodes.forEach((code) => {
      if (!codes.includes(code)) errs.push(`System charge "${code}" cannot be removed.`);
    });
    if (rows.length > 100) errs.push("Maximum 100 charge rows.");
    rows.forEach((row, i) => {
      const at = `Row ${i + 1}`;
      const name = String(row.name || "").trim();
      if (!name || name.length > 120) errs.push(`${at}: name is required (max 120 chars).`);
      const value = Number(row.value);
      if (!Number.isFinite(value) || value < 0) errs.push(`${at}: value must be ≥ 0.`);
      else if (row.chargeType === "percent" && value > 100) errs.push(`${at}: percentage cannot exceed 100.`);
      else if (row.chargeType === "fixed" && value > 999999.99) errs.push(`${at}: fixed amount cannot exceed 999999.99.`);
      if (!HOTEL_PRICING_BASES.some((b) => b.value === row.basis)) errs.push(`${at}: invalid basis.`);
      if (!HOTEL_PRICING_APPLY_ON.some((b) => b.value === row.applyOn)) errs.push(`${at}: invalid calculation base.`);
    });
    return errs;
  };

  const save = async () => {
    const errs = validate();
    if (errs.length) {
      setError(errs.join(" "));
      return;
    }
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/hotel-pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authH() },
        body: JSON.stringify({
          expectedVersion: version,
          charges: rows.map((row, i) => ({
            code: row.code,
            name: String(row.name || "").trim(),
            chargeType: row.chargeType,
            value: Number(row.value) || 0,
            basis: row.basis,
            applyOn: row.applyOn,
            isTax: !!row.isTax,
            taxable: !!row.taxable,
            enabled: !!row.enabled,
            sortOrder: Number(row.sortOrder) || i + 1,
          })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.code === "VERSION_CONFLICT") {
          await load();
          setError(body.error);
          return;
        }
        const details = Array.isArray(body.details) ? body.details.join(" ") : "";
        throw new Error([body.error, details].filter(Boolean).join(" "));
      }
      setVersion(body.version);
      setRows((body.charges || []).map((c) => toFormRow(c, systemCodes.includes(c.code))));
      setDirty(false);
      setInfo(
        `Saved — active configuration version v${body.version}. New hotel quotes use it immediately; existing quotes and bookings keep their snapshots.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const labelS = { fontSize: 10.5, fontWeight: 600, color: T.muted, display: "block", marginBottom: 4 };
  const gridCols =
  "minmax(180px,1.6fr) minmax(150px,1.2fr) minmax(110px,0.9fr) minmax(180px,1.3fr) 66px 34px";
  const secondaryBtn = {
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.muted,
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  };
  const primaryBtn = {
    ...secondaryBtn,
    background: T.accent,
    borderColor: T.accent,
    color: "#fff",
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.muted, fontSize: 13 }}>
        Loading hotel pricing configuration…
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Hotel Pricing (Global)</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
              One global configuration for ALL future hotel quotes — not per booking.
              Saved changes take effect on the next quote; accepted quotes and bookings never change.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{
              fontSize: 12, fontWeight: 700, color: T.accent,
              border: `1px solid ${T.border}`, borderRadius: 999, padding: "6px 12px",
            }}>
              Active version: {version !== null ? `v${version}` : "—"}
            </span>
            <button onClick={load} disabled={saving} style={secondaryBtn}>Reload</button>
            <button onClick={save} disabled={saving || !dirty} style={{ ...primaryBtn, opacity: saving || !dirty ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {taxNote && (
          <div style={{ marginTop: 12, fontSize: 11.5, color: T.muted, background: T.card || "transparent", border: `1px solid ${T.border}`, borderRadius: 8, padding: "8px 10px" }}>
            ⚠️ {taxNote}
          </div>
        )}
        {error && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.danger, background: T.dangerSoft || "transparent", border: `1px solid ${T.danger}55`, borderRadius: 8, padding: "9px 11px", whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.green, background: T.greenSoft || "transparent", border: `1px solid ${T.green}55`, borderRadius: 8, padding: "9px 11px" }}>
            {info}
          </div>
        )}
      </div>

      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, overflowX: "auto" }}>
        <div style={{ minWidth: 900 }}>
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
  {["Charge name", "Markup Type", "Markup Value", "Applicability", "Enabled", ""].map((h, i) => (
    <div key={i} style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
      {h}
    </div>
  ))}
</div>

          {rows.length === 0 && (
            <div style={{ padding: "24px 0", fontSize: 13, color: T.muted }}>
              No charges configured. Run the seed block of backend/db/hotelPricingSchema.sql, then Reload.
            </div>
          )}

          {rows.map((row, index) => (
            <div
              key={`${row.code || "custom"}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 8,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: `1px solid ${T.border}`,
                opacity: row.enabled ? 1 : 0.55,
              }}
            >
              <div>
                <input
                  value={row.name}
                  onChange={(e) => updateRow(index, { name: e.target.value })}
                  placeholder={row.isSystem ? row.name : "e.g. Peak Season Fee"}
                  style={{ ...inputS, marginBottom: 0 }}
                />
                <div style={{ fontSize: 9.5, color: T.faint || T.muted, marginTop: 3 }}>
                  {row.isSystem ? `system · ${row.code}` : row.code || "custom charge"}
                </div>
              </div>

              <select
                value={row.chargeType}
                onChange={(e) => updateRow(index, { chargeType: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {HOTEL_PRICING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <input
                type="number"
                min="0"
                step="0.01"
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              />

              <select
                value={row.basis}
                onChange={(e) => updateRow(index, { basis: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {HOTEL_PRICING_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>

              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) => updateRow(index, { enabled: e.target.checked })}
                style={{ width: 16, height: 16, justifySelf: "center", accentColor: T.accent }}
              />


              <div style={{ textAlign: "center" }}>

                  <button
                    onClick={() => removeRow(index)}
                    title="Remove charge"
                    style={{
                      border: `1px solid ${T.danger}55`,
                      background: T.dangerSoft || "transparent",
                      color: T.danger,
                      borderRadius: 6,
                      cursor: "pointer",
                      padding: "4px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </button>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={addRow}
              style={{
                border: `1px dashed ${T.border}`,
                background: T.bg,
                color: T.accent,
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Plus size={13} /> Add Other Charge
            </button>
            <span style={{ fontSize: 11, color: T.muted }}>
              Fixed ₹ or % · basis: per booking / room / night / guest · taxes compute last on the taxable base · value 0 keeps existing totals.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function FlightPricingSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const FLIGHT_PRICING_SYSTEM_CODES = ["gst", "service_fee", "convenience_fee"];
  const FLIGHT_PRICING_TYPES = [
    { value: "fixed", label: "Fixed ₹" },
    { value: "percent", label: "Percentage %" },
  ];
  const FLIGHT_PRICING_BASES = [
    { value: "per_booking", label: "Per Booking" },
    { value: "per_passenger", label: "Per Passenger" },
    { value: "per_leg", label: "Per Leg" },
    { value: "per_segment", label: "Per Segment" },
  ];
  const FLIGHT_PRICING_APPLY_ON = [
    { value: "supplier_fare_total", label: "Supplier fare total" },
    { value: "running_total", label: "Running total" },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);
  const [systemCodes, setSystemCodes] = useState(FLIGHT_PRICING_SYSTEM_CODES);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [dirty, setDirty] = useState(false);

  const toFormRow = (charge, isSystem) => ({
    code: charge.code,
    name: charge.name || "",
    chargeType: charge.chargeType === "percent" ? "percent" : "fixed",
    value: String(charge.value ?? 0),
    basis: charge.basis || "per_booking",
applyOn: charge.applyOn || "supplier_fare_total",
    isTax: !!charge.isTax,
    taxable: !!charge.taxable,
    enabled: charge.enabled !== false,
    sortOrder: String(charge.sortOrder ?? 0),
    isSystem,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/flight-pricing`, { headers: authH() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Load failed (${res.status})`);
      const codes = Array.isArray(body.systemCodes) ? body.systemCodes : FLIGHT_PRICING_SYSTEM_CODES;
      setSystemCodes(codes);
      setRows((body.charges || []).map((c) => toFormRow(c, codes.includes(c.code))));
      setVersion(body.version);
      setDirty(false);
    } catch (err) {
      setError(
        `${err.message}. If the pricing migration has not run yet, execute backend/db/flightPricingSchema.sql first.`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setDirty(true);
    setInfo("");
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        code: "",
        name: "",
        chargeType: "fixed",
        value: "0",
        basis: "per_booking",
        applyOn: "supplier_fare_total",
        isTax: false,
        taxable: false,
        enabled: true,
        sortOrder: String(prev.length + 1),
        isSystem: false,
      },
    ]);
    setDirty(true);
    setInfo("");
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    setInfo("");
  };

  const validate = () => {
    const errs = [];
    const codes = rows.map((r) => r.code);
    systemCodes.forEach((code) => {
      if (!codes.includes(code)) errs.push(`System charge "${code}" cannot be removed.`);
    });
    if (rows.length > 100) errs.push("Maximum 100 charge rows.");
    rows.forEach((row, i) => {
      const at = `Row ${i + 1}`;
      const name = String(row.name || "").trim();
      if (!name || name.length > 120) errs.push(`${at}: name is required (max 120 chars).`);
      const value = Number(row.value);
      if (!Number.isFinite(value) || value < 0) errs.push(`${at}: value must be ≥ 0.`);
      else if (row.chargeType === "percent" && value > 100) errs.push(`${at}: percentage cannot exceed 100.`);
      else if (row.chargeType === "fixed" && value > 999999.99) errs.push(`${at}: fixed amount cannot exceed 999999.99.`);
      if (!FLIGHT_PRICING_BASES.some((b) => b.value === row.basis)) errs.push(`${at}: invalid basis.`);
      if (!FLIGHT_PRICING_APPLY_ON.some((b) => b.value === row.applyOn)) errs.push(`${at}: invalid calculation base.`);
    });
    return errs;
  };

  const save = async () => {
    const errs = validate();
    if (errs.length) {
      setError(errs.join(" "));
      return;
    }
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/flight-pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authH() },
        body: JSON.stringify({
          expectedVersion: version,
          charges: rows.map((row, i) => ({
            code: row.code,
            name: String(row.name || "").trim(),
            chargeType: row.chargeType,
            value: Number(row.value) || 0,
            basis: row.basis,
            applyOn: row.applyOn,
            isTax: !!row.isTax,
            taxable: !!row.taxable,
            enabled: !!row.enabled,
            sortOrder: Number(row.sortOrder) || i + 1,
          })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.code === "VERSION_CONFLICT") {
          await load();
          setError(body.error);
          return;
        }
        const details = Array.isArray(body.details) ? body.details.join(" ") : "";
        throw new Error([body.error, details].filter(Boolean).join(" "));
      }
      setVersion(body.version);
      setRows((body.charges || []).map((c) => toFormRow(c, systemCodes.includes(c.code))));
      setDirty(false);
      setInfo(
        `Saved — active configuration version v${body.version}. New flight quotes use it immediately; existing quotes and bookings keep their snapshots.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const gridCols =
  "minmax(180px,1.6fr) minmax(150px,1.2fr) minmax(110px,0.9fr) minmax(160px,1.2fr) minmax(170px,1.3fr) 66px 34px";
  const secondaryBtn = {
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.muted,
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  };
  const primaryBtn = {
    ...secondaryBtn,
    background: T.accent,
    borderColor: T.accent,
    color: "#fff",
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.muted, fontSize: 13 }}>
        Loading flight pricing configuration…
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Flight Pricing (Global)</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
              One global configuration for ALL future flight quotes — not per booking.
              Saved changes take effect on the next quote; accepted quotes and bookings never change.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{
              fontSize: 12, fontWeight: 700, color: T.accent,
              border: `1px solid ${T.border}`, borderRadius: 999, padding: "6px 12px",
            }}>
              Active version: {version !== null ? `v${version}` : "—"}
            </span>
            <button onClick={load} disabled={saving} style={secondaryBtn}>Reload</button>
            <button onClick={save} disabled={saving || !dirty} style={{ ...primaryBtn, opacity: saving || !dirty ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.danger, background: T.dangerSoft || "transparent", border: `1px solid ${T.danger}55`, borderRadius: 8, padding: "9px 11px", whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.green, background: T.greenSoft || "transparent", border: `1px solid ${T.green}55`, borderRadius: 8, padding: "9px 11px" }}>
            {info}
          </div>
        )}
      </div>

      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, overflowX: "auto" }}>
        <div style={{ minWidth: 1050 }}>
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
            {[
  "Charge name",
  "Markup Type",
  "Markup Value",
  "Applicability",
  "Calculation base",
  "Enabled",
  "",
].map((h, i) => (
              <div key={i} style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {h}
              </div>
            ))}
          </div>

          {rows.length === 0 && (
            <div style={{ padding: "24px 0", fontSize: 13, color: T.muted }}>
              No charges configured. Run the seed block of backend/db/flightPricingSchema.sql, then Reload.
            </div>
          )}

          {rows.map((row, index) => (
            <div
              key={`${row.code || "custom"}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 8,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: `1px solid ${T.border}`,
                opacity: row.enabled ? 1 : 0.55,
              }}
            >
              <div>
                <input
                  value={row.name}
                  onChange={(e) => updateRow(index, { name: e.target.value })}
                  placeholder={row.isSystem ? undefined : "e.g. Peak Season Fee"}
                  style={{ ...inputS, marginBottom: 0, width: "100%" }}
                />
                <div style={{ fontSize: 9.5, color: T.faint || T.muted, marginTop: 3 }}>
                  {row.isSystem ? `system · ${row.code}` : row.code || "custom charge"}
                </div>
              </div>

              <select
                value={row.chargeType}
                onChange={(e) => updateRow(index, { chargeType: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {FLIGHT_PRICING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <input
                type="number"
                min="0"
                step="0.01"
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              />

              <select
                value={row.basis}
                onChange={(e) => updateRow(index, { basis: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {FLIGHT_PRICING_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>

              <select
                value={row.applyOn}
                onChange={(e) => updateRow(index, { applyOn: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {FLIGHT_PRICING_APPLY_ON.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>

              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) => updateRow(index, { enabled: e.target.checked })}
                style={{ width: 16, height: 16, justifySelf: "center", accentColor: T.accent }}
              />

              <div style={{ textAlign: "center" }}>
                  <button
  onClick={() => {
    if (!row.isSystem) removeRow(index);
  }}
  disabled={row.isSystem}
  title={
    row.isSystem
      ? "Required system charges cannot be removed"
      : "Remove charge"
  }
  style={{
                      border: `1px solid ${T.danger}55`,
                      background: T.dangerSoft || "transparent",
                      color: T.danger,
                      borderRadius: 6,
                      padding: "4px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                      opacity: row.isSystem ? 0.35 : 1,
                      cursor: row.isSystem ? "not-allowed" : "pointer",
                    }}
                  >
                    ✕
                  </button>
              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={addRow}
              style={{
                border: `1px dashed ${T.border}`,
                background: T.bg,
                color: T.accent,
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Plus size={13} /> Add Other Charge
            </button>
            <span style={{ fontSize: 11, color: T.muted }}>
              Fixed ₹ scales by basis (per booking/passenger/leg/segment) · % applies to the chosen base exactly once (never × passengers) · taxes compute last on the taxable base · value 0 keeps existing totals.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   BUS PRICING (GLOBAL) — admin-managed rules applied to FUTURE bus quotes
   only. Existing quotes/bookings keep their immutable snapshots.
   Mirrors HotelPricingSection/FlightPricingSection; bus-specific bases are
   per booking / per passenger / per seat.
══════════════════════════════════════════════════════════════════════ */
function BusPricingSection() {
  const T = useThemeColors();
  const inputS = useInputStyle();

  const BUS_PRICING_SYSTEM_CODES = ["gst", "service_fee", "convenience_fee"];
  const BUS_PRICING_TYPES = [
    { value: "fixed", label: "Fixed ₹" },
    { value: "percent", label: "Percentage %" },
  ];
  const BUS_PRICING_BASES = [
    { value: "per_booking", label: "Per Booking" },
    { value: "per_passenger", label: "Per Passenger" },
    { value: "per_seat", label: "Per Seat" },
  ];
  const BUS_PRICING_APPLY_ON = [
    { value: "supplier_fare_total", label: "Supplier fare total" },
    { value: "running_total", label: "Running total" },
  ];

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(null);
  const [systemCodes, setSystemCodes] = useState(BUS_PRICING_SYSTEM_CODES);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [dirty, setDirty] = useState(false);

  const toFormRow = (charge, isSystem) => ({
    code: charge.code,
    name: charge.name || "",
    chargeType: charge.chargeType === "percent" ? "percent" : "fixed",
    value: String(charge.value ?? 0),
    basis: charge.basis || "per_booking",
    applyOn: charge.applyOn || "supplier_fare_total",
    isTax: !!charge.isTax,
    taxable: !!charge.taxable,
    enabled: charge.enabled !== false,
    sortOrder: String(charge.sortOrder ?? 0),
    isSystem,
  });

  const load = async () => {
    setLoading(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/bus-pricing`, { headers: authH() });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || `Load failed (${res.status})`);
      const codes = Array.isArray(body.systemCodes) ? body.systemCodes : BUS_PRICING_SYSTEM_CODES;
      setSystemCodes(codes);
      setRows((body.charges || []).map((c) => toFormRow(c, codes.includes(c.code))));
      setVersion(body.version);
      setDirty(false);
    } catch (err) {
      setError(
        `${err.message}. If the pricing migration has not run yet, execute backend/db/busPricingSchema.sql first.`
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateRow = (index, patch) => {
    setRows((prev) => prev.map((row, i) => (i === index ? { ...row, ...patch } : row)));
    setDirty(true);
    setInfo("");
  };

  const addRow = () => {
    setRows((prev) => [
      ...prev,
      {
        code: "",
        name: "",
        chargeType: "fixed",
        value: "0",
        basis: "per_booking",
        applyOn: "supplier_fare_total",
        isTax: false,
        taxable: false,
        enabled: true,
        sortOrder: String(prev.length + 1),
        isSystem: false,
      },
    ]);
    setDirty(true);
    setInfo("");
  };

  const removeRow = (index) => {
    setRows((prev) => prev.filter((_, i) => i !== index));
    setDirty(true);
    setInfo("");
  };

  const validate = () => {
    const errs = [];
    const codes = rows.map((r) => r.code);
    systemCodes.forEach((code) => {
      if (!codes.includes(code)) errs.push(`System charge "${code}" cannot be removed.`);
    });
    if (rows.length > 100) errs.push("Maximum 100 charge rows.");
    rows.forEach((row, i) => {
      const at = `Row ${i + 1}`;
      const name = String(row.name || "").trim();
      if (!name || name.length > 120) errs.push(`${at}: name is required (max 120 chars).`);
      const value = Number(row.value);
      if (!Number.isFinite(value) || value < 0) errs.push(`${at}: value must be ≥ 0.`);
      else if (row.chargeType === "percent" && value > 100) errs.push(`${at}: percentage cannot exceed 100.`);
      else if (row.chargeType === "fixed" && value > 999999.99) errs.push(`${at}: fixed amount cannot exceed 999999.99.`);
      if (!BUS_PRICING_BASES.some((b) => b.value === row.basis)) errs.push(`${at}: invalid basis.`);
      if (!BUS_PRICING_APPLY_ON.some((b) => b.value === row.applyOn)) errs.push(`${at}: invalid calculation base.`);
    });
    return errs;
  };

  const save = async () => {
    const errs = validate();
    if (errs.length) {
      setError(errs.join(" "));
      return;
    }
    setSaving(true);
    setError("");
    setInfo("");
    try {
      const res = await fetch(`${API_BASE}/admin/bus-pricing`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", ...authH() },
        body: JSON.stringify({
          expectedVersion: version,
          charges: rows.map((row, i) => ({
            code: row.code,
            name: String(row.name || "").trim(),
            chargeType: row.chargeType,
            value: Number(row.value) || 0,
            basis: row.basis,
            applyOn: row.applyOn,
            isTax: !!row.isTax,
            taxable: !!row.taxable,
            enabled: !!row.enabled,
            sortOrder: Number(row.sortOrder) || i + 1,
          })),
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body.code === "VERSION_CONFLICT") {
          await load();
          setError(body.error);
          return;
        }
        const details = Array.isArray(body.details) ? body.details.join(" ") : "";
        throw new Error([body.error, details].filter(Boolean).join(" "));
      }
      setVersion(body.version);
      setRows((body.charges || []).map((c) => toFormRow(c, systemCodes.includes(c.code))));
      setDirty(false);
      setInfo(
        `Saved — active configuration version v${body.version}. New bus quotes use it immediately; existing quotes and bookings keep their snapshots. Hotel and flight pricing are unaffected.`
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const gridCols =
  "minmax(180px,1.6fr) minmax(120px,1fr) minmax(110px,0.9fr) minmax(150px,1.2fr) minmax(160px,1.3fr) 72px 40px";
  const secondaryBtn = {
    border: `1px solid ${T.border}`,
    background: T.bg,
    color: T.muted,
    borderRadius: 8,
    padding: "7px 14px",
    fontSize: 12.5,
    fontWeight: 600,
    cursor: "pointer",
  };
  const primaryBtn = {
    ...secondaryBtn,
    background: T.accent,
    borderColor: T.accent,
    color: "#fff",
  };

  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: T.muted, fontSize: 13 }}>
        Loading bus pricing configuration…
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Bus Pricing (Global)</div>
            <div style={{ fontSize: 12, color: T.muted, marginTop: 3 }}>
              One global configuration for ALL future bus quotes — not per booking. Saved changes take effect on the next quote;
              accepted quotes and bookings never change. Independent of hotel and flight pricing.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <span style={{
              fontSize: 12, fontWeight: 700, color: T.accent,
              border: `1px solid ${T.border}`, borderRadius: 999, padding: "6px 12px",
            }}>
              Active version: {version !== null ? `v${version}` : "—"}
            </span>
            <button onClick={load} disabled={saving} style={secondaryBtn}>Reload</button>
            <button onClick={save} disabled={saving || !dirty} style={{ ...primaryBtn, opacity: saving || !dirty ? 0.6 : 1 }}>
              {saving ? "Saving…" : "Save changes"}
            </button>
          </div>
        </div>

        {error && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.danger, background: T.dangerSoft || "transparent", border: `1px solid ${T.danger}55`, borderRadius: 8, padding: "9px 11px", whiteSpace: "pre-wrap" }}>
            {error}
          </div>
        )}
        {info && (
          <div style={{ marginTop: 12, fontSize: 12, color: T.green, background: T.greenSoft || "transparent", border: `1px solid ${T.green}55`, borderRadius: 8, padding: "9px 11px" }}>
            {info}
          </div>
        )}
      </div>

      <div style={{ background: T.bg, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, overflowX: "auto" }}>
        <div style={{ minWidth: 1050 }}>
          <div style={{ display: "grid", gridTemplateColumns: gridCols, gap: 8, paddingBottom: 8, borderBottom: `1px solid ${T.border}` }}>
            {[
  "Charge Name",
  "Markup Type",
  "Markup Value",
  "Applicability",
  "Calculation Base",
  "Enabled",
  "",
].map((h, i) => (
              <div key={i} style={{ fontSize: 10.5, fontWeight: 700, color: T.muted, textTransform: "uppercase", letterSpacing: 0.4 }}>
                {h}
              </div>
            ))}
          </div>

          {rows.length === 0 && (
            <div style={{ padding: "24px 0", fontSize: 13, color: T.muted }}>
              No charges configured. Run the seed block of backend/db/busPricingSchema.sql, then Reload.
            </div>
          )}

          {rows.map((row, index) => (
            <div
              key={`${row.code || "custom"}-${index}`}
              style={{
                display: "grid",
                gridTemplateColumns: gridCols,
                gap: 8,
                alignItems: "center",
                padding: "10px 0",
                borderBottom: `1px solid ${T.border}`,
                opacity: row.enabled ? 1 : 0.55,
              }}
            >
              <div>
                <input
                  value={row.name}
                  onChange={(e) => updateRow(index, { name: e.target.value })}
                  placeholder={row.isSystem ? undefined : "e.g. Peak Season Fee"}
                  style={{ ...inputS, marginBottom: 0, width: "100%" }}
                />
                <div style={{ fontSize: 9.5, color: T.faint || T.muted, marginTop: 3 }}>
                  {row.isSystem ? `system · ${row.code}` : row.code || "custom charge"}
                </div>
              </div>

              <select
                value={row.chargeType}
                onChange={(e) => updateRow(index, { chargeType: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {BUS_PRICING_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>

              <input
                type="number"
                min="0"
                step="0.01"
                value={row.value}
                onChange={(e) => updateRow(index, { value: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              />

              <select
                value={row.basis}
                onChange={(e) => updateRow(index, { basis: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {BUS_PRICING_BASES.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>

              <select
                value={row.applyOn}
                onChange={(e) => updateRow(index, { applyOn: e.target.value })}
                style={{ ...inputS, marginBottom: 0 }}
              >
                {BUS_PRICING_APPLY_ON.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>

              <input
                type="checkbox"
                checked={row.enabled}
                onChange={(e) => updateRow(index, { enabled: e.target.checked })}
                style={{ width: 16, height: 16, justifySelf: "center", accentColor: T.accent }}
              />

              <div style={{ textAlign: "center" }}>

                  <button
                    onClick={() => removeRow(index)}
                    title="Remove charge"
                    style={{
                      border: `1px solid ${T.danger}55`,
                      background: T.dangerSoft || "transparent",
                      color: T.danger,
                      borderRadius: 6,
                      cursor: "pointer",
                      padding: "4px 8px",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    ✕
                  </button>

              </div>
            </div>
          ))}

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap", alignItems: "center" }}>
            <button
              onClick={addRow}
              style={{
                border: `1px dashed ${T.border}`,
                background: T.bg,
                color: T.accent,
                borderRadius: 8,
                padding: "7px 12px",
                fontSize: 12,
                fontWeight: 700,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              <Plus size={13} /> Add Other Charge
            </button>
            <span style={{ fontSize: 11, color: T.muted }}>
              Fixed ₹ scales by basis (per booking/passenger/seat) · % applies to the chosen base exactly once (never × seats) · taxes compute last on the taxable base · value 0 keeps existing totals.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function AdminDashboardInner() {
  const { adminLogout } = useAdminAuth();
  const { theme, T, toggleTheme } = useTheme();
  const [tab, setTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);

  /* ── MOBILE / DESKTOP DETECTION (pure JS, no CSS media-query dependency) ── */
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth <= 900 : false
  );
  useEffect(() => {
    const checkSize = () => setIsMobile(window.innerWidth <= 900);
    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, []);
  // Sidebar drawer should always start closed when switching to mobile
  useEffect(() => { if (!isMobile) setSidebarOpen(false); }, [isMobile]);

  const [visaApps, setVisaApps] = useState([]);
  const [contactMsgs, setContactMsgs] = useState([]);
  const [groupInquiries, setGroupInquiries] = useState([]);
  const [visaEnquiries, setVisaEnquiries] = useState([]);
  const [flights, setFlights] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [buses, setBuses] = useState(MOCK_BUSES);
  const [trains, setTrains] = useState(MOCK_TRAINS);
  const [packages, setPackages] = useState([]);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [holidayBookings, setHolidayBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadUsers = useCallback(async () => { setLoadingUsers(true); const d = await apiFetch("/users"); if (d) setUsers(Array.isArray(d) ? d : []); setLoadingUsers(false); }, []);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const [v, c, f, h, b, pkgs, hb, gi, ve] = await Promise.all([
      apiFetch("/visa-applications"), apiFetch("/contact"),
      apiFetch("/flight-bookings?admin=1"), apiFetch("/hotel-bookings/admin/all"),
      apiFetch("/bus-bookings?admin=1"),
      apiFetch("/packages?admin=1"), apiFetch("/holiday-bookings?admin=1"), apiFetch("/admin/group-inquiries"),
      apiFetch("/admin/visa-enquiries")
    ]);
    if (v) setVisaApps(v); else setVisaApps([]);
    if (c) setContactMsgs(c); else setContactMsgs([]);
    if (f) setFlights(f); else setFlights([]);
    if (h) setHotels(h); else setHotels([]);
    if (b) setBuses(Array.isArray(b) ? b : []); else setBuses([]);
    if (pkgs) setPackages(Array.isArray(pkgs) ? pkgs : []); else setPackages([]);
    if (hb) setHolidayBookings(Array.isArray(hb) ? hb : []); else setHolidayBookings([]);
    if (gi) setGroupInquiries(Array.isArray(gi) ? gi : []); else setGroupInquiries([]);
    if (ve) setVisaEnquiries(Array.isArray(ve) ? ve : []); else setVisaEnquiries([]);
    if (!f || !h) setError("Some data unavailable. Check backend connection.");
    setLastRefresh(new Date()); setLoading(false); loadUsers();
  }, [loadUsers]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (tab === "users") loadUsers(); }, [tab, loadUsers]);

  const newMsgs = contactMsgs.filter(m => m.status === "new").length;
  const pendVisa = visaApps.filter(a => a.status === "submitted").length;
  const newGroupInquiries =
  groupInquiries.filter(i => i.status === "new").length;

const newVisaEnquiries =
  visaEnquiries.filter(i => i.status === "new").length;

const badge = {
  visa: pendVisa || null,
  contact: newMsgs || null,
  group_inquiries: newGroupInquiries || null,
  visa_enquiries: newVisaEnquiries || null
};
  const handleBMStatusChange = (type, id, s) => {
    if (type === "Flight") setFlights(prev => prev.map(b => b.id === id ? { ...b, status: s } : b));
    else if (type === "Hotel") setHotels(prev => prev.map(b => b.id === id ? { ...b, status: s } : b));
    else if (type === "Bus") setBuses(prev => prev.map(b => b.id === id ? { ...b, status: s } : b));
    else if (type === "Train") setTrains(prev => prev.map(b => b.id === id ? { ...b, status: s } : b));
  };

  const exportCSV = () => {
    const m = { flights, hotels, buses, trains, visa: visaApps, contact: contactMsgs, group_inquiries: groupInquiries, users };
    const d = m[tab] || []; if (!d.length) return;
    const flatten = (o, p = "") => Object.entries(o).reduce((acc, [k, v]) => { if (v && typeof v === "object" && !Array.isArray(v)) return { ...acc, ...flatten(v, `${p}${k}_`) }; return { ...acc, [`${p}${k}`]: Array.isArray(v) ? JSON.stringify(v) : v } }, {});
    const rows = d.map(flatten); const cols = [...new Set(rows.flatMap(Object.keys))];
    const csv = [cols.join(","), ...rows.map(r => cols.map(c => `"${String(r[c] || "").replace(/"/g, '""')}"`).join(","))].join("\n");
    const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); a.download = `${tab}_export.csv`; a.click();
  };

  const sidebarWidth = collapsed ? 76 : 240;

  const SidebarContent = () =>
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* ── Logo area ── */}
      <div style={{ padding: collapsed ? "20px 10px 16px" : "20px 18px 16px", display: "flex", alignItems: "center", justifyContent: collapsed ? "center" : "space-between", borderBottom: `1px solid ${T.border}`, gap: 8 }}>
        {!collapsed && (
          <img
            src={Logo}
            alt="MakeBeeTrip"
            style={{ height: 38, width: "auto", maxWidth: 150, objectFit: "contain" }}
          />
        )}
        {/* Collapse toggle only makes sense on desktop; hide it on mobile drawer */}
        {!isMobile && (
          <button
            onClick={() => setCollapsed(c => !c)}
            title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
            style={{ background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 8, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.accent, flexShrink: 0 }}
          >
            {collapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        )}
        {/* On mobile show a close (X) button instead */}
        {isMobile && (
          <button
            onClick={() => setSidebarOpen(false)}
            title="Close menu"
            style={{ background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 8, width: 30, height: 30, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: T.accent, flexShrink: 0 }}
          >
            <X size={16} />
          </button>
        )}
      </div>
      <nav style={{ padding: collapsed ? "16px 8px 18px" : "16px 12px 20px", flex: 1, display: "flex", flexDirection: "column", gap: 20, overflowY: "auto" }}>
        {NAV.map(item => {
          const isA = tab === item.id; const b = badge[item.id];
          const showCollapsed = collapsed && !isMobile;
          return (
            <button key={item.id} onClick={() => { setTab(item.id); setSidebarOpen(false); }}
              title={showCollapsed ? item.label : undefined}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: showCollapsed ? "11px" : "11px 12px", borderRadius: 10, border: "1px solid transparent", cursor: "pointer", textAlign: "left", width: "100%", justifyContent: showCollapsed ? "center" : "flex-start", background: isA ? T.accent : "transparent", color: isA ? "#fff" : T.muted, fontSize: 13, fontWeight: isA ? 600 : 400, transition: "all .15s", boxShadow: isA ? "0 8px 18px rgba(22,163,74,.22)" : "none", position: "relative" }}
              onMouseEnter={e => { if (!isA) { e.currentTarget.style.background = T.bg; e.currentTarget.style.color = T.ink; e.currentTarget.style.borderColor = T.border; } }}
              onMouseLeave={e => { if (!isA) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.muted; e.currentTarget.style.borderColor = "transparent"; } }}
            >
              <span style={{ width: 20, textAlign: "center", flexShrink: 0, color: isA ? "#fff" : T.faint, display: "flex", alignItems: "center" }}>{item.icon}</span>
              {!showCollapsed && <span style={{ flex: 1, whiteSpace: "nowrap" }}>{item.label}</span>}
              {b > 0 && !showCollapsed && <span style={{ background: T.danger, color: "#fff", fontSize: 11, fontWeight: 600, minWidth: 20, height: 20, borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 5px" }}>{b}</span>}
              {b > 0 && showCollapsed && <span style={{ position: "absolute", top: 4, right: 4, background: T.danger, width: 8, height: 8, borderRadius: "50%" }} />}
              {isA && !showCollapsed && <ChevronRight size={14} style={{ flexShrink: 0 }} />}
            </button>
          );
        })}
      </nav>
      <div style={{ marginTop: "auto", padding: (collapsed && !isMobile) ? "14px 8px 20px" : "14px 12px 22px", borderTop: `1px solid ${T.border}`, background: T.card }}>
        <button onClick={load} disabled={loading} title={(collapsed && !isMobile) ? "Refresh All" : undefined} style={{ width: "100%", padding: (collapsed && !isMobile) ? "10px" : "10px 12px", background: T.accentSoft, border: `1px solid ${T.border}`, borderRadius: 10, color: T.muted, fontSize: 13, cursor: "pointer", marginBottom: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 7, fontWeight: 500 }}>
          <RefreshCw size={14} /> {!(collapsed && !isMobile) && (loading ? "Loading…" : "Refresh All")}
        </button>
        <button onClick={adminLogout} title={(collapsed && !isMobile) ? "Sign out" : undefined} style={{ width: "100%", padding: (collapsed && !isMobile) ? "10px" : "10px 12px", background: "transparent", border: "1px solid transparent", borderRadius: 10, color: T.muted, fontSize: 13, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, fontWeight: 400 }}>
          <LogOut size={14} /> {!(collapsed && !isMobile) && "Sign out"}
        </button>
        {lastRefresh && !(collapsed && !isMobile) && <div style={{ fontSize: 11, color: T.faint, textAlign: "center", marginTop: 8, fontWeight: 400 }}>Updated {lastRefresh.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</div>}
      </div>
    </div>


  return (
    <div style={{ display: "flex", minHeight: "100vh", alignItems: "stretch", background: T.bg, fontFamily: "system-ui,-apple-system,sans-serif", position: "relative" }}>

      {/* Overlay - only mounts on mobile while drawer is open */}
      {isMobile && sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 200 }}
        />
      )}

      {/* Desktop Sidebar - only mounts when NOT mobile, so it can never leak onto desktop layout issues */}
      {!isMobile && (
        <aside style={{ width: sidebarWidth, minHeight: "100vh", height: "auto", alignSelf: "stretch", background: T.sidebar, display: "flex", flexDirection: "column", position: "sticky", top: 0, overflowY: "auto", flexShrink: 0, zIndex: 100, transition: "width .2s ease", borderRight: `1px solid ${T.border}` }}>
          <SidebarContent />
        </aside>
      )}

      {/* Mobile Sidebar (drawer) - only mounts when isMobile is true */}
      {isMobile && (
        <aside style={{
          width: 240,
          background: T.sidebar,
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          height: "calc(100vh + 120px)",
          minHeight: "calc(100vh + 120px)",
          overflowY: "auto",
          zIndex: 250,
          transform: sidebarOpen ? "translateX(0)" : "translateX(-100%)",
          transition: "transform .25s",
          boxShadow: "4px 0 20px rgba(0,0,0,0.3)"
        }}>
          <SidebarContent />
        </aside>
      )}

      <main style={{ flex: 1, padding: "0", overflow: "hidden", minWidth: 0 }}>
        <div style={{ background: T.card, borderBottom: `1px solid ${T.border}`, padding: "20px 20px", display: "flex", alignItems: "center", gap: 14, position: "sticky", top: 0, zIndex: 50 }}>

          {/* Hamburger menu - only shown on mobile */}
          {isMobile && (
            <button onClick={() => setSidebarOpen(!sidebarOpen)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", alignItems: "center", padding: 4 }}>
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: T.ink }}>{LABELS[tab]}</div>
          </div>
          <button onClick={toggleTheme} title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} style={{ padding: "8px", background: T.accent, color: "#fff", border: "none", borderRadius: "50%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", width: 34, height: 34 }}>
            {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          {["flights", "hotels", "buses", "trains", "visa", "group_inquiries", "visa_enquiries", "contact", "users"].includes(tab) && (
            <button onClick={exportCSV} style={{ padding: "7px 14px", background: T.accent, color: "#fff", border: "none", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <Download size={14} />Export
            </button>
          )}
        </div>

        <div style={{ padding: "22px 20px 60px" }}>
          {error && <div style={{ background: T.warnSoft, border: `1px solid ${T.warn}55`, color: T.warn, padding: "10px 14px", borderRadius: 10, marginBottom: 18, fontSize: 13 }}>⚠ {error}</div>}
          {loading ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "80px 0", color: T.muted, gap: 14 }}>
              <div style={{ width: 34, height: 34, border: `3px solid ${T.border}`, borderTopColor: T.accent, borderRadius: "50%", animation: "spin .8s linear infinite" }} />
              <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
              <p style={{ margin: 0, fontSize: 14 }}>Loading data…</p>
            </div>
          ) : (
            <>
              {tab === "overview" && <Overview visaApps={visaApps} contactMsgs={contactMsgs} flights={flights} hotels={hotels} buses={buses} trains={trains} packages={packages} holidayBookings={holidayBookings} users={users} setTab={setTab} />}
              {tab === "users" && <UsersSection data={users} loading={loadingUsers} onRefresh={loadUsers} onStatusChange={(id, s) => setUsers(prev => prev.map(u => u.id === id ? { ...u, status: s } : u))} />}
              {tab === "booking_mgmt" && <BookingManagementSection flights={flights} hotels={hotels} buses={buses} trains={trains} holidayBookings={holidayBookings} onStatusChange={handleBMStatusChange} />}
              {tab === "flights" && <FlightsSection data={flights} onStatusChange={(id, s) => setFlights(prev => prev.map(b => b.id === id ? { ...b, status: s } : b))} />}
              {tab === "hotels" && <HotelsSection data={hotels} onStatusChange={(id, s) => setHotels(prev => prev.map(b => b.id === id ? { ...b, status: s } : b))} />}
              {tab === "hotel_pricing" && <HotelPricingSection />}
              {tab === "flight_pricing" && <FlightPricingSection />}
              {tab === "bus_pricing" && <BusPricingSection />}
              {tab === "buses" && <BusesSection data={buses} onStatusChange={(id, s) => setBuses(prev => prev.map(b => b.id === id ? { ...b, status: s } : b))} />}
              {tab === "trains" && <TrainsSection data={trains} onStatusChange={(id, s) => setTrains(prev => prev.map(b => b.id === id ? { ...b, status: s } : b))} />}
              {tab === "holidays" && <HolidaysSection />}
              {tab === "holiday_bookings" && <HolidayBookingsSection data={holidayBookings} onStatusChange={(id, s) => setHolidayBookings(prev => prev.map(b => b.id === id ? { ...b, status: s } : b))} />}
              {tab === "coupons" && <CouponsSection />}
{tab === "gift_cards" && <GiftCardsSection />}
              {tab === "activities" && <ActivityAdminPanel view="activities" />}
              {tab === "activity_cities" && <ActivityAdminPanel view="cities" />}
              {tab === "activity_categories" && <ActivityAdminPanel view="categories" />}
              {tab === "activity_analytics" && <ActivityAdminPanel view="analytics" />}
              {tab === "activity_bookings" && <ActivityAdminPanel view="bookings" />}
              {tab === "visa" && <VisaSection data={visaApps} onStatusChange={(id, s) => setVisaApps(prev => prev.map(a => a.application_id === id ? { ...a, status: s } : a))} />}
              {tab === "ai_conversations" && <AIConversationsSection />}
              {tab === "group_inquiries" && <GroupInquiriesSection data={groupInquiries} onStatusChange={(id, s) => setGroupInquiries(prev => prev.map(i => i.id === id ? { ...i, status: s } : i))} />}
              {tab === "visa_enquiries" && (
  <VisaEnquiriesSection
    data={visaEnquiries}
    onStatusChange={(id, status) => {
      apiPatch(`/admin/visa-enquiries/${id}/status`, {
        status,
      });

      setVisaEnquiries((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, status }
            : item
        )
      );
    }}
  />
)}
              {tab === "contact" && <ContactSection data={contactMsgs} onStatusChange={(id, s) => setContactMsgs(prev => prev.map(m => m.id === id ? { ...m, status: s } : m))} />}
              {tab === "reports" && <ReportsSection />}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

let _resolvingHolidayChildren = false;
function resolveHolidayChildren() {
  if (_resolvingHolidayChildren) return Promise.resolve();
  _resolvingHolidayChildren = true;
  return new Promise(resolve => {
    if (typeof window === "undefined") {
      resolve();
      _resolvingHolidayChildren = false;
      return;
    }
    if (document.querySelector("main[data-holiday-resolving]")) {
      resolve();
      _resolvingHolidayChildren = false;
      return;
    }
    setTimeout(() => {
      try {
        const p = document.createElement("div");
        p.style.cssText = "position:absolute;left:-9999px;width:200px;height:60px;background:#ececec;opacity:.55;border-radius:8px;font-size:12px;line-height:1.4;padding:6px;color:#333;z-index:999999";
        document.body.appendChild(p);
        try {
          const target = document.querySelector("main") || document.body;
          target.style.position = target.style.position || "relative";
          target.appendChild(p);
        } catch {
          const target = document.body;
          target.style.position = target.style.position || "relative";
          target.appendChild(p);
        }
        if (requestAnimationFrame) requestAnimationFrame(() => p.remove());
        else p.remove();
      } catch {}
      resolve();
      _resolvingHolidayChildren = false;
    }, 2);
  });
}

export default function AdminDashboard() {
  return (
    <ThemeProvider>
      <AdminDashboardInner />
    </ThemeProvider>
  );
}
